import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { Button, Alert, Form, Col, Row, Spinner } from "react-bootstrap";
import { FaPlus, FaMinus, FaEdit, FaTrash, FaSave } from 'react-icons/fa';
import swal from "sweetalert";
import { DatePicker, Select } from 'antd';
import MultiSelectField from './MultiSelectField';
import {
  fetchPrimarySiteOptions,
  readDiagnosisPrimarySites,
  attachDiagnosisPrimarySites,
  leadSiteName,
} from '../lib/PrimarySiteUtils';
import PatientStagingDetails from './PatientStagingDetails';
import moment from 'moment';
import C from '../lib/Config';
import { classifySaveUserCaseResponse, SaveOutcome } from '../lib/SaveUserCaseResponse';
import { getExtractionResult, ExtractionOutcome, ReportType } from '../lib/ClinicalExtraction';
import {
  mapDiagnosisResultToForm,
  T_STAGE_VALUES,
  N_STAGE_VALUES,
} from '../lib/DiagnosisExtractionMapper';
import Crypt from '../lib/Crypt';
import OPVisitManagement from './OPVisit';
import getHospitalDetails from '../lib/HospitalConfig';
import MicButton from './voice/MicButton';
import { appendText } from './voice/voiceWrite';
import './DiagnosisManagement.css';
const measurementUnitOptions = ["mm", "cm"];
// NOTE: the per-row "T STAGE" / "N STAGE" columns previously offered overall
// AJCC stage values (I, IA, IIB…), which are not T or N classifications. They
// now use T_STAGE_VALUES / N_STAGE_VALUES from DiagnosisExtractionMapper, the
// same digit vocabulary the Staging block records.

// ---------------------------------------------------------------------------
// TEMPORARY diagnostic capture — remove once the save constraint violation is
// resolved. Writes to localStorage so the record survives the page reload that
// follows a save, which is what made the console output impossible to read.
//
//   window.__diagnosisDebug()        -> print everything captured
//   window.__diagnosisDebug.copy()   -> copy it to the clipboard as JSON
//   window.__diagnosisDebug.clear()  -> wipe it
// ---------------------------------------------------------------------------
const DIAG_DEBUG_KEY = 'diagnosisDebugLog';

// Console-driven bisect for the save constraint violation: suppress one
// extraction-populated field at a time and re-save, no rebuild needed.
//   __diagSuppress('extractionResultId')  -> send it as null / empty
//   __diagSuppress.list()                 -> what is currently suppressed
//   __diagSuppress.clear()                -> back to normal
const diagSuppressed = new Set();

const recordDiagDebug = (label, detail) => {
  try {
    const raw = localStorage.getItem(DIAG_DEBUG_KEY);
    const log = raw ? JSON.parse(raw) : [];
    const text = typeof detail === 'string' ? detail : JSON.stringify(detail);
    log.push({
      at: new Date().toISOString(),
      label,
      // Stack traces run to tens of KB; keep well inside the storage quota.
      detail: text && text.length > 6000 ? text.slice(0, 6000) + '…[truncated]' : text,
    });
    while (log.length > 15) log.shift();
    localStorage.setItem(DIAG_DEBUG_KEY, JSON.stringify(log));
  } catch (e) {
    // Diagnostics must never be able to break a save.
  }
};

if (typeof window !== 'undefined') {
  const read = () => {
    try {
      return JSON.parse(localStorage.getItem(DIAG_DEBUG_KEY) || '[]');
    } catch (e) {
      return [];
    }
  };
  window.__diagnosisDebug = () => {
    const log = read();
    console.log('=== diagnosis debug: ' + log.length + ' entr' + (log.length === 1 ? 'y' : 'ies') + ' ===');
    log.forEach((e) => console.log('\n[' + e.at + '] ' + e.label + '\n' + e.detail));
    return log;
  };
  // Returns the raw JSON string. The console prints the return value, so this
  // works even when the clipboard API refuses (it needs document focus, which
  // typing into DevTools does not always give you).
  window.__diagnosisDebug.text = () => JSON.stringify(read(), null, 2);
  window.__diagnosisDebug.copy = () => {
    const text = window.__diagnosisDebug.text();
    if (!navigator.clipboard) return 'clipboard unavailable — use __diagnosisDebug.text()';
    return navigator.clipboard.writeText(text).then(
      () => 'copied ' + text.length + ' chars',
      (err) => 'copy failed (' + err.message + ') — use __diagnosisDebug.text()'
    );
  };
  window.__diagnosisDebug.clear = () => {
    localStorage.removeItem(DIAG_DEBUG_KEY);
    return 'cleared';
  };

  window.__diagSuppress = (field) => {
    diagSuppressed.add(field);
    return 'suppressing: ' + Array.from(diagSuppressed).join(', ');
  };
  window.__diagSuppress.list = () => Array.from(diagSuppressed);
  window.__diagSuppress.clear = () => {
    diagSuppressed.clear();
    return 'nothing suppressed';
  };
}

// Add these options at the top of the component
const histologyOptions = [
  "Metastatic neuroendocrine carcinoma",
  "Metastatic lentigo maligna melanoma",
  // ... rest of the options
];
const histologyGradeOptions = [
  "Low grade lymphoma, stage1",
  "Intermediate grade histologic differentiation",
  // ... rest of the options
];
const icd10Options = [
  "Malignat melanoma of skin left wrist",
  "Maligant melanoma of skin of right forearm",
  // ... rest of the options
];
const riskStratificationOptions = [
  { label: "Low Risk", value: "Low" },
  { label: "Intermediate Risk", value: "Intermediate" },
  { label: "High Risk", value: "High" },
  { label: "N/A", value: "NotAvailable" }
];

const biomarkerNameOptions = [
  "PD-HER2/neu (FISH)",
  "MGMT mutation",
  // ... rest of the options
];

const { Option } = Select;

// Standard date formatter function to ensure consistent DD-MM-YYYY format
const formatDate = (dateString) => {
  if (!dateString) return "";

  // Check if the date is already in DD-MM-YYYY format
  if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
    return dateString;
  }

  // Convert from any date string to DD-MM-YYYY
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return ""; // Return empty string if invalid date
  
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
};

// A diagnosis's primary sites, for display. Reads the collection and falls back
// to the deprecated scalar, which is still populated by the backend as a mirror
// of the lead entry (spec §5) and is all the /diagnoses/patient/{id} fallback
// endpoint gives us.
//
// Names come from the response, never from a lookup against the picker options:
// those only include active sites, so a diagnosis on a since-retired site would
// render blank (spec §6).
const formatPrimarySites = (diagnosis) => {
  const names = (diagnosis?.primarySites || [])
    .map((site) => site && site.siteName)
    .filter(Boolean);

  if (names.length > 0) return names.join(", ");
  return diagnosis?.primarySite || "N/A";
};

const DiagnosisManagement = forwardRef(({ patientId, doctorId, onDiagnosisLoaded }, ref) => {
  // Create a ref to track if component is mounted
  const isMounted = useRef(true);

  const [hospitalDetails] = useState(getHospitalDetails);

  // State management for original diagnosis functionality
  const [diagnosisData, setDiagnosisData] = useState([]);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [currentEditIndex, setCurrentEditIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // State for saving functionality
  const [saveLoader, setSaveLoader] = useState(false);
  const [initialAssessmentId, setInitialAssessmentId] = useState(null);
  const [initialAssesmentErrors, setInitialAssesmentErrors] = useState({});
  const [fetchedStagingIds, setFetchedStagingIds] = useState(new Set());
  const [selectedMetastaticSites, setSelectedMetastaticSites] = useState([]);
  const [chooseInput, setChooseInput] = useState("");
  const [showDiagnosisList, setShowDiagnosisList] = useState(true);
  const [activeDropdown, setActiveDropdown] = useState({
    type: null, 
    index: null, 
    field: null  
  });

  // Replace it with this expanded version that includes all required fields:
// Error handling
const [errors, setErrors] = useState({
  status: "",
  diagnosisType: "",
  primarySite: "",
  canSiteIdIAD: "",
  pathology: "",
  cancerStage: "",
  limbLossIdIAD: "",
  mutationIdIA: "",
  prevCanSiteIdIAD: ""
});

  const [availableMetastaticSites, setAvailableMetastaticSites] = useState([]);
  // AI pre-population of a NEW diagnosis. `extractionResultId` is stamped on
  // the saved record so an AI-derived diagnosis can be told apart from a typed
  // one; it stays null for manual entry.
  const [extractionLoading, setExtractionLoading] = useState(false);
  const [extractionNotice, setExtractionNotice] = useState(null);
  const [extractionResultId, setExtractionResultId] = useState(null);
  // Ref rather than the state flag above: two clicks in the same tick would
  // both read a stale `extractionLoading === false` and fire twice.
  const extractionInFlight = useRef(false);
  const [formData, setFormData] = useState({
    status: "",
    diagnosisType: "",
    primarySite: "",
    description: "",
    histology: "",
    histologyGrade: "",
    metastasis: "",
    metastasisDate: "",
    metastasisDetails: "",
    metastaticSites: [],
    icd10Diagnosis: "",
    riskStratification: "",
    coreDiagnosis: "",
    tumorGroup: "",
    laterality: "",
    lymphNodes: [
      { lnGroup: "", length: "", lengthUnit: "mm", width: "", widthUnit: "mm", depth: "", depthUnit: "mm", nStage: "", description: "" },
    ],
    investigationReports: [],
    biomarkers: [{ name: "", status: "", value: "" }],
    tumorSizes: [
      { length: "", lengthUnit: "mm",
        width: "", widthUnit: "mm",
        depth: "", depthUnit: "mm",
        tStage: "",
        description: ""
      },
    ],
    stagingDetails: [
      { type: "", t: "", n: "", m: "", stage: "", date: null, isSaved: false }
    ],
    remarks: "",
    dateOfDiagnosis: null
  });

  
  
  // Additional state for moved fields from PatientProfile
  const [pathology, setPathology] = useState('');
  const [cancerStage, setCancerStage] = useState('');
  const [boneMetasis, setBoneMetasis] = useState('N');
  const [brainMets, setBrainMets] = useState('N');
  const [limbLossIdIAD, setLimbLossIdIAD] = useState([]);
  const [mutationIdIA, setMutationIdIA] = useState([]);
  const [canSiteIdIAD, setCanSiteIdIAD] = useState([]);
  const [prevCanSiteIdIAD, setPrevCanSiteIdIAD] = useState([]);
  const [doctorObservation, setDoctorObservation] = useState('');
  
  // Arrays for dropdown selections
  const [cancerStageMstArr, setCancerStageMstArr] = useState([]);
  const [limbLossArr, setLimbLossArr] = useState([]);
  const [mutationArr, setMutationArr] = useState([]);
  const [canSitesArr, setCanSitesArr] = useState([]);
  const [prevCanSitesArr, setPrevCanSitesArr] = useState([]);
  const [diagnosisStatuses, setDiagnosisStatuses] = useState([]);
  const [diagnosisTypes, setDiagnosisTypes] = useState([]);
  // `primarySites` is the OPTION list from the master endpoint;
  // `selectedPrimarySites` is this diagnosis's own sites. The names are close
  // because the option-list state predates the collection.
  const [primarySites, setPrimarySites] = useState([]);
  const [selectedPrimarySites, setSelectedPrimarySites] = useState([]);
  // Whether selectedPrimarySites is this diagnosis's COMPLETE set — either
  // because the server sent the collection, or because the user has since
  // chosen it themselves. False means all we have is a seed seed from the
  // deprecated scalar, which only ever mirrors the lead site: sending that as a
  // replacement would drop any second primary the record actually has. See the
  // gate in prepareDataForAPI.
  const [primarySitesKnown, setPrimarySitesKnown] = useState(false);
  const [histologies, setHistologies] = useState([]);
  const [histologyGrades, setHistologyGrades] = useState([]);
  const [biomarkers, setBiomarkers] = useState([]);
  const [biomarkerStatuses, setBiomarkerStatuses] = useState([]);
  const [loadingDropdowns, setLoadingDropdowns] = useState({
    statuses: false,
    types: false,
    primarySites: false,
    histologies: false,
    histologyGrades: false,
    biomarkers: false,
    biomarkerStatuses: false
  });
  const [newItemInput, setNewItemInput] = useState({
    status: "",
    type: "",
    histology: "",
    histologyGrade: "",
    biomarker: "",
    biomarkerStatus: ""
  });

  

  //for save all button to enable
  const initialdata = {
    status: "",
    diagnosisType :"",
    pathology: pathology,
    currentDiagnosis: canSiteIdIAD
  };
  const [initialFormData, setInitialFormData] = useState(initialdata);
  const [saveallLoader, setSaveallLoader] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  useEffect(() => {
    const { status, diagnosisType, pathology, currentDiagnosis } = initialFormData;
  
    const isFormFilled =
      status && diagnosisType && pathology && currentDiagnosis?.length > 0;
 
    setIsFormValid(isFormFilled );
  }, [initialFormData]);

  
  useEffect(() => {
    setInitialFormData({
      status: formData.status,
      diagnosisType: formData.diagnosisType,
      pathology: pathology,
      currentDiagnosis: canSiteIdIAD
    });
  }, [pathology, canSiteIdIAD]); // Runs whenever pathology or canSiteIdIAD changes
  

  // Normalizes a measurement to "<number> <unit>" for the API.
  //
  // The unit MUST be passed in from the row's own lengthUnit/widthUnit/depthUnit.
  // This used to hardcode "mm", which silently discarded the unit dropdown and
  // persisted a 2.4 cm tumour as "2.4 mm" — a tenfold understatement.
const cleanMeasurement = (measurementStr, unit = 'mm') => {
  if (!measurementStr) return '';

  // Extract just the numeric part
  const numericValue = measurementStr.toString().replace(/[^\d.]/g, '');

  // If there's a value, return it with a single unit
  if (numericValue && numericValue.trim() !== '') {
    const safeUnit = measurementUnitOptions.includes(unit) ? unit : 'mm';
    return `${numericValue} ${safeUnit}`;
  }

  return '';
};

  // Form validation state
  
  // Function to print diagnosis details
  const printDiagnosis = (diagnosisId) => {
    const diagnosisToPrint = diagnosisData.find(diagnosis => diagnosis.diagnosisId === diagnosisId);
    if (!diagnosisToPrint) return;

    const ia = (diagnosisToPrint.initialAssessmentData && diagnosisToPrint.initialAssessmentData.patInitiAssesmentDto) || null;

    const resolvedHistology = diagnosisToPrint.histology
      || diagnosisToPrint.histologyList?.[0]?.histologyDescription
      || (ia && ia.histologyText)
      || '';

    const resolvedHistologyGrade = diagnosisToPrint.histologyGrade
      || (diagnosisToPrint.histologyGrades?.length > 0 ? diagnosisToPrint.histologyGrades[0].gradeCode : null)
      || (ia && ia.histologyGradeText)
      || '';

    const resolvedDescription = diagnosisToPrint.diagnosisDescription || diagnosisToPrint.description || '';

    const resolvedCancerStage = (ia && ia.stageMasterDTO && (ia.stageMasterDTO.stageName || ia.stageMasterDTO.label)) || '';


    const logoUrl = `${window.location.origin}${process.env.PUBLIC_URL || ''}${hospitalDetails.logo}`;

    // Generate HTML content for printing
    const printContent = `
      <html>
        <head>
          <title>Diagnosis Report - ${diagnosisToPrint.dateOfDiagnosis ? formatDate(diagnosisToPrint.dateOfDiagnosis) : 'Diagnosis'}</title>
          <style>
            @page { size: auto; margin: 0mm 0mm 12mm 0mm; }
            @page { @bottom-center { content: "Page " counter(page) " of " counter(pages); font-family: Arial, sans-serif; font-size: 11px; color: #555; } }
            @page { @bottom-left { content: none; } @bottom-right { content: none; } @top-left { content: none; } @top-center { content: none; } @top-right { content: none; } }
            body { font-family: Arial, sans-serif; margin: 15mm 20mm; color: #333; }
            .letterhead { display: flex; align-items: center; padding: 12px 0 12px 0; border-bottom: 2px solid #0056b3; margin-bottom: 18px; }
            .letterhead-logo { flex: 0 0 90px; display: flex; align-items: center; justify-content: center; }
            .letterhead-logo img { max-width: 80px; max-height: 80px; }
            .letterhead-info { flex: 1; padding-left: 15px; }
            .letterhead-name { font-weight: bold; font-size: 18px; color: #0056b3; margin-bottom: 3px; }
            .letterhead-subname { font-size: 13px; margin-bottom: 4px; }
            .letterhead-address, .letterhead-contact { font-size: 12px; margin-bottom: 2px; }
            .report-title { text-align: center; font-size: 16px; font-weight: bold; color: #0056b3; margin-bottom: 6px; }
            .report-date { text-align: center; font-size: 13px; margin-bottom: 16px; color: #555; }
            .section { margin-bottom: 15px; }
            .section-title { font-weight: bold; color: #2c3e50; border-bottom: 1px solid #eee; padding-bottom: 5px; margin-bottom: 8px; }
            table { width: 100%; border-collapse: collapse; margin-top: 6px; }
            th, td { border: 1px solid #ddd; padding: 7px 8px; text-align: left; font-size: 13px; }
            th { background-color: #f2f2f2; font-weight: bold; }
            p { font-size: 13px; margin: 4px 0; }
          </style>
        </head>
        <body>
          <div class="letterhead">
            <div class="letterhead-logo">
              <img src="${logoUrl}" alt="Hospital Logo" />
            </div>
            <div class="letterhead-info">
              <div class="letterhead-name">${hospitalDetails.name}</div>
              <div class="letterhead-subname">${hospitalDetails.subName}</div>
              <div class="letterhead-address">${hospitalDetails.address}</div>
              <div class="letterhead-contact">
                ${hospitalDetails.phone1}${hospitalDetails.phone2 ? ' | ' + hospitalDetails.phone2 : ''}
                ${hospitalDetails.website ? ' | ' + hospitalDetails.website : ''}
                ${hospitalDetails.email ? ' | ' + hospitalDetails.email : ''}
              </div>
            </div>
          </div>
          <div class="report-title">Diagnosis Report</div>
          <div class="report-date">Date: ${diagnosisToPrint.dateOfDiagnosis ? formatDate(diagnosisToPrint.dateOfDiagnosis) : 'N/A'}</div>

          <div class="section">
            <h3 class="section-title">Basic Information</h3>
            <table>
              <tr>
                <th>Status</th>
                <th>Diagnosis Type</th>
                <th>Primary Site</th>
                <th>ICD-10 Diagnosis</th>
                <th>Risk Stratification</th>
              </tr>
              <tr>
                <td>${diagnosisToPrint.status || 'N/A'}</td>
                <td>${diagnosisToPrint.diagnosisType || 'N/A'}</td>
                <td>${formatPrimarySites(diagnosisToPrint)}</td>
                <td>${diagnosisToPrint.icd10Diagnosis || 'N/A'}</td>
                <td>${diagnosisToPrint.riskStratification || 'N/A'}</td>
              </tr>
            </table>
          </div>

          <div class="section">
            <h3 class="section-title">Diagnosis Details</h3>
            <table>
              <tr>
                <th>Cancer Stage</th>
                <th>Pathology</th>
                <th>Histology</th>
                <th>Histology Grade</th>
              </tr>
              <tr>
                <td>${resolvedCancerStage || 'N/A'}</td>
                <td>${(ia && ia.pathologyText) || 'N/A'}</td>
                <td>${resolvedHistology || 'N/A'}</td>
                <td>${resolvedHistologyGrade || 'N/A'}</td>
              </tr>
            </table>
            ${ia && ia.patientCancerSiteDTOList && ia.patientCancerSiteDTOList.length > 0 ? `
            <p><strong>Current Diagnosis: </strong>${ia.patientCancerSiteDTOList.map(s => s.cancerSiteMstDto && s.cancerSiteMstDto.cancerSiteName ? s.cancerSiteMstDto.cancerSiteName : '').filter(Boolean).join(', ')}</p>` : ''}
            ${ia && ia.patientPrevCancerSiteDTOList && ia.patientPrevCancerSiteDTOList.length > 0 ? `
            <p><strong>Previous Cancer History: </strong>${ia.patientPrevCancerSiteDTOList.map(s => s.cancerSiteMstDTO && s.cancerSiteMstDTO.cancerSiteName ? s.cancerSiteMstDTO.cancerSiteName : '').filter(Boolean).join(', ')}</p>` : ''}
            ${ia && ia.patientMutationXrefList && ia.patientMutationXrefList.length > 0 ? `
            <p><strong>Mutation: </strong>${ia.patientMutationXrefList.map(m => m.mutationMstDTO && m.mutationMstDTO.mutationName ? m.mutationMstDTO.mutationName : '').filter(Boolean).join(', ')}</p>` : ''}
            ${ia && ia.patientLimbLossDTOList && ia.patientLimbLossDTOList.length > 0 ? `
            <p><strong>Limb Loss: </strong>${ia.patientLimbLossDTOList.map(l => l.limbLossMasterDTO && l.limbLossMasterDTO.limbLossName ? l.limbLossMasterDTO.limbLossName : '').filter(Boolean).join(', ')}</p>` : ''}
          </div>

          ${resolvedDescription ? `
          <div class="section">
            <h3 class="section-title">Diagnosis Description</h3>
            <p>${resolvedDescription.replace(/\n/g, '<br>')}</p>
          </div>` : ''}

          ${diagnosisToPrint.metastasis ? `
          <div class="section">
            <h3 class="section-title">Metastasis Information</h3>
            <p><strong>Metastasis: </strong>${diagnosisToPrint.metastasis}</p>
            ${diagnosisToPrint.metastasisDate ? `<p><strong>Metastasis Date: </strong>${diagnosisToPrint.metastasisDate}</p>` : ''}
            ${diagnosisToPrint.metastasisDetails ? `<p><strong>Details: </strong>${diagnosisToPrint.metastasisDetails.replace(/\n/g, '<br>')}</p>` : ''}
            ${diagnosisToPrint.metastaticSites && diagnosisToPrint.metastaticSites.length > 0 ? `
            <p><strong>Metastatic Sites: </strong>${diagnosisToPrint.metastaticSites.map(site => typeof site === 'string' ? site : site.siteName).join(', ')}</p>` : ''}
          </div>` : ''}

          ${diagnosisToPrint.stagingDetails && diagnosisToPrint.stagingDetails.length > 0 ? `
          <div class="section">
            <h3 class="section-title">Staging Information</h3>
            <table>
              <tr>
                <th>Type</th>
                <th>T</th>
                <th>N</th>
                <th>M</th>
                <th>Stage</th>
                <th>Date</th>
              </tr>
              ${diagnosisToPrint.stagingDetails.map(staging => `
              <tr>
                <td>${staging.type || staging.stagingType || 'N/A'}</td>
                <td>${staging.t || staging.tStage || 'N/A'}</td>
                <td>${staging.n || staging.nStage || 'N/A'}</td>
                <td>${staging.m || staging.mStage || 'N/A'}</td>
                <td>${staging.stage || 'N/A'}</td>
                <td>${staging.date ? formatDate(staging.date) : (staging.stagingDate ? formatDate(staging.stagingDate) : 'N/A')}</td>
              </tr>
              `).join('')}
            </table>
          </div>` : ''}

          ${diagnosisToPrint.tumorSizes && diagnosisToPrint.tumorSizes.length > 0 ? `
          <div class="section">
            <h3 class="section-title">Tumor Information</h3>
            <table>
              <tr>
                <th>Length</th>
                <th>Width</th>
                <th>Depth</th>
                <th>T Stage</th>
                <th>Description</th>
              </tr>
              ${diagnosisToPrint.tumorSizes.map(tumor => `
              <tr>
                <td>${tumor.length ? `${tumor.length} ${tumor.lengthUnit || 'mm'}` : 'N/A'}</td>
                <td>${tumor.width ? `${tumor.width} ${tumor.widthUnit || 'mm'}` : 'N/A'}</td>
                <td>${tumor.depth ? `${tumor.depth} ${tumor.depthUnit || 'mm'}` : 'N/A'}</td>
                <td>${tumor.tStage || 'N/A'}</td>
                <td>${tumor.description || 'N/A'}</td>
              </tr>
              `).join('')}
            </table>
          </div>` : ''}

          ${diagnosisToPrint.lymphNodes && diagnosisToPrint.lymphNodes.length > 0 ? `
          <div class="section">
            <h3 class="section-title">Lymph Nodes</h3>
            <table>
              <tr>
                <th>LN Group</th>
                <th>Length</th>
                <th>Width</th>
                <th>Depth</th>
                <th>N Stage</th>
                <th>Description</th>
              </tr>
              ${diagnosisToPrint.lymphNodes.map(node => `
              <tr>
                <td>${node.lnGroup || 'N/A'}</td>
                <td>${node.length ? `${node.length} ${node.lengthUnit || 'mm'}` : 'N/A'}</td>
                <td>${node.width ? `${node.width} ${node.widthUnit || 'mm'}` : 'N/A'}</td>
                <td>${node.depth ? `${node.depth} ${node.depthUnit || 'mm'}` : 'N/A'}</td>
                <td>${node.nStage || 'N/A'}</td>
                <td>${node.description || 'N/A'}</td>
              </tr>
              `).join('')}
            </table>
          </div>` : ''}

          ${diagnosisToPrint.biomarkers && diagnosisToPrint.biomarkers.length > 0 ? `
          <div class="section">
            <h3 class="section-title">Biomarkers</h3>
            <table>
              <tr>
                <th>Name</th>
                <th>Status</th>
                <th>Value</th>
              </tr>
              ${diagnosisToPrint.biomarkers.map(biomarker => `
              <tr>
                <td>${biomarker.name || 'N/A'}</td>
                <td>${biomarker.status || 'N/A'}</td>
                <td>${biomarker.value || 'N/A'}</td>
              </tr>
              `).join('')}
            </table>
          </div>` : ''}

          ${diagnosisToPrint.remarks ? `
          <div class="section">
            <h3 class="section-title">Remarks</h3>
            <p>${diagnosisToPrint.remarks.replace(/\n/g, '<br>')}</p>
          </div>` : ''}

        </body>
      </html>
    `;
    
    // Create a new window and print
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.onload = function() {
      printWindow.print();
      // printWindow.close(); // Option to auto-close after print
    };
  };

  // Function to save diagnosis data
  // const saveDiagnosis = async (initialAssessmentId) => {
  //   // First validate the form for diagnosis
  //   if (!validateForm()) return { success: false, error: "Diagnosis validation failed" };
    
  //   setSaveLoader(true);
    
  //   try {
  //     // Prepare diagnosis data
  //     const diagnosisDTO = prepareDataForAPI(initialAssessmentId);
      
  //     // Override the initialAssessmentId with the one provided
  //     diagnosisDTO.patientIniAssessmentId = initialAssessmentId;
      
  //     // Ensure required fields are present
  //     if (!diagnosisDTO.dateOfDiagnosis) {
  //       diagnosisDTO.dateOfDiagnosis = new Date().toISOString().split('T')[0]; // Set today's date as default
  //     }
      
  //     // Ensure metastasis is one of the allowed values
  //     if (diagnosisDTO.metastasis && !['Yes', 'No', "Don't Know"].includes(diagnosisDTO.metastasis)) {
  //       diagnosisDTO.metastasis = "Don't Know"; // Default to "Don't Know" if invalid value
  //     }
      
  //     console.log("Final diagnosis data being sent:", diagnosisDTO);
      
  //     const endpoint = currentEditIndex !== null ? 
  //       '/patientDiagnosis/update' : 
  //       '/patientDiagnosis/save';
      
  //     console.log(`Sending diagnosis data to endpoint: ${C.getUrl()}${endpoint}`, diagnosisDTO);
      
  //     const response = await fetch(`${C.getUrl()}${endpoint}`, {
  //       method: 'PUT',
  //       headers: {
  //         ...getAuthHeaders(),
  //         'Content-Type': 'application/json',
  //         'Accept': 'application/json, text/plain, */*'
  //       },
  //       body: JSON.stringify(diagnosisDTO)
  //     });
      
  //     // Log HTTP status to help debug
  //     console.log(`Diagnosis API response status: ${response.status}`);
      
  //     // Try to get response as text first
  //     const responseText = await response.text();
  //     console.log('Response text:', responseText);
      
  //     let data;
  //     try {
  //       // Try to parse the response as JSON
  //       data = JSON.parse(responseText);
  //       console.log('Save diagnosis response (parsed):', data);
  //     } catch (parseError) {
  //       console.log('Could not parse response as JSON, using as text');
  //       data = { message: responseText };
  //     }
      
  //     if (!response.ok || (data.errorMsgs && data.errorMsgs.length > 0)) {
  //       const errorMessage = data.errorMsgs ? data.errorMsgs.join(', ') : `Failed to save diagnosis. Status: ${response.status}`;
  //       throw new Error(errorMessage);
  //     }
      
  //     // Update local state
  //     try {
  //       if (isMounted.current) {
  //         await fetchPatientDiagnoses();
  //       }
  //     } catch (fetchErr) {
  //       console.error("Error refreshing diagnoses:", fetchErr);
  //     }
      
  //     hideDiagnosisForm();
      
  //     return { success: true, data };
  //   } catch (err) {
  //     console.error("Error saving diagnosis:", err);
  //     return { success: false, error: err.message || "Unknown error" };
  //   } finally {
  //     if (isMounted.current) {
  //       setSaveLoader(false);
  //     }
  //   }
  // };
  // Modify the saveDiagnosis function in DiagnosisManagement.jsx

const saveDiagnosis = async (initialAssessmentId) => {
  // First validate the form for diagnosis
  if (!validateForm()) return { success: false, error: "Diagnosis validation failed" };
  
  setSaveLoader(true);
  
  try {
    // Prepare diagnosis data
    const diagnosisDTO = prepareDataForAPI(initialAssessmentId);
    
    // Override the initialAssessmentId with the one provided
    diagnosisDTO.patientIniAssessmentId = initialAssessmentId;
    
    // Ensure required fields are present
    if (!diagnosisDTO.dateOfDiagnosis) {
      diagnosisDTO.dateOfDiagnosis = new Date().toISOString().split('T')[0]; // Set today's date as default
    }
    
    // Ensure metastasis is one of the allowed values
    if (diagnosisDTO.metastasis && !['Yes', 'No', "Don't Know"].includes(diagnosisDTO.metastasis)) {
      diagnosisDTO.metastasis = "Don't Know"; // Default to "Don't Know" if invalid value
    }
    
    
    const endpoint = currentEditIndex !== null ?
      '/patientDiagnosis/update' :
      '/patientDiagnosis/save';

    console.log('[diagnosis] PUT', endpoint, '\npayload:', JSON.stringify(diagnosisDTO, null, 2));
    // Captured separately because these are the fields this branch changed:
    // a constraint violation most likely involves one of them.
    recordDiagDebug('PUT ' + endpoint + ' — changed fields', {
      extractionResultId: diagnosisDTO.extractionResultId,
      // Absent from this log means the key was absent from the body, which is
      // the "leave the existing links alone" case — not a bug.
      primarySites: diagnosisDTO.primarySites,
      metastasisDate: diagnosisDTO.metastasisDate,
      metastaticSites: diagnosisDTO.metastaticSites,
      tumorSizes: diagnosisDTO.tumorSizes,
      lymphNodes: diagnosisDTO.lymphNodes,
      biomarkers: diagnosisDTO.biomarkers,
      stagingDetails: diagnosisDTO.stagingDetails,
    });
    recordDiagDebug('PUT ' + endpoint + ' — full payload', diagnosisDTO);

    const response = await fetch(`${C.getUrl()}${endpoint}`, {
      method: 'PUT',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
        'Accept': 'application/json, text/plain, */*'
      },
      body: JSON.stringify(diagnosisDTO)
    });

    // Try to get response as text first
    const responseText = await response.text();
    console.log('[diagnosis] save response', response.status, responseText);
    recordDiagDebug('save response ' + response.status, responseText);
    
    let data;
    try {
      // Try to parse the response as JSON
      data = JSON.parse(responseText);
    } catch (parseError) {
      data = { message: responseText };
    }
    
    if (!response.ok || (data.errorMsgs && data.errorMsgs.length > 0)) {
      const errorMessage = data.errorMsgs ? data.errorMsgs.join(', ') : `Failed to save diagnosis. Status: ${response.status}`;
      throw new Error(errorMessage);
    }
    
    // Get the diagnosis ID from the response
    let diagnosisId = null;
    if (data && data.message) {
      try {
        const diagnosisData = JSON.parse(data.message);
        diagnosisId = diagnosisData.diagnosisId;
      } catch (e) {
      }
    } else if (data && data.diagnosisId) {
      diagnosisId = data.diagnosisId;
    } else if (currentEditIndex !== null) {
      // If we're editing, use the existing ID
      diagnosisId = formData.diagnosisId;
    }
    
    // If we have staging data and a diagnosis ID, save the staging with the diagnosis ID
    if (diagnosisId && formData.stagingDetails && formData.stagingDetails.length > 0) {
      const stagingDetail = formData.stagingDetails[0];
      
      // Only save if there's actual staging data to save
      if (stagingDetail.type || stagingDetail.t || stagingDetail.n || stagingDetail.m || stagingDetail.stage) {
        // Format date correctly for API
        let formattedDate = null;
        if (stagingDetail.date) {
          try {
            const parsedDate = moment(stagingDetail.date, ["DD-MM-YYYY", "YYYY-MM-DD", "MM-DD-YYYY"]);
            if (parsedDate.isValid()) {
              formattedDate = parsedDate.format("YYYY-MM-DD");
            }
          } catch (dateError) {
          }
        }
        
        // Prepare staging data including the diagnosis ID
        const stagingData = {
          patientId: getPatientId(),
          diagnosisId: diagnosisId, // Link to the diagnosis
          activeInd: 'Y',
          stagingType: stagingDetail.type || '',
          tStage: stagingDetail.t || '',
          nStage: stagingDetail.n || '',
          mStage: stagingDetail.m || '',
          stage: stagingDetail.stage || '',
          stagingDate: formattedDate || new Date().toISOString().split('T')[0],
          createdById: parseInt(localStorage.getItem('userId') || doctorId || 1, 10),
          updatedById: parseInt(localStorage.getItem('userId') || doctorId || 1, 10),
          createdDate: new Date().toISOString(),
          updatedDate: new Date().toISOString()
        };
        
        
        // Save the staging data
        const stagingResponse = await fetch(C.getUrl() + "/patientStaging/save", {
          method: "PUT",
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "application/json"
          },
          body: JSON.stringify(stagingData)
        });
        
        if (!stagingResponse.ok) {
        } else {
        }
      }
    }
    
    // Update local state
    try {
      if (isMounted.current) {
        await fetchPatientDiagnoses();
      }
    } catch (fetchErr) {
    }
    
    hideDiagnosisForm();
    
    return { success: true, data };
  } catch (err) {
    return { success: false, error: err.message || "Unknown error" };
  } finally {
    if (isMounted.current) {
      setSaveLoader(false);
    }
  }
};
 // Add this modified function to ensure staging data is properly fetched and associated with each diagnosis
 const fetchStagingForDiagnosis = async (patientId, diagnosisId) => {
  if (!patientId) {
    return;
  }
  
  
  try {
   // const response = await fetch(`${C.getUrl()}/patientStaging/getByPatientId?patientId=${patientId}`);
   const response = await fetch(`${C.getUrl()}/patientStaging/getByDiagnosisId?diagnosisId=${diagnosisId}`);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch staging data. Status: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.statusCode === 200 && data.message) {
      try {
        const stagings = JSON.parse(data.message);
        // Get active stagings - we'll also include those that match the diagnosis ID
        const relevantStagings = stagings.filter((s) => 
          (s.activeInd === "Y") || 
          (s.diagnosisId && s.diagnosisId.toString() === diagnosisId.toString())
        );
        
        
        if (relevantStagings && relevantStagings.length > 0) {
          // Find the diagnosis object in the diagnosisData array
          setDiagnosisData(prevData => {
            // Make a copy of the previous data
            const updatedData = [...prevData];
            
            // Find the diagnosis index
            const diagnosisIndex = updatedData.findIndex(d => 
              d.diagnosisId && d.diagnosisId.toString() === diagnosisId.toString()
            );
            
            if (diagnosisIndex >= 0) {
              // Map all relevant staging records to the proper format
              const mappedStagingDetails = relevantStagings.map((s) => ({
                stagingId: s.stagingId,
                type: s.stagingType || "",
                t: s.tStage || "",
                n: s.nStage || "",
                m: s.mStage || "",
                stage: s.stage || "",
                date: s.stagingDate ? moment(s.stagingDate).format("DD-MM-YYYY") : null,
                isSaved: true,
                isModified: false
              }));
              
              
              // Update the diagnosis with staging details
              updatedData[diagnosisIndex] = {
                ...updatedData[diagnosisIndex],
                stagingDetails: mappedStagingDetails
              };
              
              return updatedData;
            }
            
            // If we didn't find the diagnosis, return the original array
            return prevData;
          });
          
          // Also update the fetchedStagingIds
          setFetchedStagingIds(prev => new Set([...prev, diagnosisId]));
        }
      } catch (error) {
      }
    }
  } catch (err) {
  }
};
  // Fetch data and methods to expose to parent through ref
  useImperativeHandle(ref, () => ({
    // Original diagnosis saving method - now just calls the component function
    saveDiagnosis,
    
    // Method to get initial assessment data for API
    getInitialAssessmentData: () => {
      return {
        doctorObservation,
        pathology,
        cancerStage,
        boneMetasis,
        brainMets,
        limbLossIdIAD,
        mutationIdIA,
        canSiteIdIAD,
        prevCanSiteIdIAD
      };
    },
    
    // Method to validate initial assessment
    validateInitialAssesment: () => validateInitialAssesment(),
    
    // Add a method to properly handle OP Visit completion
    // notifyOpVisitSaveComplete: async () => {
    //   if (opVisitRef.current) {
    //     console.log("Notifying OP Visit save complete, refreshing data");
        
    //     try {
    //       // First hide the form to ensure state is reset
    //       opVisitRef.current.hideForm();
          
    //       // Then explicitly refresh the data with a small delay to ensure proper loading
    //       setTimeout(async () => {
    //         await opVisitRef.current.refreshData();
    //         console.log("OP Visit data refreshed after notification");
    //       }, 1000);
    //     } catch (error) {
    //       console.error("Error during OP Visit refresh:", error);
    //     }
    //   }
    // }
  }));




  // Function to get auth headers
  const getAuthHeaders = () => {
    return {
      'Content-Type': 'application/json'
    };
  };

  // Get the patient ID from props or localStorage
  const getPatientId = () => {
    // First try the prop
    if (patientId) {
      return patientId;
    }
    
    // Then try localStorage
    const storedPatientId = localStorage.getItem('PatientpatientId');
    if (storedPatientId) {
      return storedPatientId;
    }
    
    // As a final fallback, try to get it from other localStorage items
    const firstName = localStorage.getItem('PatientfirstName');
    if (firstName) {
      const patId = localStorage.getItem('PatientpatientId');
      if (patId) {
        return patId;
      }
    }
    
    return null;
  };

  // Function to fetch patient diagnoses
  const fetchPatientDiagnoses = async () => {
    const patientId = getPatientId();
    
    // Validate patient ID
    if (!patientId) {
      setError('No patient selected. Please select a patient first.');
      return;
    }
  
    setLoading(true);
    setError(null);

    try {
      // Normalize patientId to number to ensure consistency
      const normalizedPatientId = Number(patientId);
      if (!normalizedPatientId || isNaN(normalizedPatientId)) {
        throw new Error('Invalid patient ID');
      }

      // First fetch the initial assessment details for the patient
      const initialAssessmentDetails = await fetchInitialAssessmentDetails(normalizedPatientId);

      // Then fetch the diagnoses
      const primaryUrl = `${C.getUrl()}/patientDiagnosis/get/${normalizedPatientId}`;
      console.log('[diagnosis] GET', primaryUrl, '| patientId=', normalizedPatientId, typeof normalizedPatientId);

      let response = await fetch(primaryUrl, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      // If that fails, try a fallback endpoint. Keep the primary status: the
      // fallback path does not exist on the backend, so it always 404s, and
      // reporting ITS status hid whatever actually went wrong on the primary.
      const primaryStatus = response.status;
      let primaryBody = '';

      if (!response.ok) {
        // Read the primary failure BEFORE it is overwritten — on a 500 this is
        // Spring's error JSON, which names the underlying exception.
        try {
          primaryBody = await response.text();
        } catch (readErr) {
          primaryBody = '<body could not be read>';
        }
        console.error(
          '[diagnosis] primary failed:', primaryStatus, primaryUrl,
          '\nresponse body:', primaryBody
        );
        recordDiagDebug('GET failed ' + primaryStatus + ' ' + primaryUrl, primaryBody);

        response = await fetch(`${C.getUrl()}/diagnoses/patient/${normalizedPatientId}`, {
          method: 'GET',
          headers: getAuthHeaders(),
        });
        if (!response.ok) {
          console.error('[diagnosis] fallback failed:', response.status);
        }
      }

      if (!response.ok) {
        if (primaryStatus === 401) {
          throw new Error('Your session has expired. Please sign in again.');
        }

        // Surface the backend's own reason when it sent one, instead of a
        // bare status code that says nothing about the cause.
        let detail = '';
        try {
          const parsed = JSON.parse(primaryBody);
          detail = parsed.message || parsed.error || parsed.errorMsg || '';
        } catch (e) {
          detail = primaryBody ? String(primaryBody).slice(0, 300) : '';
        }

        throw new Error(
          `Failed to fetch diagnoses. Status: ${primaryStatus}` +
          (detail ? ` — ${detail}` : '') +
          ' (see browser console for the full response)'
        );
      }
  
      const data = await response.json();
      
      // Process the data based on format
      let processedData = [];
      if (data && data.statusCode === 200 && data.message) {
        // Handle API response with statusCode/message structure
        try {
          processedData = JSON.parse(data.message);
        } catch (e) {
          processedData = data.message;
        }
      } else if (Array.isArray(data)) {
        // Direct array of diagnoses
        processedData = data;
      } else if (data && data.diagnoses && Array.isArray(data.diagnoses)) {
        // Nested diagnoses array
        processedData = data.diagnoses;
      } else if (data && typeof data === 'object') {
        // Single diagnosis object
        processedData = [data];
      }
  
      // Ensure it's an array
      if (!Array.isArray(processedData)) {
        processedData = [];
      }
  
      // Combine diagnoses with initial assessment details
      const diagnosesWithDetails = processedData.map(diagnosis => {
        return {
          ...diagnosis,
          initialAssessmentData: initialAssessmentDetails
        };
      });
  
      setDiagnosisData(diagnosesWithDetails);
      
      if (diagnosesWithDetails.length === 0) {
        setError('No diagnosis data found for this patient.');
      } else {
        // Fetch staging data for each diagnosis immediately
        for (const diagnosis of diagnosesWithDetails) {
          if (diagnosis.diagnosisId) {
            await fetchStagingForDiagnosis(patientId, diagnosis.diagnosisId);
          }
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to load diagnoses. Please try again.');
      setDiagnosisData([]);
    } finally {
      setLoading(false);
    }
  };

  // Notify parent when diagnosis data is loaded
  useEffect(() => {
    if (onDiagnosisLoaded) {
      const hasDiagnosis = diagnosisData && diagnosisData.length > 0;
      let latestDate = '';
      let latestPrimarySite = '';
      // The rows, not just the lead name: the Confirm New Treatment Plan popup
      // queries protocols by siteId, and the deprecated scalar carries none.
      let latestPrimarySites = [];
      // Feeds the "Ongoing Clinical Trials" link on that same popup, which
      // takes site + histology + biomarkers as query parameters.
      let latestHistology = '';
      let latestBiomarkerNames = [];
      if (hasDiagnosis) {
        const sorted = [...diagnosisData].sort((a, b) => {
          const dateA = a.dateOfDiagnosis ? new Date(a.dateOfDiagnosis) : new Date(0);
          const dateB = b.dateOfDiagnosis ? new Date(b.dateOfDiagnosis) : new Date(0);
          return dateB - dateA;
        });
        latestDate = sorted[0].dateOfDiagnosis ? formatDate(sorted[0].dateOfDiagnosis) : '';
        latestPrimarySite = sorted[0].primarySite || '';
        latestPrimarySites = readDiagnosisPrimarySites(sorted[0]) || [];
        // Same fallback chain the diagnosis card and the printout use: the
        // scalar when the row carries one, otherwise the linked histology, and
        // finally the free text captured on the initial assessment.
        latestHistology = sorted[0].histology
          || sorted[0].histologyList?.[0]?.histologyDescription
          || sorted[0].initialAssessmentData?.patInitiAssesmentDto?.histologyText
          || '';
        latestBiomarkerNames = Array.isArray(sorted[0].biomarkers)
          ? sorted[0].biomarkers
              .map(biomarker => (biomarker && biomarker.name ? String(biomarker.name).trim() : ''))
              .filter(name => name !== '')
          : [];
      }
      // The third argument stays on the deprecated scalar deliberately —
      // CaseProfile renders it as prose ("primary site X"), where a comma list
      // would read badly.
      onDiagnosisLoaded(hasDiagnosis, latestDate, latestPrimarySite, latestPrimarySites, {
        histology: latestHistology,
        biomarkers: latestBiomarkerNames,
      });
    }
  }, [diagnosisData]);

  // Load diagnoses when patient ID is available
  useEffect(() => {
    const patientId = getPatientId();
    if (patientId) {
      fetchPatientDiagnoses();
    } else {
    }
    
    // Fetch additional data for the moved fields
    getAllCancerSite();
    getAllCancerSitesForPrevCancerSites();
    getAllLimbLossMstData();
    getAllMutation();
    getCancerStage();
    fetchDiagnosisStatuses();
    fetchDiagnosisTypes();
    fetchPrimarySites();
    fetchHistologies();
    fetchHistologyGrades();
    fetchBiomarkers();
    fetchBiomarkerStatuses();
    // Only call fetchStagingForDiagnosis when both patientId and diagnosisId are available
    // diagnosisId will be available after diagnosis data is fetched in fetchDiagnoses
    // So this separate call is removed as it's already handled within fetchDiagnoses for each diagnosis
    
    // If patient ID is available, get initial assessment data
    if (patientId) {
      getInitialAssessmentDetails(patientId);
    }
  }, [patientId]); // Re-run when patientId prop changes

  // Set isMounted to false when component unmounts
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Data fetching methods for moved fields
  const getAllCancerSite = async () => {
    fetch(C.getUrl() + '/cancerSite/getAllCancerSites')
      .then(res => res.json())
      .then(res => {
        let resObj = JSON.parse(res.message);
        var arr = [];
        resObj.map(function (d) {
          arr.push({ id: d.selectionId, name: d.selectionValue });
        });
        setCanSitesArr(arr);
      })
      .catch(err => {
      });
  };

  const getAllCancerSitesForPrevCancerSites = async () => {
    fetch(C.getUrl() + '/cancerSite/getAllCancerSitesForPrevCancerSites')
      .then(res => res.json())
      .then(res => {
        let resObj = JSON.parse(res.message);
        var arr = [];
        resObj.map(function (d) {
          arr.push({ id: d.selectionId, name: d.selectionValue });
        });
        setPrevCanSitesArr(arr);
      })
      .catch(err => {
      });
  };

  const getAllLimbLossMstData = async () => {
    fetch(C.getUrl() + '/limbLossMst/getAllLimbLoss')
      .then(res => res.json())
      .then(res => {
        let resObj = JSON.parse(res.message);
        var arr = [];
        resObj.map(function (d) {
          arr.push({ id: d.selectionId, name: d.selectionValue });
        });
        setLimbLossArr(arr);
      })
      .catch(err => {
      });
  };

  const getAllMutation = async () => {
    fetch(C.getUrl() + '/mutation/getAll')
      .then(res => res.json())
      .then(res => {
        let resObj = JSON.parse(res.message);
        var arr = [];
        resObj.map(function (d) {
          arr.push({ id: d.selectionId, name: d.selectionValue });
        });
        setMutationArr(arr);
      })
      .catch(err => {
      });
  };

  const getCancerStage = async () => {
    fetch(C.getUrl() + '/stageMaster/getallStages/')
      .then(res => res.json())
      .then(res => {
        let data = JSON.parse(res.message);
        var arr = [];
        data.map((item, index) => {
          let jsonList = new Object();
          jsonList['key'] = Crypt.getUniqueKey(index);
          jsonList['id'] = item.stageId;
          jsonList['label'] = item.stageName;
          arr.push(jsonList);
        });
        setCancerStageMstArr(arr);
      })
      .catch(err => {
      });
  };

  // const fetchDiagnosisStatuses = async () => {
  //   setLoadingDropdowns(prev => ({ ...prev, statuses: true }));
  //   try {
  //     const response = await fetch(`${C.getUrl()}/patientDiagnosis/diagnosisStatuses`, {
  //       method: 'GET',
  //       headers: getAuthHeaders(),
  //     });

  //     if (!response.ok) {
  //       throw new Error('Failed to fetch diagnosis statuses');
  //     }

  //     const data = await response.json();
  //     setDiagnosisStatuses(data);
  //   } catch (err) {
  //     console.error("Error fetching diagnosis statuses:", err);
  //   } finally {
  //     setLoadingDropdowns(prev => ({ ...prev, statuses: false }));
  //   }
  // };

  // const fetchDiagnosisTypes = async () => {
  //   setLoadingDropdowns(prev => ({ ...prev, types: true }));
  //   try {
  //     const response = await fetch(`${C.getUrl()}/patientDiagnosis/diagnosisTypes`, {
  //       method: 'GET',
  //       headers: getAuthHeaders(),
  //     });

  //     if (!response.ok) {
  //       throw new Error('Failed to fetch diagnosis types');
  //     }

  //     const data = await response.json();
  //     setDiagnosisTypes(data);
  //   } catch (err) {
  //     console.error("Error fetching diagnosis types:", err);
  //   } finally {
  //     setLoadingDropdowns(prev => ({ ...prev, types: false }));
  //   }
  // };
  // Modify the fetchDiagnosisStatuses and fetchDiagnosisTypes functions to filter duplicates

const fetchDiagnosisStatuses = async () => {
  setLoadingDropdowns(prev => ({ ...prev, statuses: true }));
  try {
    const response = await fetch(`${C.getUrl()}/patientDiagnosis/diagnosisStatuses`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch diagnosis statuses');
    }

    const data = await response.json();
    
    // Filter out duplicates based on statusName
    const uniqueStatuses = [];
    const statusNames = new Set();
    
    data.forEach(status => {
      if (!statusNames.has(status.statusName)) {
        statusNames.add(status.statusName);
        uniqueStatuses.push(status);
      }
    });
    
    setDiagnosisStatuses(uniqueStatuses);
  } catch (err) {
  } finally {
    setLoadingDropdowns(prev => ({ ...prev, statuses: false }));
  }
};

const fetchDiagnosisTypes = async () => {
  setLoadingDropdowns(prev => ({ ...prev, types: true }));
  try {
    const response = await fetch(`${C.getUrl()}/patientDiagnosis/diagnosisTypes`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch diagnosis types');
    }

    const data = await response.json();
    
    // Filter out duplicates based on typeName
    const uniqueTypes = [];
    const typeNames = new Set();
    
    data.forEach(type => {
      if (!typeNames.has(type.typeName)) {
        typeNames.add(type.typeName);
        uniqueTypes.push(type);
      }
    });
    
    setDiagnosisTypes(uniqueTypes);
  } catch (err) {
  } finally {
    setLoadingDropdowns(prev => ({ ...prev, types: false }));
  }
};

  // Shared with the protocol screens — this endpoint returns a plain array
  // rather than the { statusCode, message } envelope the rest of the API uses,
  // and that quirk is documented once, in PrimarySiteUtils.
  const fetchPrimarySites = async () => {
    setLoadingDropdowns(prev => ({ ...prev, primarySites: true }));
    try {
      setPrimarySites(await fetchPrimarySiteOptions());
    } catch (err) {
    } finally {
      setLoadingDropdowns(prev => ({ ...prev, primarySites: false }));
    }
  };

  const fetchHistologies = async () => {
    setLoadingDropdowns(prev => ({ ...prev, histologies: true }));
    try {
      const response = await fetch(`${C.getUrl()}/patientDiagnosis/histologies`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch histologies');
      }

      const data = await response.json();
      setHistologies(data);
    } catch (err) {
    } finally {
      setLoadingDropdowns(prev => ({ ...prev, histologies: false }));
    }
  };

  const fetchHistologyGrades = async () => {
    setLoadingDropdowns(prev => ({ ...prev, histologyGrades: true }));
    try {
      const response = await fetch(`${C.getUrl()}/patientDiagnosis/histologyGrades`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch histology grades');
      }

      const data = await response.json();
      setHistologyGrades(data);
    } catch (err) {
    } finally {
      setLoadingDropdowns(prev => ({ ...prev, histologyGrades: false }));
    }
  };

  const fetchBiomarkers = async () => {
    setLoadingDropdowns(prev => ({ ...prev, biomarkers: true }));
    try {
      const response = await fetch(`${C.getUrl()}/patientDiagnosis/biomarkers`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch biomarkers');
      }

      const data = await response.json();
      setBiomarkers(data);
    } catch (err) {
    } finally {
      setLoadingDropdowns(prev => ({ ...prev, biomarkers: false }));
    }
  };

  const fetchBiomarkerStatuses = async () => {
    setLoadingDropdowns(prev => ({ ...prev, biomarkerStatuses: true }));
    try {
      const response = await fetch(`${C.getUrl()}/patientDiagnosis/biomarkerStatuses`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch biomarker statuses');
      }

      const data = await response.json();
      setBiomarkerStatuses(data);
    } catch (err) {
    } finally {
      setLoadingDropdowns(prev => ({ ...prev, biomarkerStatuses: false }));
    }
  };

  const saveDiagnosisStatus = async (newStatus) => {
    try {
      const userId = getCurrentUserId();
      const response = await fetch(`${C.getUrl()}/patientDiagnosis/saveDiagnosisStatus`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          statusName: newStatus,
          statusDescription: newStatus,
          createdById: userId,
          updatedById: userId
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to save new diagnosis status');
      }

      const data = await response.json();
      
      // Refresh the diagnosis statuses list
      await fetchDiagnosisStatuses();
      return true;
    } catch (err) {
      swal({
        title: "Error!",
        text: "Failed to save new diagnosis status",
        icon: "error",
        buttons: false,
        timer: 2000,
      });
      return false;
    }
  };

  const saveDiagnosisType = async (newType) => {
    try {
      const userId = getCurrentUserId();
      const response = await fetch(`${C.getUrl()}/patientDiagnosis/saveDiagnosisType`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          typeName: newType,
          typeDescription: newType,
          createdById: userId,
          updatedById: userId
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to save new diagnosis type');
      }

      const data = await response.json();
      
      // Refresh the diagnosis types list
      await fetchDiagnosisTypes();
      return true;
    } catch (err) {
      swal({
        title: "Error!",
        text: "Failed to save new diagnosis type",
        icon: "error",
        buttons: false,
        timer: 2000,
      });
      return false;
    }
  };

  // No savePrimarySite here, unlike the other masters on this form:
  // primary_site_master is a curated clinical vocabulary and is deliberately
  // not extensible from the UI. The "Create <x>" escape hatch was removed from
  // this field, and the unused writer that fed it went with it.

  const saveHistology = async (newHistology) => {
    try {
      const userId = getCurrentUserId();
      const response = await fetch(`${C.getUrl()}/patientDiagnosis/saveHistology`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          histologyName: newHistology,
          histologyDescription: newHistology,
          createdById: userId,
          updatedById: userId
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to save new histology');
      }

      const data = await response.json();
      
      // Refresh the histologies list
      await fetchHistologies();
      return true;
    } catch (err) {
      swal({
        title: "Error!",
        text: "Failed to save new histology",
        icon: "error",
        buttons: false,
        timer: 2000,
      });
      return false;
    }
  };

  const saveHistologyGrade = async (newGrade) => {
    try {
      const userId = getCurrentUserId();
      const response = await fetch(`${C.getUrl()}/patientDiagnosis/saveHistologyGrade`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          gradeName: newGrade,
          gradeDescription: newGrade,
          createdById: userId,
          updatedById: userId
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to save new histology grade');
      }

      const data = await response.json();
      
      // Refresh the histology grades list
      await fetchHistologyGrades();
      return true;
    } catch (err) {
      swal({
        title: "Error!",
        text: "Failed to save new histology grade",
        icon: "error",
        buttons: false,
        timer: 2000,
      });
      return false;
    }
  };

  const saveBiomarker = async (newBiomarker) => {
    try {
      const userId = getCurrentUserId();
      const response = await fetch(`${C.getUrl()}/patientDiagnosis/saveBiomarker`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          biomarkerName: newBiomarker,
          biomarkerDescription: newBiomarker,
          createdById: userId,
          updatedById: userId
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to save new biomarker');
      }

      const data = await response.json();
      
      // Refresh the biomarkers list
      await fetchBiomarkers();
      return true;
    } catch (err) {
      swal({
        title: "Error!",
        text: "Failed to save new biomarker",
        icon: "error",
        buttons: false,
        timer: 2000,
      });
      return false;
    }
  };

  const saveBiomarkerStatus = async (newStatus) => {
    try {
      const userId = getCurrentUserId();
      const response = await fetch(`${C.getUrl()}/patientDiagnosis/saveBiomarkerStatus`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          statusName: newStatus,
          statusDescription: newStatus,
          createdById: userId,
          updatedById: userId
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to save new biomarker status');
      }

      const data = await response.json();
      
      // Refresh the biomarker statuses list
      await fetchBiomarkerStatuses();
      return true;
    } catch (err) {
      swal({
        title: "Error!",
        text: "Failed to save new biomarker status",
        icon: "error",
        buttons: false,
        timer: 2000,
      });
      return false;
    }
  };

  const getInitialAssessmentDetails = (patientId) => {
    fetch(C.getUrl() + '/userCase/getInitialAssessmentDetails?patientId=' + patientId)
      .then(res => res.json())
      .then(res => {
        let resObj = res;
        setInitialAssesmentDetail(JSON.parse(resObj.message));
      })
      .catch(err => {
      });
  };

  const setInitialAssesmentDetail = (caseDetail) => {
    if (caseDetail && caseDetail.patInitiAssesmentDto) {
      let initialAssessment = caseDetail.patInitiAssesmentDto;
      
      setDoctorObservation(initialAssessment.doctorObservation || '');
      setPathology(initialAssessment.pathologyText || '');
      
      if (initialAssessment.stageMasterDTO !== undefined && initialAssessment.stageMasterDTO.stageId !== '') {
        setCancerStage(initialAssessment.stageMasterDTO.stageId);
      }
      
      setBoneMetasis(initialAssessment.boneMetasis || 'N');
      setBrainMets(initialAssessment.brainMets || 'N');
      
      if (caseDetail.patInitiAssesmentDto.patientCancerSiteDTOList
          && caseDetail.patInitiAssesmentDto.patientCancerSiteDTOList?.length !== 0) {
        let patCancerSiteList = caseDetail.patInitiAssesmentDto.patientCancerSiteDTOList;
        var selectedCancerSite = [];
        
        patCancerSiteList.map(function (d) {
          selectedCancerSite.push({ id: d.cancerSiteMstDto.cancerSiteId, name: d.cancerSiteMstDto.cancerSiteName });
        });
        
        setCanSiteIdIAD(selectedCancerSite);
      }
      
      if (caseDetail.patInitiAssesmentDto.patientPrevCancerSiteDTOList
          && caseDetail.patInitiAssesmentDto.patientPrevCancerSiteDTOList?.length !== 0) {
        let patPrevCancerSiteList = caseDetail.patInitiAssesmentDto.patientPrevCancerSiteDTOList;
        var selectedPrevCancerSite = [];
        
        patPrevCancerSiteList.map(function (d) {
          selectedPrevCancerSite.push({ id: d.cancerSiteMstDTO.cancerSiteId, name: d.cancerSiteMstDTO.cancerSiteName });
        });
        
        setPrevCanSiteIdIAD(selectedPrevCancerSite);
      }
      
      if (caseDetail.patInitiAssesmentDto.patientLimbLossDTOList
          && caseDetail.patInitiAssesmentDto.patientLimbLossDTOList?.length !== 0) {
        let patLimbLossList = caseDetail.patInitiAssesmentDto.patientLimbLossDTOList;
        var selectedLimbLoss = [];
        
        patLimbLossList.map(function (d) {
          selectedLimbLoss.push({ id: d.limbLossMasterDTO.limbLossId, name: d.limbLossMasterDTO.limbLossName });
        });
        
        setLimbLossIdIAD(selectedLimbLoss);
      }
      
      if (caseDetail.patInitiAssesmentDto.patientMutationXrefList
          && caseDetail.patInitiAssesmentDto.patientMutationXrefList.size !== 0) {
        let patientMutationXrefList = caseDetail.patInitiAssesmentDto.patientMutationXrefList;
        var selectedMutationId = [];
        
        patientMutationXrefList.map(function (d) {
          selectedMutationId.push({ id: d.mutationMstDTO.mutationId, name: d.mutationMstDTO.mutationName });
        });
        
        setMutationIdIA(selectedMutationId);
      }
    }
  };
  
  // Format date helper functions
  const formatDateMMMDDYYY = (date) => {
    if (date !== undefined && date !== "") {
      var myDate = new Date(date);
      var month = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][myDate.getMonth()];
      var str = month + " " + myDate.getDate() + ", " + myDate.getFullYear();
      return str;
    }
    return "";
  };

  const fetchDiagnosisDetail = async (diagnosisId) => {
    setLoading(true);
    try {
      const response = await fetch(`${C.getUrl()}/patientDiagnosis/getDetail/${diagnosisId}`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });
  
      if (!response.ok) {
        throw new Error('Failed to fetch diagnosis details');
      }
  
      const data = await response.json();
      
      // Process the data based on format
      let diagnosisDetail = null;
      if (data && data.statusCode === 200 && data.message) {
        // Handle API response with statusCode/message structure
        try {
          diagnosisDetail = JSON.parse(data.message);
        } catch (e) {
          diagnosisDetail = data.message;
        }
      } else {
        diagnosisDetail = data;
      }
  
      
      // Handle metastatic sites formatting
      if (diagnosisDetail && diagnosisDetail.metastaticSites && Array.isArray(diagnosisDetail.metastaticSites)) {
        // Format each site object consistently
        const formattedSites = diagnosisDetail.metastaticSites.map(site => {
          if (typeof site === 'string') {
            return { siteName: site, siteId: '' };
          }
          return {
            siteId: site.siteId || site.id || '',
            siteName: site.siteName || site
          };
        });
        
        // Update both the state and the detail object
        setSelectedMetastaticSites(formattedSites);
        diagnosisDetail.metastaticSites = formattedSites;
      } else {
        setSelectedMetastaticSites([]);
      }
  
      // Format staging details
      if (diagnosisDetail && diagnosisDetail.stagingDetails) {
        // Ensure the staging details are in the correct format
        diagnosisDetail.stagingDetails = diagnosisDetail.stagingDetails.map(staging => ({
          stagingId: staging.stagingId || 0,
          type: staging.type || staging.stagingType || '',
          t: staging.t || staging.tStage || '',
          n: staging.n || staging.nStage || '',
          m: staging.m || staging.mStage || '',
          stage: staging.stage || '',
          date: staging.date ? moment(staging.date).format("DD-MM-YYYY") : 
                (staging.stagingDate ? moment(staging.stagingDate).format("DD-MM-YYYY") : null),
          isSaved: true,
          isModified: false
        }));
      } else if (diagnosisDetail) {
        // If no staging details, initialize with empty array
        diagnosisDetail.stagingDetails = [];
        
        // Try to fetch staging details separately
        const patientId = getPatientId();
        if (patientId) {
          fetchStagingForDiagnosis(patientId, diagnosisId);
        }
      }
      
      setLoading(false);
      return diagnosisDetail;
    } catch (err) {
      setError("Failed to load diagnosis details. Please try again.");
      setLoading(false);
      return null;
    }
  };

  // Delete diagnosis
  const deleteDiagnosis = async (diagnosisId) => {
    // Confirm before deletion
    const willDelete = await swal({
      title: "Are you sure?",
      text: "Once deleted, you will not be able to recover this diagnosis!",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });
    
    if (willDelete) {
      try {
        const response = await fetch(`${C.getUrl()}/patientDiagnosis/delete/${diagnosisId}`, {
          method: 'DELETE',
          headers: getAuthHeaders(),
        });

        if (!response.ok) {
          throw new Error('Failed to delete diagnosis');
        }
        
        swal({
          title: "Success!",
          text: "Diagnosis has been deleted",
          icon: "success",
          buttons: false,
          timer: 2000,
        });
        
        fetchPatientDiagnoses(); // Refresh the list
      } catch (err) {
        let errorMessage = "Failed to delete diagnosis.";
        
        errorMessage = err.message || errorMessage;
        
        swal({
          title: "Error!",
          text: errorMessage,
          icon: "error",
          buttons: false,
          timer: 2000,
        });
      }
    }
  };

// Function to fetch the latest staging data for a patient when adding a new diagnosis
// Function to fetch the latest diagnosis and its staging data
const fetchLatestDiagnosisStaging = async (patientId) => {
  try {
    // First get the patient's diagnoses
    const response = await fetch(`${C.getUrl()}/patientDiagnosis/get/${patientId}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      return;
    }

    const data = await response.json();
    
    // Process the data to get the diagnoses
    let diagnoses = [];
    if (data && data.statusCode === 200 && data.message) {
      try {
        diagnoses = JSON.parse(data.message);
      } catch (e) {
        diagnoses = data.message;
      }
    } else if (Array.isArray(data)) {
      diagnoses = data;
    } else if (data && data.diagnoses && Array.isArray(data.diagnoses)) {
      diagnoses = data.diagnoses;
    } else if (data && typeof data === 'object') {
      diagnoses = [data];
    }

    // Ensure it's an array
    if (!Array.isArray(diagnoses)) {
      diagnoses = [];
    }

    // Sort by date (newest first)
    diagnoses.sort((a, b) => {
      const dateA = a.dateOfDiagnosis ? new Date(a.dateOfDiagnosis) : new Date(0);
      const dateB = b.dateOfDiagnosis ? new Date(b.dateOfDiagnosis) : new Date(0);
      return dateB - dateA;
    });

    if (diagnoses.length > 0) {
      // Get the latest diagnosis
      const latestDiagnosis = diagnoses[0];
      
      if (latestDiagnosis.diagnosisId) {
        // Now fetch the staging data for this diagnosis ID
        const stagingResponse = await fetch(`${C.getUrl()}/patientStaging/getByDiagnosisId?diagnosisId=${latestDiagnosis.diagnosisId}`);
        
        if (!stagingResponse.ok) {
          return;
        }
        
        const stagingResult = await stagingResponse.json();
        
        if (stagingResult.statusCode === 200 && stagingResult.message) {
          const stagings = JSON.parse(stagingResult.message);
          
          // Get active stagings
          const activeStaging = stagings.filter((s) => s.activeInd === "Y");
          
          if (activeStaging.length > 0) {
            // Get the most recent staging record
            const latestStaging = activeStaging[0];
            
            // Map staging data to our form format
            const stagingRecord = {
              stagingId: null, // Don't include the ID as this is a new record
              type: latestStaging.stagingType || "",
              t: latestStaging.tStage || "",
              n: latestStaging.nStage || "",
              m: latestStaging.mStage || "",
              stage: latestStaging.stage || "",
              date: null, // Don't include the date as this is a new record
              isSaved: false,
              isModified: false
            };
            
            // Update the form data with the staging details
            setFormData(prevData => ({
              ...prevData,
              stagingDetails: [stagingRecord]
            }));
            
          } else {
            // Initialize with empty staging detail
            setFormData(prevData => ({
              ...prevData,
              stagingDetails: [{
                type: '',
                t: '',
                n: '',
                m: '',
                stage: '',
                date: null,
                isSaved: false,
                isModified: false
              }]
            }));
          }
        }
      }
    } else {
      // Initialize with empty staging detail
      setFormData(prevData => ({
        ...prevData,
        stagingDetails: [{
          type: '',
          t: '',
          n: '',
          m: '',
          stage: '',
          date: null,
          isSaved: false,
          isModified: false
        }]
      }));
    }
  } catch (error) {
    // Initialize with empty staging detail if there's an error
    setFormData(prevData => ({
      ...prevData,
      stagingDetails: [{
        type: '',
        t: '',
        n: '',
        m: '',
        stage: '',
        date: null,
        isSaved: false,
        isModified: false
      }]
    }));
  }
};

  // Show diagnosis form for adding or editing
//   const showDiagnosisForm = async (diagnosisIndex = null) => {
//     if (diagnosisIndex !== null) {
//       // Edit mode - populate form with existing data
//       const diagnosisToEdit = diagnosisData[diagnosisIndex];
      
//       // If we have a diagnosisId, fetch the full details
//       if (diagnosisToEdit.diagnosisId) {
//         const detailedDiagnosis = await fetchDiagnosisDetail(diagnosisToEdit.diagnosisId);
//         console.log(detailedDiagnosis);
//         if (detailedDiagnosis) {
//           // Ensure metastatic sites are properly formatted for the state
//           const metastaticSites = (detailedDiagnosis.metastaticSites || []).map(site => {
//             // Make sure each site has the correct structure
//             return {
//               siteId: site.siteId || site.id || '',
//               siteName: site.siteName || site
//             };
//           });
          
//           // Set the selected metastatic sites first to ensure they display correctly
//           setSelectedMetastaticSites(metastaticSites);

//           setInitialFormData({
//             status:detailedDiagnosis.status || "",
//             diagnosisType: detailedDiagnosis.diagnosisType || "",
//             pathology: pathology,
//             currentDiagnosis: canSiteIdIAD
//           });
          
//           setFormData({
//             diagnosisId: detailedDiagnosis.diagnosisId,
//             status: detailedDiagnosis.status || "",
//             diagnosisType: detailedDiagnosis.diagnosisType || "",
//             primarySite: detailedDiagnosis.primarySite || "",
//             description: detailedDiagnosis.diagnosisDescription || "",
//             // histology: detailedDiagnosis.histology || "",
//             // histologyGrade: detailedDiagnosis.histologyGrade || ""
//             histology: detailedDiagnosis.histology || detailedDiagnosis.histologyList?.[0]?.histologyDescription || detailedDiagnosis.initialAssessmentData?.patInitiAssesmentDto?.histologyText || "",
//             histologyGrade: detailedDiagnosis.histologyGrade || (detailedDiagnosis.histologyGrades?.length > 0 ? detailedDiagnosis.histologyGrades[0].gradeCode : detailedDiagnosis.initialAssessmentData?.patInitiAssesmentDto?.histologyGradeText) || "",
//             metastasis: detailedDiagnosis.metastasis || "",
//             metastasisDate: detailedDiagnosis.metastasisDate || "",
//             metastasisDetails: detailedDiagnosis.metastasisDetails || "",
//             metastaticSites: metastaticSites,
//             icd10Diagnosis: detailedDiagnosis.icd10Diagnosis || "",
//             riskStratification: detailedDiagnosis.riskStratification || "",
//             lymphNodes: detailedDiagnosis.lymphNodes || [
//               { lnGroup: "", length: "", lengthUnit: "mm", width: "", widthUnit: "mm", depth: "", depthUnit: "mm", nStage: "", description: "" },
//             ],
//             investigationReports: detailedDiagnosis.investigationReports || [],
//             biomarkers: detailedDiagnosis.biomarkers?.length > 0
//               ? detailedDiagnosis.biomarkers
//               : [{ name: "", status: "", value: "" }],
//             tumorSizes: detailedDiagnosis.tumorSizes?.length > 0
//               ? detailedDiagnosis.tumorSizes
//               : [{ length: "", lengthUnit: "mm", 
//                 width: "", widthUnit: "mm", 
//                 depth: "", depthUnit: "mm", 
//                 tStage: "", 
//                 description: "" 
//               },
//             ],
//             lymphNodes: detailedDiagnosis.lymphNodes?.length > 0
//             ? detailedDiagnosis.lymphNodes
//             : [{ lnGroup: "", length: "", lengthUnit: "mm", width: "", widthUnit: "mm", depth: "", depthUnit: "mm", nStage: "", description: "" }],
//             stagingDetails: detailedDiagnosis.stagingDetails?.length > 0
//               ? detailedDiagnosis.stagingDetails
//               : [{ type: "", t: "", n: "", m: "", stage: "", date: null, isSaved: false }],
//             remarks: detailedDiagnosis.remarks || "",
// // For editing (keep existing logic for detailed diagnosis)
// //dateOfDiagnosis: detailedDiagnosis.dateOfDiagnosis ? moment(detailedDiagnosis.dateOfDiagnosis) : moment()
// dateOfDiagnosis: detailedDiagnosis.dateOfDiagnosis ? moment(detailedDiagnosis.dateOfDiagnosis) : moment()
//           });
//         }
//       } else {
//         // Fallback to using the list data if detailed fetch fails
//         // Ensure metastatic sites are properly formatted
//         const metastaticSites = (diagnosisToEdit.metastaticSites || []).map(site => {
//           // Handle both object and string formats
//           return {
//             siteId: site.siteId || '',
//             siteName: site.siteName || site
//           };
//         });
        
//         // Set the selected metastatic sites first
//         setSelectedMetastaticSites(metastaticSites);
        
//         setFormData({
//           diagnosisId: diagnosisToEdit.diagnosisId,
//           status: diagnosisToEdit.status || "",
//           diagnosisType: diagnosisToEdit.diagnosisType || diagnosisToEdit.type || "",
//           primarySite: diagnosisToEdit.primarySite || "",
//           description: diagnosisToEdit.diagnosisDescription || diagnosisToEdit.description || "",
//           // histology: diagnosisToEdit.histology || "",
//           // histologyGrade: diagnosisToEdit.histologyGrade || "",
//           histology: diagnosisToEdit.histology || diagnosisToEdit.histologyList?.[0]?.histologyDescription || diagnosisToEdit.initialAssessmentData?.patInitiAssesmentDto?.histologyText || "",
//           histologyGrade: diagnosisToEdit.histologyGrade || (diagnosisToEdit.histologyGrades?.length > 0 ? diagnosisToEdit.histologyGrades[0].gradeCode : diagnosisToEdit.initialAssessmentData?.patInitiAssesmentDto?.histologyGradeText) || "",
//           metastasis: diagnosisToEdit.metastasis || "",
//           metastasisDate: diagnosisToEdit.metastasisDate || "",
//           metastasisDetails: diagnosisToEdit.metastasisDetails || "",
//           metastaticSites: metastaticSites,
//           icd10Diagnosis: diagnosisToEdit.icd10Diagnosis || "",
//           riskStratification: diagnosisToEdit.riskStratification || "",
//           lymphNodes: diagnosisToEdit.lymphNodes || [
//             { lnGroup: "", length: "", width: "", depth: "", nStage: "", description: "" },
//           ],
//           investigationReports: diagnosisToEdit.investigationReports || [],
//           stagingDetails: diagnosisToEdit.stagingDetails?.length > 0
//             ? diagnosisToEdit.stagingDetails
//             : [{ type: "", t: "", n: "", m: "", stage: "", date: null, isSaved: false }],
//           biomarkers: diagnosisToEdit.biomarkers?.length > 0
//             ? diagnosisToEdit.biomarkers
//             : [{ name: "", status: "", value: "" }],
//           tumorSizes: diagnosisToEdit.tumorSizes?.length > 0
//             ? diagnosisToEdit.tumorSizes
//             : [{ length: "", width: "", depth: "", tStage: "", description: "" }],
//           remarks: diagnosisToEdit.remarks || "",
//           dateOfDiagnosis: moment(), 
//         });
//       }
  
//       setCurrentEditIndex(diagnosisIndex);
//     } else {
//       // Add mode - reset form
//       setFormData({
//         date: "",
//         useEstimatedYear: false,
//         status: "",
//         diagnosisType: "",
//         primarySite: "",
//         description: "",
//         histology: "",
//         histologyGrade: "",
//         metastasis: "",
//         metastasisDate: "",
//         metastasisDetails: "",
//         metastaticSites: [],
//         icd10Diagnosis: "",
//         riskStratification: "",
//         lymphNodes: [
//           { lnGroup: "", length: "", lengthUnit: "mm", width: "", widthUnit: "mm", depth: "", depthUnit: "mm", nStage: "", description: "" },
//         ],
//         investigationReports: [],
//         // stagingDetails: [
//         //   { type: "", t: "", n: "", m: "", stage: "", date: null, isSaved: false }
//         // ],
//         stagingDetails: [], // Initialize with empty array instead of default value
//         biomarkers: [{ name: "", status: "", value: "" }],
//         tumorSizes: [
//           { length: "", lengthUnit: "mm", 
//             width: "", widthUnit: "mm", 
//             depth: "", depthUnit: "mm", 
//             tStage: "", 
//             description: "" 
//           },
//         ],
//         remarks: "",
//         dateOfDiagnosis: null
//       });

//         // Fetch the latest diagnosis ID and then get its staging data
//   const patId = getPatientId();
//   if (patId) {
//     fetchLatestDiagnosisStaging(patId);
//   }

//       setSelectedMetastaticSites([]);
//       setCurrentEditIndex(null);
//     }
  
//     setIsFormVisible(true);
//     // setShowDiagnosisList(false);
//     setErrors({});
//   };

// Pulls the latest DIAGNOSIS extraction for this patient and pre-populates a
// NEW diagnosis form from it. Only ever called from the "add" path — an
// existing record must never be overwritten by an extraction.
//
// One fetch per click, no polling: a diagnosis run can take ~3 minutes and a
// background timer writing into a form the user is already typing in would be
// worse than asking them to press Retry.
const applyExtractionToForm = async () => {
  // Prevents a second in-flight read from a rapid Retry or double-click.
  if (extractionInFlight.current) return;

  const patientId = getPatientId();
  if (!patientId) return;

  extractionInFlight.current = true;
  setExtractionLoading(true);
  setExtractionNotice(null);

  try {
    const res = await getExtractionResult(patientId, ReportType.DIAGNOSIS);

    // The form can be closed or the tab left while a read is in flight.
    if (!isMounted.current) return;

    if (res.outcome === ExtractionOutcome.UNAUTHORIZED) {
      setExtractionNotice({
        tone: 'warning',
        text: 'Your session has expired, so the report could not be read. Please sign in again.',
        canRetry: false,
      });
      return;
    }

    const isSuccess =
      res.outcome === ExtractionOutcome.SUCCESS ||
      res.outcome === ExtractionOutcome.SUCCESS_STALE;

    if (!isSuccess) {
      // A business failure here is the normal "nothing ready yet" case and is
      // NOT an error on screen — the form stays blank and usable.
      setExtractionNotice({
        tone: 'warning',
        text:
          res.outcome === ExtractionOutcome.BUSINESS
            ? res.message
            : 'Auto-fill failed — please enter the diagnosis manually.',
        canRetry: true,
      });
      return;
    }

    const data = res.data || {};
    const masters = {
      statuses: diagnosisStatuses.map((s) => s.statusName),
      diagnosisTypes: diagnosisTypes.map((t) => t.typeName),
      // The raw rows, not just names: a diagnosis links to a site by id.
      primarySites: primarySites,
      histologies: histologies.map((h) => h.histologyName),
      histologyGrades: histologyGrades.map((g) => g.gradeName),
      biomarkerNames: biomarkers.map((b) => b.biomarkerName),
      biomarkerStatuses: biomarkerStatuses.map((s) => s.statusName),
    };

    const { patch, metastaticSiteNames, unmatched, notes, filledCount } =
      mapDiagnosisResultToForm(data.result, masters);

    if (filledCount === 0) {
      setExtractionNotice({
        tone: 'warning',
        text:
          'A report was scanned but nothing could be matched to this form. ' +
          'Please enter the diagnosis manually.',
        notes,
        unmatched,
        canRetry: true,
      });
      return;
    }

    // Metastatic sites are built here rather than looped through
    // handleMetastaticSiteToggle: that handler reads selectedMetastaticSites
    // from its closure, so calling it repeatedly would keep only the last site.
    let sitePatch = {};
    if (metastaticSiteNames.length > 0) {
      const selected = metastaticSiteNames.map((name) => {
        const known = availableMetastaticSites.find(
          (s) => s && typeof s.siteName === 'string' &&
            s.siteName.trim().toLowerCase() === name.toLowerCase()
        );
        // Attach the master id when the extracted name happens to match one.
        // Otherwise send null, NOT 0: site_id is a foreign key, so 0 is
        // rejected just as hard as null but cannot be recognised as "unmapped".
        return {
          siteId: (known && (known.siteId || known.id)) || null,
          siteName: (known && known.siteName) || name,
          patientIniAssessmentId: initialAssessmentId || 0,
          activeInd: 'Y',
        };
      });
      setSelectedMetastaticSites(selected);
      sitePatch = { metastaticSites: selected };
    }

    // Primary sites live in their own state, not in formData, so lift them out
    // of the patch before it is spread — same reason metastatic sites are
    // handled separately above. The deprecated scalar is set alongside so the
    // print view and the parent's narrative text stay in step.
    const { primarySites: extractedPrimarySites, coreDiagnosis: extractedCoreDiagnosis, ...formPatch } = patch;
    if (Array.isArray(extractedPrimarySites) && extractedPrimarySites.length > 0) {
      setSelectedPrimarySites(extractedPrimarySites);
      setPrimarySitesKnown(true);
      formPatch.primarySite = leadSiteName(extractedPrimarySites);
    }

    // Current Diagnosis (core_diagnosis) mapping from extraction.
    // Format: "<description> - <ICD code>" from the cancer-site master list.
    // Try to match against available cancer sites.
    if (extractedCoreDiagnosis && canSitesArr && canSitesArr.length > 0) {
      // Extract the description part (before the dash)
      const descriptionPart = String(extractedCoreDiagnosis).split('-')[0].trim();
      const matched = canSitesArr.find(site =>
        site && site.name &&
        site.name.toLowerCase().includes(descriptionPart.toLowerCase())
      );

      if (matched) {
        const currentDiagnosisArray = [{ id: matched.id, name: matched.name }];
        setCanSiteIdIAD(currentDiagnosisArray);
      }
    }

    // Spread over the reset defaults so anything unmapped keeps its default.
    setFormData((prev) => ({ ...prev, ...formPatch, ...sitePatch }));
    setExtractionResultId(data.extractionResultId || null);

    // §4.1.1 — a SUCCESS can carry an OLDER result because a newer run failed.
    // Showing that silently would be worse than the bug the fallback replaced.
    let text =
      'Pre-filled from ' +
      (data.documentCount > 0 ? data.documentCount : 'the latest') +
      ' scanned report' + (data.documentCount > 1 ? 's' : '') +
      '. Please review before saving.';

    if (res.outcome === ExtractionOutcome.SUCCESS_STALE) {
      const when = data.lastAttemptDate
        ? moment(data.lastAttemptDate).format('DD-MMM-YYYY')
        : 'recently';
      text +=
        ' This is an earlier result — the extraction run on ' + when + ' did not succeed' +
        (data.lastAttemptErrorMessage ? ': ' + data.lastAttemptErrorMessage : '') + '.';
    }

    setExtractionNotice({
      tone: res.outcome === ExtractionOutcome.SUCCESS_STALE ? 'warning' : 'info',
      text,
      notes,
      unmatched,
      canRetry: false,
    });
  } catch (err) {
    if (isMounted.current) {
      setExtractionNotice({
        tone: 'warning',
        text: 'Auto-fill failed — please enter the diagnosis manually.',
        canRetry: true,
      });
    }
  } finally {
    extractionInFlight.current = false;
    if (isMounted.current) setExtractionLoading(false);
  }
};

// Modify the showDiagnosisForm function to reset lymph nodes, tumor sizes, and staging details when adding a new diagnosis

const showDiagnosisForm = async (diagnosisIndex = null) => {
  // Guard rapid double-clicks on "Add New Diagnosis". Editing an existing
  // diagnosis is never blocked by an in-flight extraction.
  if (diagnosisIndex === null && extractionInFlight.current) return;
  if (diagnosisIndex !== null) {
    // Edit mode - populate form with existing data
    const diagnosisToEdit = diagnosisData[diagnosisIndex];
    
    // If we have a diagnosisId, fetch the full details
    if (diagnosisToEdit.diagnosisId) {
      const detailedDiagnosis = await fetchDiagnosisDetail(diagnosisToEdit.diagnosisId);
      if (detailedDiagnosis) {
        // Ensure metastatic sites are properly formatted for the state
        const metastaticSites = (detailedDiagnosis.metastaticSites || []).map(site => {
          // Make sure each site has the correct structure
          return {
            siteId: site.siteId || site.id || '',
            siteName: site.siteName || site
          };
        });
        
        // Set the selected metastatic sites first to ensure they display correctly
        setSelectedMetastaticSites(metastaticSites);

        const primarySiteState = hydratePrimarySites(detailedDiagnosis);
        setSelectedPrimarySites(primarySiteState.sites);
        setPrimarySitesKnown(primarySiteState.known);

        setInitialFormData({
          status: detailedDiagnosis.status || "",
          diagnosisType: detailedDiagnosis.diagnosisType || "",
          pathology: pathology,
          currentDiagnosis: canSiteIdIAD
        });
        
        setFormData({
          diagnosisId: detailedDiagnosis.diagnosisId,
          status: detailedDiagnosis.status || "",
          diagnosisType: detailedDiagnosis.diagnosisType || "",
          // Deprecated mirror of the lead site (spec §5) — read for the print
          // view and the parent's narrative text, never written back.
          primarySite: leadSiteName(primarySiteState.sites),
          description: detailedDiagnosis.diagnosisDescription || "",
          histology: detailedDiagnosis.histology || detailedDiagnosis.histologyList?.[0]?.histologyDescription || detailedDiagnosis.initialAssessmentData?.patInitiAssesmentDto?.histologyText || "",
          histologyGrade: detailedDiagnosis.histologyGrade || (detailedDiagnosis.histologyGrades?.length > 0 ? detailedDiagnosis.histologyGrades[0].gradeCode : detailedDiagnosis.initialAssessmentData?.patInitiAssesmentDto?.histologyGradeText) || "",
          metastasis: detailedDiagnosis.metastasis || "",
          // Held as a moment: the save path calls .format() on it, so a raw
          // string here would throw on save.
          metastasisDate: detailedDiagnosis.metastasisDate
            ? moment(detailedDiagnosis.metastasisDate)
            : "",
          metastasisDetails: detailedDiagnosis.metastasisDetails || "",
          metastaticSites: metastaticSites,
          icd10Diagnosis: detailedDiagnosis.icd10Diagnosis || "",
          riskStratification: detailedDiagnosis.riskStratification || "",
          lymphNodes: detailedDiagnosis.lymphNodes?.length > 0
            ? detailedDiagnosis.lymphNodes
            : [{ lnGroup: "", length: "", lengthUnit: "mm", width: "", widthUnit: "mm", depth: "", depthUnit: "mm", nStage: "", description: "" }],
          investigationReports: detailedDiagnosis.investigationReports || [],
          biomarkers: detailedDiagnosis.biomarkers?.length > 0
            ? detailedDiagnosis.biomarkers
            : [{ name: "", status: "", value: "" }],
          tumorSizes: detailedDiagnosis.tumorSizes?.length > 0
            ? detailedDiagnosis.tumorSizes
            : [{ 
                length: "", lengthUnit: "mm", 
                width: "", widthUnit: "mm", 
                depth: "", depthUnit: "mm", 
                tStage: "", 
                description: "" 
              }],
          stagingDetails: detailedDiagnosis.stagingDetails?.length > 0
            ? detailedDiagnosis.stagingDetails
            : [{ type: "", t: "", n: "", m: "", stage: "", date: null, isSaved: false }],
          remarks: detailedDiagnosis.remarks || "",
          dateOfDiagnosis: detailedDiagnosis.dateOfDiagnosis ? moment(detailedDiagnosis.dateOfDiagnosis) : moment()
        });

        // Carry existing AI provenance through an update rather than wiping it.
        setExtractionResultId(detailedDiagnosis.extractionResultId || null);
      }
    } else {
      // Fallback to using the list data if detailed fetch fails
      // Ensure metastatic sites are properly formatted
      const metastaticSites = (diagnosisToEdit.metastaticSites || []).map(site => {
        // Handle both object and string formats
        return {
          siteId: site.siteId || '',
          siteName: site.siteName || site
        };
      });
      
      // Set the selected metastatic sites first
      setSelectedMetastaticSites(metastaticSites);

      // This branch runs on list data, which may have come from the
      // /diagnoses/patient/{id} fallback endpoint — a shape we do not control,
      // so the scalar seed inside hydratePrimarySites is load-bearing here.
      const primarySiteState = hydratePrimarySites(diagnosisToEdit);
      setSelectedPrimarySites(primarySiteState.sites);
      setPrimarySitesKnown(primarySiteState.known);

      setFormData({
        diagnosisId: diagnosisToEdit.diagnosisId,
        status: diagnosisToEdit.status || "",
        diagnosisType: diagnosisToEdit.diagnosisType || diagnosisToEdit.type || "",
        primarySite: leadSiteName(primarySiteState.sites),
        description: diagnosisToEdit.diagnosisDescription || diagnosisToEdit.description || "",
        histology: diagnosisToEdit.histology || diagnosisToEdit.histologyList?.[0]?.histologyDescription || diagnosisToEdit.initialAssessmentData?.patInitiAssesmentDto?.histologyText || "",
        histologyGrade: diagnosisToEdit.histologyGrade || (diagnosisToEdit.histologyGrades?.length > 0 ? diagnosisToEdit.histologyGrades[0].gradeCode : diagnosisToEdit.initialAssessmentData?.patInitiAssesmentDto?.histologyGradeText) || "",
        metastasis: diagnosisToEdit.metastasis || "",
        metastasisDate: diagnosisToEdit.metastasisDate
          ? moment(diagnosisToEdit.metastasisDate)
          : "",
        metastasisDetails: diagnosisToEdit.metastasisDetails || "",
        metastaticSites: metastaticSites,
        icd10Diagnosis: diagnosisToEdit.icd10Diagnosis || "",
        riskStratification: diagnosisToEdit.riskStratification || "",
        lymphNodes: diagnosisToEdit.lymphNodes || [
          { lnGroup: "", length: "", width: "", depth: "", nStage: "", description: "" },
        ],
        investigationReports: diagnosisToEdit.investigationReports || [],
        stagingDetails: diagnosisToEdit.stagingDetails?.length > 0
          ? diagnosisToEdit.stagingDetails
          : [{ type: "", t: "", n: "", m: "", stage: "", date: null, isSaved: false }],
        biomarkers: diagnosisToEdit.biomarkers?.length > 0
          ? diagnosisToEdit.biomarkers
          : [{ name: "", status: "", value: "" }],
        tumorSizes: diagnosisToEdit.tumorSizes?.length > 0
          ? diagnosisToEdit.tumorSizes
          : [{ length: "", width: "", depth: "", tStage: "", description: "" }],
        remarks: diagnosisToEdit.remarks || "",
        dateOfDiagnosis: moment(),
      });

      // Carry existing AI provenance through an update rather than wiping it.
      setExtractionResultId(diagnosisToEdit.extractionResultId || null);
    }

    setCurrentEditIndex(diagnosisIndex);
  } else {
    // Add mode - reset form with empty values for all fields
    setSelectedPrimarySites([]);
    setPrimarySitesKnown(false);
    setFormData({
      status: "",
      diagnosisType: "",
      primarySite: "",
      description: "",
      histology: "",
      histologyGrade: "",
      metastasis: "",
      metastasisDate: "",
      metastasisDetails: "",
      metastaticSites: [],
      icd10Diagnosis: "",
      riskStratification: "",
      lymphNodes: [
        { lnGroup: "", length: "", lengthUnit: "mm", width: "", widthUnit: "mm", depth: "", depthUnit: "mm", nStage: "", description: "" },
      ],
      investigationReports: [],
      // Initialize with empty staging details - don't fetch or pre-populate from existing data
      stagingDetails: [
        { type: "", t: "", n: "", m: "", stage: "", date: null, isSaved: false }
      ],
      biomarkers: [{ name: "", status: "", value: "" }],
      tumorSizes: [
        { length: "", lengthUnit: "mm", 
          width: "", widthUnit: "mm", 
          depth: "", depthUnit: "mm", 
          tStage: "", 
          description: "" 
        },
      ],
      remarks: "",
      dateOfDiagnosis: moment()
    });

    setSelectedMetastaticSites([]);
    setCurrentEditIndex(null);

    // Do NOT call fetchLatestDiagnosisStaging here - we don't want to pre-populate staging
  }

  setIsFormVisible(true);
  setErrors({});

  if (diagnosisIndex === null) {
    // Add mode only. The form is already visible and usable — pre-population
    // fills in behind it and never blocks the user.
    setExtractionResultId(null);
    setExtractionNotice(null);
    applyExtractionToForm();
  } else {
    // Edit mode: keep whatever provenance the saved record already carries;
    // never re-stamp it from a newer extraction.
    setExtractionNotice(null);
  }
};



  // Hide diagnosis form
  const hideDiagnosisForm = () => {
    setIsFormVisible(false);
    setCurrentEditIndex(null);
    setShowDiagnosisList(true);
    setErrors({});
    setInitialFormData({});
    // Must be cleared here, not just on the next open. `primarySites` is only
    // put on the payload when the selection is non-empty, so a set left over
    // from the diagnosis we just closed would be written onto whichever
    // diagnosis is opened next if that one's own sites failed to load.
    setSelectedPrimarySites([]);
    setPrimarySitesKnown(false);
  };


// Function to parse and format measurements correctly
// const parseMeasurement = (measurementString) => {
//   if (!measurementString) return { value: "", unit: "mm" };
  
//   // If it's already a proper object with value and unit properties
//   if (typeof measurementString === 'object' && measurementString.value !== undefined) {
//     return measurementString;
//   }
  
//   // If it's a string that might contain a unit
//   if (typeof measurementString === 'string') {
//     // Check for common measurement units
//     const unitRegex = /\s*(mm|cm|in)$/i;
//     const match = measurementString.trim().match(unitRegex);
    
//     if (match) {
//       // Extract unit and value
//       const unit = match[1].toLowerCase();
//       const value = measurementString.replace(unitRegex, '').trim();
//       return { value, unit };
//     } else {
//       // No unit found, return just the value
//       return { value: measurementString.trim(), unit: "mm" };
//     }
//   }
  
//   // Default return if measurement is in an unexpected format
//   return { value: "", unit: "mm" };
// };
const parseMeasurement = (measurementString) => {
  if (!measurementString) return { value: "", unit: "mm" };
  
  // Handle string case
  if (typeof measurementString === 'string') {
    // Check if the string already contains a unit
    const unitRegex = /\s*(mm|cm)$/i;
    const match = measurementString.trim().match(unitRegex);
    
    if (match) {
      // String already has a unit, extract the value and unit
      const unit = match[1].toLowerCase();
      const value = measurementString.replace(unitRegex, '').trim();
      return { value, unit };
    } else {
      // No unit found, assume it's just the value
      return { value: measurementString.trim(), unit: "mm" };
    }
  }
  
  // Handle object case (shouldn't happen, but just in case)
  if (typeof measurementString === 'object' && measurementString.value) {
    return measurementString;
  }
  
  // Default fallback
  return { value: "", unit: "mm" };
};

  // Handle form input changes
  // const handleInputChange = (e) => {
  //   const { name, value, type, checked } = e.target;
  //   setFormData((prevData) => ({
  //     ...prevData,
  //     [name]: type === "checkbox" ? checked : value,
  //   }));
  // };
  // Update the handleInputChange function to immediately validate the form after changes
const handleInputChange = (e) => {
  const { name, value, type, checked } = e.target;
  setFormData((prevData) => ({
    ...prevData,
    [name]: type === "checkbox" ? checked : value,
  }));
  
  // Clear the specific error when that field is filled
  if (errors[name]) {
    setErrors(prev => ({
      ...prev,
      [name]: ""
    }));
  }
};

// Voice dictation appends to a free-text field on a new line rather than
// replacing it, so a second dictation keeps the first one and anything typed by
// hand in between. Mirrors handleInputChange, error clearing included, so a
// dictated value dismisses a validation message exactly as typing would.
const appendVoiceText = (fieldName) => (text) => {
  setFormData((prev) => ({
    ...prev,
    [fieldName]: appendText(prev[fieldName], text),
  }));
  setErrors((prev) => (prev[fieldName] ? { ...prev, [fieldName]: "" } : prev));
};

  // Handle fields for moved initial assessment fields
  // const handleInitialAssessmentChange = (e) => {
  //   const { name, value } = e.target;
    
  //   switch(name) {
  //     case 'pathology':
  //       setPathology(value);
  //       setInitialFormData(prev => ({
  //         ...prev,
  //         pathology: pathology
  //       }));
  //       break;
  //     case 'cancerStage':
  //       setCancerStage(value);
  //       break;
  //     case 'boneMetasis':
  //       setBoneMetasis(value);
  //       break;
  //     case 'brainMets':
  //       setBrainMets(value);
  //       break;
  //     case 'doctorObservation':
  //       setDoctorObservation(value);
  //       break;
  //     default:
  //       break;
  //   }
  // };
// Update the handleInitialAssessmentChange function to clear errors on change
const handleInitialAssessmentChange = (e) => {
  const { name, value } = e.target;
  
  switch(name) {
    case 'pathology':
      setPathology(value);
      setInitialFormData(prev => ({
        ...prev,
        pathology: value
      }));
      if (errors.pathology) {
        setErrors(prev => ({
          ...prev,
          pathology: ""
        }));
      }
      break;
    case 'cancerStage':
      setCancerStage(value);
      if (errors.cancerStage) {
        setErrors(prev => ({
          ...prev,
          cancerStage: ""
        }));
      }
      break;
    case 'boneMetasis':
      setBoneMetasis(value);
      break;
    case 'brainMets':
      setBrainMets(value);
      break;
    case 'doctorObservation':
      setDoctorObservation(value);
      break;
    default:
      break;
  }
};
  // Handle metastasis radio button changes
  const handleMetastasisChange = (value) => {
    setFormData((prevData) => ({
      ...prevData,
      metastasis: value,
    }));
    
    // If metastasis is set to No or Don't Know, clear the metastatic sites
    if (value !== "Yes") {
      setSelectedMetastaticSites([]);
      setFormData(prevData => ({
        ...prevData,
        metastaticSites: []
      }));
    }
  };

  const handleMetastaticSiteToggle = (site) => {
    // Extract site information properly
    const siteName = typeof site === 'string' ? site : 
      (typeof site.siteName === 'string' ? site.siteName : 
        (site.siteName && typeof site.siteName === 'object' ? site.siteName.siteName : site.name || ''));
    
    // Check if already selected
    const isAlreadySelected = selectedMetastaticSites.some(s => {
      const selectedName = typeof s === 'string' ? s : 
        (typeof s.siteName === 'string' ? s.siteName : 
          (s.siteName && typeof s.siteName === 'object' ? s.siteName.siteName : s.name || ''));
      return selectedName === siteName;
    });
    
    if (isAlreadySelected) return;

    // Create a consistent site object format with string siteName
    const newSite = {
      siteId: typeof site === 'object' ? 
        (site.siteId || site.id || (site.siteName && typeof site.siteName === 'object' ? site.siteName.siteId : 0) || 0) : 0,
      siteName: siteName,
      patientIniAssessmentId: initialAssessmentId || 0,
      activeInd: 'Y'
    };

    // Update both the UI state and form data
    const updatedSites = [...selectedMetastaticSites, newSite];
    setSelectedMetastaticSites(updatedSites);
    setFormData(prevData => ({
      ...prevData,
      metastaticSites: updatedSites
    }));

    // Clear the input field
    setChooseInput("");

    // Set form to valid to prevent Save button from being disabled
    setIsFormValid(true);
  };

  const removeMetastaticSite = (siteToRemove) => {
    // Handle different data formats
    const updatedSites = selectedMetastaticSites.filter(site => {
      const currentSiteName = typeof site === 'string' ? site : site.siteName;
      const siteToRemoveName = typeof siteToRemove === 'string' ? siteToRemove : siteToRemove.siteName;
      
      return currentSiteName !== siteToRemoveName;
    });
    
    setSelectedMetastaticSites(updatedSites);
    setFormData(prevData => ({
      ...prevData,
      metastaticSites: updatedSites
    }));
    
    // Keep the form valid even after removing sites
    // This ensures the Save button remains enabled
    setIsFormValid(true);
  };

   // Fetch all metastatic sites
   const fetchMetastaticSites = async () => {
    try {
      const response = await fetch(`${C.getUrl()}/patientDiagnosis/metastaticSites`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch metastatic sites');
      }

      const data = await response.json();
      // Assuming the response is an array of metastatic site objects
      setAvailableMetastaticSites(data);
    } catch (err) {
      swal({
        title: "Error!",
        text: "Failed to load metastatic sites",
        icon: "error",
        buttons: false,
        timer: 2000,
      });
    }
  };
  
  // Call this in useEffect when the component mounts
  useEffect(() => {
    fetchMetastaticSites();
  }, []);

  // Is biomarker row filled?
  const isBiomarkerRowFilled = (index) => {
    const biomarker = formData.biomarkers[index];
    return biomarker.name || biomarker.status || biomarker.value;
  };

  // Handle biomarker changes
  const handleBiomarkerChange = (index, field, value) => {
    const updatedBiomarkers = [...formData.biomarkers];
    updatedBiomarkers[index] = {
      ...updatedBiomarkers[index],
      [field]: value,
    };

    // Check if the last row is being filled and add a new row if needed
    if (index === updatedBiomarkers.length - 1 && isBiomarkerRowFilled(index)) {
      updatedBiomarkers.push({ name: "", status: "", value: "" });
    }

    setFormData((prevData) => ({
      ...prevData,
      biomarkers: updatedBiomarkers,
    }));
  };

  // Remove biomarker row
  const removeBiomarkerRow = (index) => {
    const updatedBiomarkers = [...formData.biomarkers];
    updatedBiomarkers.splice(index, 1);

    setFormData((prevData) => ({
      ...prevData,
      biomarkers:
        updatedBiomarkers.length > 0
          ? updatedBiomarkers
          : [{ name: "", status: "", value: "" }],
    }));
  };

  // Is tumor size row filled?
  const isTumorSizeRowFilled = (index) => {
    const tumorSize = formData.tumorSizes[index];
    return tumorSize.length || tumorSize.width || tumorSize.depth || tumorSize.tStage || tumorSize.description;
  };

  // Handle tumor size changes
// Handle tumor size changes
const handleTumorSizeChange = (index, field, value) => {
  const updatedTumorSizes = [...formData.tumorSizes];
  updatedTumorSizes[index] = {
    ...updatedTumorSizes[index],
    [field]: value,
  };

  // Check if the last row is being filled and add a new row if needed
  if (index === updatedTumorSizes.length - 1 && isTumorSizeRowFilled(index)) {
    updatedTumorSizes.push({
      length: "", lengthUnit: "mm", 
      width: "", widthUnit: "mm", 
      depth: "", depthUnit: "mm", 
      tStage: "", 
      description: "" 
    });
  }

  setFormData((prevData) => ({
    ...prevData,
    tumorSizes: updatedTumorSizes,
  }));
};



// Handle tumor size unit selection
const handleTumorUnitSelect = (index, field, selectedUnit) => {
  const updatedTumorSizes = [...formData.tumorSizes];
  updatedTumorSizes[index] = {
    ...updatedTumorSizes[index],
    [`${field}Unit`]: selectedUnit
  };
  
  setFormData((prevData) => ({
    ...prevData,
    tumorSizes: updatedTumorSizes,
  }));
  
  // Close the dropdown
  setActiveDropdown({ type: null, index: null, field: null });
};
  const handleTumorStageSelect = (index, selectedStage) => {
    const updatedTumorSizes = [...formData.tumorSizes];
    updatedTumorSizes[index] = {
      ...updatedTumorSizes[index],
      tStage: selectedStage
    };
    
    setFormData((prevData) => ({
      ...prevData,
      tumorSizes: updatedTumorSizes,
    }));
    
    // Close the dropdown
    setActiveDropdown({ type: null, index: null, field: null });
  };

  // Remove tumor size row
  const removeTumorSizeRow = (index) => {
    const updatedTumorSizes = [...formData.tumorSizes];
    updatedTumorSizes.splice(index, 1);
  
    setFormData((prevData) => ({
      ...prevData,
      tumorSizes:
        updatedTumorSizes.length > 0
          ? updatedTumorSizes
          : [{ length: "", lengthUnit: "mm", width: "", widthUnit: "mm", depth: "", depthUnit: "mm", tStage: "", description: "" }],
    }));
  };

  // Is lymph node row filled?
  const isLymphNodeRowFilled = (index) => {
    const lymphNode = formData.lymphNodes[index];
    return lymphNode.lnGroup || lymphNode.length || lymphNode.width || lymphNode.depth || lymphNode.nStage || lymphNode.description;
  };
  // Handle Lymph Node Changes
  // const handleLymphNodeChange = (index, field, value) => {
  //   const updatedLymphNodes = [...formData.lymphNodes];
  //   updatedLymphNodes[index] = {
  //     ...updatedLymphNodes[index],
  //     [field]: value,
  //   };
  
  //   // Check if the last row is being filled and add a new row if needed
  //   if (index === updatedLymphNodes.length - 1 && isLymphNodeRowFilled(index)) {
  //     updatedLymphNodes.push({
  //       lnGroup: "", 
  //       length: "", lengthUnit: "mm", 
  //       width: "", widthUnit: "mm", 
  //       depth: "", depthUnit: "mm", 
  //       nStage: "", 
  //       description: "" 
  //     });
  //   }
  
  //   setFormData((prevData) => ({
  //     ...prevData,
  //     lymphNodes: updatedLymphNodes,
  //   }));
  // };
// Modified handleLymphNodeChange function
const handleLymphNodeChange = (index, field, value) => {
  const updatedLymphNodes = [...formData.lymphNodes];
  
  if (['length', 'width', 'depth'].includes(field)) {
    // For measurement fields, just update the value part
    // The unit is handled separately via handleLymphUnitSelect
    const numericValue = value.replace(/[^0-9.]/g, '');
    updatedLymphNodes[index] = {
      ...updatedLymphNodes[index],
      [field]: numericValue,
    };
  } else {
    // For other fields, update normally
    updatedLymphNodes[index] = {
      ...updatedLymphNodes[index],
      [field]: value,
    };
  }

  // Check if the last row is being filled and add a new row if needed
  if (index === updatedLymphNodes.length - 1 && isLymphNodeRowFilled(index)) {
    updatedLymphNodes.push({
      lnGroup: "", 
      length: "", lengthUnit: "mm", 
      width: "", widthUnit: "mm", 
      depth: "", depthUnit: "mm", 
      nStage: "", 
      description: "" 
    });
  }

  setFormData((prevData) => ({
    ...prevData,
    lymphNodes: updatedLymphNodes,
  }));
};


  // Handle lymph node unit selection
const handleLymphUnitSelect = (index, field, selectedUnit) => {
  const updatedLymphNodes = [...formData.lymphNodes];
  updatedLymphNodes[index] = {
    ...updatedLymphNodes[index],
    [`${field}Unit`]: selectedUnit
  };
  
  setFormData((prevData) => ({
    ...prevData,
    lymphNodes: updatedLymphNodes,
  }));
  
  // Close the dropdown
  setActiveDropdown({ type: null, index: null, field: null });
};
  
  const handleLymphStageSelect = (index, selectedStage) => {
    const updatedLymphNodes = [...formData.lymphNodes];
    updatedLymphNodes[index] = {
      ...updatedLymphNodes[index],
      nStage: selectedStage
    };
    
    setFormData((prevData) => ({
      ...prevData,
      lymphNodes: updatedLymphNodes,
    }));
    
    // Close the dropdown
    setActiveDropdown({ type: null, index: null, field: null });
  };
  
  // Close dropdown when clicking elsewhere
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setActiveDropdown({ type: null, index: null, field: null });
      }
    };
  
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Add new handler for file upload
  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    
    // Process each file to encode it as Base64
    files.forEach((file, index) => {
      const reader = new FileReader();
      
      reader.onload = (event) => {
        const base64String = event.target.result;
        
        // Update the formData with the encoded file
        setFormData((prev) => {
          const updatedReports = [...prev.investigationReports];
          
          // Add the new file with Base64 content
          updatedReports.push({
            name: file.name,
            fileName: file.name,
            fileContent: base64String,
            fileType: file.type,
            size: file.size
          });
          
          return {
            ...prev,
            investigationReports: updatedReports
          };
        });
      };
      
      // Read the file as a data URL (Base64)
      reader.readAsDataURL(file);
    });
  };

  // Remove lymph node row
  const removeLymphNodeRow = (index) => {
    const lymphNodes = [...formData.lymphNodes];
    lymphNodes.splice(index, 1);
    
    setFormData((prevData) => ({
      ...prevData,
      lymphNodes: lymphNodes.length > 0 
        ? lymphNodes
        : [{ lnGroup: "", length: "", lengthUnit: "mm", width: "", widthUnit: "mm", depth: "", depthUnit: "mm", nStage: "", description: "" }],
    }));
  };

  // Validate form
  // const validateForm = () => {
  //   const newErrors = {};

  //   // Add validation rules as needed
  //   if (!formData.status) newErrors.status = "Status is required";
  //   if (!formData.diagnosisType) newErrors.diagnosisType = "Diagnosis Type is required";
    
  //   // Validate metastatic sites if metastasis is "Yes"
  //   if (formData.metastasis === "Yes" && (!formData.metastaticSites || formData.metastaticSites.length === 0)) {
  //     newErrors.metastaticSites = "Please select at least one metastatic site";
  //   }

  //    // Validate Limb Loss, Mutation, and Previous Cancer History are required
  //    if (!limbLossIdIAD || limbLossIdIAD.length === 0) {
  //     newErrors.limbLoss = "Limb Loss is required";
  //   }
    
  //   if (!mutationIdIA || mutationIdIA.length === 0) {
  //     newErrors.mutation = "Mutation is required";
  //   }
    
  //   if (!prevCanSiteIdIAD || prevCanSiteIdIAD.length === 0) {
  //     newErrors.prevCancerHistory = "Previous Cancer History is required";
  //   }
    
  //   setErrors(newErrors);
  //   return Object.keys(newErrors).length === 0;
  // };
// First, update the form validation function to check all required fields
const validateForm = () => {
  const newErrors = {};

  // Check all required fields
  if (!formData.status) newErrors.status = "Status is required";
  if (!formData.diagnosisType) newErrors.diagnosisType = "Diagnosis Type is required";
  if (!selectedPrimarySites || selectedPrimarySites.length === 0) {
    newErrors.primarySite = "Primary Site is required";
  }
  
  if (!canSiteIdIAD || canSiteIdIAD.length === 0) {
    newErrors.canSiteIdIAD = "Current Diagnosis is required";
  }
  
  if (!pathology || pathology.trim() === "") {
    newErrors.pathology = "Pathology is required";
  }
  
  if (!cancerStage || cancerStage === "") {
    newErrors.cancerStage = "Cancer Stage is required";
  }
  
  if (!limbLossIdIAD || limbLossIdIAD.length === 0) {
    newErrors.limbLossIdIAD = "Limb Loss is required";
  }
  
  if (!mutationIdIA || mutationIdIA.length === 0) {
    newErrors.mutationIdIA = "Mutation is required";
  }
  
  if (!prevCanSiteIdIAD || prevCanSiteIdIAD.length === 0) {
    newErrors.prevCanSiteIdIAD = "Previous Cancer History is required";
  }
  
  // Validate metastatic sites if metastasis is "Yes"
  if (formData.metastasis === "Yes" && (!formData.metastaticSites || formData.metastaticSites.length === 0)) {
    newErrors.metastaticSites = "Please select at least one metastatic site";
  }
    
  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};

// Add this useEffect to check form validity based on all required fields
// useEffect(() => {
//   const isFormFilled = formData.status && 
//                       formData.diagnosisType && 
//                       formData.primarySite && 
//                       canSiteIdIAD.length > 0 && 
//                       pathology && 
//                       cancerStage && 
//                       limbLossIdIAD.length > 0 && 
//                       mutationIdIA.length > 0 && 
//                       prevCanSiteIdIAD.length > 0;
  
//   setIsFormValid(isFormFilled);
// }, [formData.status, formData.diagnosisType, formData.primarySite, canSiteIdIAD, pathology, cancerStage, limbLossIdIAD, mutationIdIA, prevCanSiteIdIAD]);
//   // Prepare data for API
useEffect(() => {
  const isFormFilled = 
    formData.status &&
    formData.diagnosisType &&
    selectedPrimarySites.length > 0 &&
    canSiteIdIAD && canSiteIdIAD.length > 0 &&
    pathology && pathology.trim() !== "" && 
    cancerStage && 
    limbLossIdIAD && limbLossIdIAD.length > 0 && 
    mutationIdIA && mutationIdIA.length > 0 && 
    prevCanSiteIdIAD && prevCanSiteIdIAD.length > 0;
  
  setIsFormValid(isFormFilled);
}, [
  formData.status,
  formData.diagnosisType,
  selectedPrimarySites,
  canSiteIdIAD,
  pathology, 
  cancerStage, 
  limbLossIdIAD, 
  mutationIdIA, 
  prevCanSiteIdIAD
]);

  const prepareDataForAPI = (initialAssessmentId) => {
    // Create histology list
    const histologyList = [];
    if (formData.histology) {
      // Handle both array and single value
      const histologyValue = formData.histology;
      histologyList.push({
        histologyCode: typeof histologyValue === 'object' ? 
          (histologyValue.value || histologyValue.code || histologyValue) : histologyValue,
        histologyDescription: typeof histologyValue === 'object' ? 
          (histologyValue.label || histologyValue.description || histologyValue) : histologyValue,
        patientIniAssessmentId: initialAssessmentId,
        activeInd: 'Y'
      });
    }
  
    // Create histology grade list
    const histologyGrades = [];
    if (formData.histologyGrade) {
      // Handle both array and single value
      const gradeValue = formData.histologyGrade;
      histologyGrades.push({
        gradeCode: typeof gradeValue === 'object' ? 
          (gradeValue.value || gradeValue.code || gradeValue) : gradeValue,
        gradeDescription: `Grade ${typeof gradeValue === 'object' ? 
          (gradeValue.value || gradeValue.code || gradeValue) : gradeValue}`,
        patientIniAssessmentId: initialAssessmentId,
        activeInd: 'Y'
      });
    }
  
    // Create biomarker list
    const biomarkers = [];
    if (formData.biomarkers && formData.biomarkers.length > 0) {
      formData.biomarkers.forEach(row => {
        if (row.name) {
          biomarkers.push({
            name: row.name,
            status: row.status || '',
            value: row.value || '',
            patientIniAssessmentId: initialAssessmentId,
            activeInd: 'Y'
          });
        }
      });
    }
  
    // Create tumor size list - filter out placeholder rows
   // Create tumor size list
const tumorSizes = [];
if (formData.tumorSizes && formData.tumorSizes.length > 0) {
  formData.tumorSizes.forEach(row => {
    // Check if this row has actual data
    if (row.length || row.width || row.depth || row.tStage || row.description) {
      // Clean all measurements to ensure consistent format, preserving the
      // unit the user (or the extraction) actually selected for each dimension.
      const cleanedLength = cleanMeasurement(row.length, row.lengthUnit);
      const cleanedWidth = cleanMeasurement(row.width, row.widthUnit);
      const cleanedDepth = cleanMeasurement(row.depth, row.depthUnit);

      tumorSizes.push({
        length: cleanedLength,
        width: cleanedWidth,
        depth: cleanedDepth,
        tStage: row.tStage || '',
        description: row.description || '',
        patientIniAssessmentId: initialAssessmentId,
        activeInd: 'Y'
      });
    }
  });
}
  
    // Create lymph node list - filter out placeholder rows
    // const lymphNodes = [];
    // if (formData.lymphNodes && formData.lymphNodes.length > 0) {
    //   formData.lymphNodes.forEach(row => {
    //     // Check if this row has actual data (not just placeholders)
    //     if (
    //       row.lnGroup || 
    //       (row.length && !row.length.includes('_mm')) || 
    //       (row.width && !row.width.includes('_mm')) || 
    //       (row.depth && !row.depth.includes('_mm')) ||
    //       row.nStage || 
    //       row.description
    //     ) {
    //       lymphNodes.push({
    //         lnGroup: row.lnGroup || '',
    //         length: (row.length && !row.length.includes('_mm')) ? `${row.length}  ${row.lengthUnit || 'mm'}` : '',
    //         width: (row.width && !row.width.includes('_mm')) ? `${row.width}  ${row.widthUnit || 'mm'}` : '',
    //         depth: (row.depth && !row.depth.includes('_mm')) ? `${row.depth}  ${row.depthUnit || 'mm'}` : '',
    //         nStage: row.nStage || '',
    //         description: row.description || '',
    //         patientIniAssessmentId: initialAssessmentId,
    //         activeInd: 'Y'
    //       });
    //     }
    //   });
    // }
     // Create lymph node list with proper formatting
  // Create lymph node list
const lymphNodes = [];
if (formData.lymphNodes && formData.lymphNodes.length > 0) {
  formData.lymphNodes.forEach(row => {
    // Check if this row has actual data
    if (row.lnGroup || row.length || row.width || row.depth || row.nStage || row.description) {
      // Clean all measurements to ensure consistent format, preserving the
      // unit the user (or the extraction) actually selected for each dimension.
      const cleanedLength = cleanMeasurement(row.length, row.lengthUnit);
      const cleanedWidth = cleanMeasurement(row.width, row.widthUnit);
      const cleanedDepth = cleanMeasurement(row.depth, row.depthUnit);

      lymphNodes.push({
        lnGroup: row.lnGroup || '',
        length: cleanedLength,
        width: cleanedWidth,
        depth: cleanedDepth,
        nStage: row.nStage || '',
        description: row.description || '',
        patientIniAssessmentId: initialAssessmentId,
        activeInd: 'Y'
      });
    }
  });
}
  
    // Create investigation reports list
    const investigationReports = [];
    if (formData.investigationReports && formData.investigationReports.length > 0) {
      formData.investigationReports.forEach(file => {
        if (file.name || file.fileName) {
          investigationReports.push({
            fileName: file.name || file.fileName,
            fileContent: file.fileContent || '',
            patientIniAssessmentId: initialAssessmentId,
            activeInd: 'Y'
          });
        }
      });
    }
  
    // Create metastatic sites list - IMPORTANT FIX
    const metastaticSites = [];
    if (selectedMetastaticSites && selectedMetastaticSites.length > 0) {
      selectedMetastaticSites.forEach(site => {
        // Extract the actual site information properly
        // null (not 0) when the site has no master entry — site_id is a foreign
        // key, and null is the only value that reads as "free text, unmapped".
        const siteId = typeof site === 'object' ?
          (site.siteId || site.id || (site.siteName && typeof site.siteName === 'object' ? site.siteName.siteId : null) || null) : null;
        
        const siteName = typeof site === 'string' ? site : 
          (typeof site.siteName === 'string' ? site.siteName : 
            (site.siteName && typeof site.siteName === 'object' ? site.siteName.siteName : ''));
        
        metastaticSites.push({
          siteId: siteId,
          siteName: siteName,
          patientIniAssessmentId: initialAssessmentId,
          activeInd: 'Y'
        });
      });
    }

    // Create staging details list - filter out placeholder rows
    const stagingDetails = [];
    if (formData.stagingDetails && formData.stagingDetails.length > 0) {
      formData.stagingDetails.forEach(row => {
        // Check if this row has actual data (not just placeholders)
        if (row.type || row.t || row.n || row.m || row.stage) {
          stagingDetails.push({
            stagingId: row.stagingId || 0,
            type: row.type || '',
            t: row.t || '',
            n: row.n || '',
            m: row.m || '',
            stage: row.stage || '',
            date: row.date || null,
            patientIniAssessmentId: initialAssessmentId,
            activeInd: 'Y'
          });
        }
      });
    }
  
    // Ensure status is a valid value from the status dropdown
    let validStatus = formData.status;
    if (!formData.status || formData.status.trim() === '') {
      // Default to a valid status if none is selected
      validStatus = 'Newly Diagnosed';
    }
  
    // Ensure metastasis is one of the allowed values in the enum
    let validMetastasis = formData.metastasis;
    if (!['Yes', 'No', "Don't Know"].includes(formData.metastasis)) {
      validMetastasis = "Don't Know";
    }
    let validRiskStratification = formData.riskStratification;
    if (validRiskStratification) {
      // Extract just the first word if it contains "Risk"
      if (validRiskStratification.includes("Risk")) {
        validRiskStratification = validRiskStratification.split(" ")[0];
      }
    }
    // Create the main diagnosis object
    const diagnosisDTO = {
      patientId: getPatientId(),
      patientIniAssessmentId: initialAssessmentId,
      dateOfDiagnosis: formData.dateOfDiagnosis ? formData.dateOfDiagnosis.format('YYYY-MM-DD') : new Date().toISOString().split('T')[0],
      status: validStatus,
      // Deprecated and ignored on input once primarySites is sent (spec §5).
      // Kept because it is still the promote path for a diagnosis that has no
      // links yet, on the chance the array is ever omitted.
      primarySite: formData.primarySite,
      diagnosisType: formData.diagnosisType,
      diagnosisDescription: formData.description || '',
      riskStratification: validRiskStratification || null,
      metastasis: validMetastasis,
      // Defensive: this field has no rendered input, so it is only ever set
      // programmatically — accept a moment or a plain YYYY-MM-DD string.
      metastasisDate: formData.metastasisDate
        ? (moment.isMoment(formData.metastasisDate)
            ? formData.metastasisDate.format('YYYY-MM-DD')
            : moment(formData.metastasisDate).format('YYYY-MM-DD'))
        : null,
      metastasisDetails: formData.metastasisDetails || '',
      remarks: formData.remarks || '',
      createdById: doctorId || getCurrentUserId(),
      updatedById: doctorId || getCurrentUserId(),
      activeInd: 'Y',
      histologyList,
      histologyGrades,
      biomarkers,
      tumorSizes,
      lymphNodes,
      investigationReports,
      metastaticSites,
      stagingDetails
    };
    
    // Records which extraction run this diagnosis was populated from, so an
    // AI-derived record can be told apart from a hand-typed one after the fact.
    // Null for manual entry. Needs patient_diagnosis_extraction_link.sql — if
    // that migration has not been run the column is absent and the value is
    // dropped silently.
    diagnosisDTO.extractionResultId = extractionResultId || null;

    // Attached here, NOT in the object literal above. Spec §3: this key is
    // three-valued — omitted leaves the existing links alone, [] DELETES EVERY
    // LINK on the diagnosis, and a populated array replaces the set. Every
    // other child collection above is spread in unconditionally and is [] when
    // the user filled nothing in, which for this field is the payload that
    // silently wipes the record and still returns 200.
    //
    // Two guards, and both matter:
    //   - primarySitesKnown: sending a replacement set requires knowing the
    //     whole set. When the collection never arrived we hold only a seed from
    //     the deprecated scalar, which mirrors the LEAD site alone — sending
    //     that would drop any second primary the record has. Omitting the key
    //     leaves the links exactly as they are, which is the point of the
    //     omit case.
    //   - attachDiagnosisPrimarySites: never writes the key for an empty
    //     payload, so the destructive [] cannot be produced from here.
    if (primarySitesKnown) {
      attachDiagnosisPrimarySites(diagnosisDTO, selectedPrimarySites);
    }

    // Add diagnosisId if we're editing an existing diagnosis
    if (formData.diagnosisId) {
      diagnosisDTO.diagnosisId = formData.diagnosisId;
    }

    // TEMPORARY bisect hook — see __diagSuppress above. No effect unless a
    // tester has explicitly suppressed a field from the console.
    //
    // Do NOT add a `primarySites` branch here by following the pattern: every
    // line below sets a collection to [], and for primarySites that is the
    // delete-every-link payload (spec §3). Suppressing that field means
    // `delete diagnosisDTO.primarySites`.
    if (diagSuppressed.size > 0) {
      if (diagSuppressed.has('extractionResultId')) diagnosisDTO.extractionResultId = null;
      if (diagSuppressed.has('metastasisDate')) diagnosisDTO.metastasisDate = null;
      if (diagSuppressed.has('metastaticSites')) diagnosisDTO.metastaticSites = [];
      if (diagSuppressed.has('biomarkers')) diagnosisDTO.biomarkers = [];
      if (diagSuppressed.has('tumorSizes')) diagnosisDTO.tumorSizes = [];
      if (diagSuppressed.has('lymphNodes')) diagnosisDTO.lymphNodes = [];
      if (diagSuppressed.has('stagingDetails')) diagnosisDTO.stagingDetails = [];
      recordDiagDebug('bisect: suppressed', Array.from(diagSuppressed));
    }

    return diagnosisDTO;
  };

 // Toggle dropdown visibility
const handleDropdownToggle = (type, index, field) => {
  if (activeDropdown.type === type && activeDropdown.index === index && activeDropdown.field === field) {
    // Close dropdown if clicking the same field
    setActiveDropdown({ type: null, index: null, field: null });
  } else {
    // Open this dropdown, close others
    setActiveDropdown({ type, index, field });
  }
};  

  // New methods for the moved fields
  // const handleCanSiteSelect = (selectedList) => {
  //   setCanSiteIdIAD(selectedList);
  //   setInitialFormData(prev => ({
  //     ...prev,
  //     currentDiagnosis: selectedList
  //   }));
  // };
// A diagnosis carries a LIST of primary sites (spec §2) — a patient can have
// synchronous primaries. `formData.primarySite` is kept in step as a local
// mirror of the lead entry purely so the print view and the parent's narrative
// text keep working; it is deprecated and must not be written back (spec §5).
const handlePrimarySitesChange = (sites) => {
  setSelectedPrimarySites(sites);
  // The user has now stated the full set, whatever we managed to load.
  setPrimarySitesKnown(true);
  setFormData(prev => ({ ...prev, primarySite: leadSiteName(sites) }));

  if (errors.primarySite && sites.length > 0) {
    setErrors(prev => ({ ...prev, primarySite: "" }));
  }
};

// Works out the selection to show when an existing diagnosis is opened.
//
// Returns { sites, known }. `known` is false when the server never sent the
// collection at all — not the same as sending an empty one — in which case the
// sites below are a best-effort seed from the deprecated scalar and must not be
// sent back as a replacement set.
const hydratePrimarySites = (source) => {
  const loaded = readDiagnosisPrimarySites(source);
  let sites = loaded || [];

  // Either a pre-migration record, or the fallback list endpoint
  // (/diagnoses/patient/{id}) whose shape we do not control. The deprecated
  // scalar is all we have, so seed from it rather than showing a required
  // field as blank. Resolving the name to an id is best-effort: if the option
  // list has not arrived yet the backend resolves it by name anyway (spec §4).
  if (sites.length === 0 && source && source.primarySite) {
    const wanted = String(source.primarySite).trim().toLowerCase();
    const match = primarySites.find(
      (option) => String(option.siteName || '').trim().toLowerCase() === wanted
    );
    sites = [{
      siteId: match ? match.siteId : null,
      siteName: match ? match.siteName : source.primarySite,
      leadSite: true,
    }];
  }

  return { sites, known: loaded !== null };
};

// Update the selection handlers to clear errors
const handleCanSiteSelect = (selectedList) => {
  setCanSiteIdIAD(selectedList || []);
  setInitialFormData(prev => ({
    ...prev,
    currentDiagnosis: selectedList
  }));
  
  if (errors.canSiteIdIAD && selectedList.length > 0) {
    setErrors(prev => ({
      ...prev,
      canSiteIdIAD: ""
    }));
  }
};

  // const handlePrevCanSiteSelect = (selectedList) => {
  //   setPrevCanSiteIdIAD(selectedList);
  // };
  const handlePrevCanSiteSelect = (selectedList) => {
    setPrevCanSiteIdIAD(selectedList);
    
    if (errors.prevCanSiteIdIAD && selectedList.length > 0) {
      setErrors(prev => ({
        ...prev,
        prevCanSiteIdIAD: ""
      }));
    }
  };
  // const handleLimbLossSelect = (selectedList) => {
  //   setLimbLossIdIAD(selectedList);
  // };
  const handleLimbLossSelect = (selectedList) => {
    setLimbLossIdIAD(selectedList);
    
    if (errors.limbLossIdIAD && selectedList.length > 0) {
      setErrors(prev => ({
        ...prev,
        limbLossIdIAD: ""
      }));
    }
  };

  // const handleMutationSelect = (selectedList) => {
  //   setMutationIdIA(selectedList);
  // };
  const handleMutationSelect = (selectedList) => {
    setMutationIdIA(selectedList);
    
    if (errors.mutationIdIA && selectedList.length > 0) {
      setErrors(prev => ({
        ...prev,
        mutationIdIA: ""
      }));
    }
  };

  // Function to get the current user ID from localStorage
  const getCurrentUserId = () => {
    try {
      return localStorage.getItem('userId') || 1;
    } catch (error) {
      return 1; // Default to 1 if there's an error
    }
  };

  // Function to validate initial assessment fields
  const validateInitialAssesment = () => {
    let formIsValid = true;
    let errors = {};
    
    if (canSiteIdIAD === null || canSiteIdIAD.length === 0) {
      errors["canSiteIdIAD"] = "Please select Current Diagnosis";
      formIsValid = false;
    }
    if (mutationIdIA === null || mutationIdIA.length === 0) {
      errors["mutationIdIA"] = "Please select Mutation";
      formIsValid = false;
    }
    // if (doctorObservation === null || doctorObservation === "") {
    //   errors["doctorObservation"] = "Please enter Doctor Observation";
    //   formIsValid = false;
    // }
    if (pathology === null || pathology === "") {
      errors["pathology"] = "Please enter Pathology";
      formIsValid = false;
    }
    if (cancerStage === null || cancerStage === "") {
      errors["cancerStage"] = "Please select Cancer Stage";
      formIsValid = false;
    }
    if (limbLossIdIAD === null || limbLossIdIAD.length === 0) {
      errors["limbLossIdIAD"] = "Please select Limb Loss";
      formIsValid = false;
    }
    if (prevCanSiteIdIAD === null || prevCanSiteIdIAD.length === 0) {
      errors["prevCanSiteIdIAD"] = "Please select Previous Cancer History";
      formIsValid = false;
    }
    
    setInitialAssesmentErrors(errors);
    return formIsValid;
  };

  // Function to save initial assessment and then diagnosis data
 // Function to save initial assessment and then diagnosis data
// In the DiagnosisManagement.jsx file, replace the existing saveInitialAssessment 
// function with this updated version:

const saveInitialAssessment = async () => {
  // Check if initial assessment validation passes
  const validInitialAssessment = validateInitialAssesment();
  
  // Only show validation error for diagnosis if it's visible
  if (isFormVisible && !validateForm()) {
    swal({
      title: "Validation Error!",
      text: "Please fill all required diagnosis fields correctly.",
      icon: "error",
      buttons: false,
      timer: 3000,
    });
    setInitialFormData(formData); // Update initialFormData with the latest
    
    setSaveallLoader(false);
    return;
  }

  setSaveLoader(true);

  try {
    // Prepare default initial assessment payload
    let initialAssessmentData = {
      status: 'I',
      patInitiAssesmentDto: {
        creationDate: new Date(),
        doctorObservation: doctorObservation || "",
        pathologyText: pathology || "TBD",
        pathology: pathology || "TBD", 
        patientDTO: {
          patientID: getPatientId()
        },
        createdByIdUser: {
          userId: doctorId || getCurrentUserId()
        },
        stageMasterDTO: {
          stageId: cancerStage || "6"
        },
        patientCancerSiteDTOList: [],
        patientPrevCancerSiteDTOList: [],
        patientLimbLossDTOList: [],
        patientMutationXrefList: [],
        boneMetasis: boneMetasis || "N",
        brainMets: brainMets || "N"
      }
    };

    // Process cancer site data if available and valid
    if (validInitialAssessment && canSiteIdIAD !== null && canSiteIdIAD.length !== 0) {
      canSiteIdIAD.forEach(function (obj) {
        let data = {
          cancerSiteMstDto: {
            cancerSiteId: obj.id
          }
        };
        initialAssessmentData.patInitiAssesmentDto.patientCancerSiteDTOList.push(data);
      });
    } else {
      // Default cancer site when validation fails or data is empty
      initialAssessmentData.patInitiAssesmentDto.patientCancerSiteDTOList = [
        { cancerSiteMstDto: { cancerSiteId: 3325 } },
        { cancerSiteMstDto: { cancerSiteId: 3325 } }
      ];
    }
    
    // Process previous cancer site data if available and valid
    if (validInitialAssessment && prevCanSiteIdIAD !== null && prevCanSiteIdIAD.length !== 0) {
      prevCanSiteIdIAD.forEach(function (obj) {
        let dataObject = {
          cancerSiteMstDTO: {
            cancerSiteId: obj.id
          }
        };
        initialAssessmentData.patInitiAssesmentDto.patientPrevCancerSiteDTOList.push(dataObject);
      });
    } else {
      // Default previous cancer site when validation fails or data is empty
      initialAssessmentData.patInitiAssesmentDto.patientPrevCancerSiteDTOList = [
        { cancerSiteMstDTO: { cancerSiteId: 3325 } }
      ];
    }
    
    // Process limb loss data if available and valid
    if (validInitialAssessment && limbLossIdIAD !== null && limbLossIdIAD.length !== 0) {
      limbLossIdIAD.forEach(function (obj) {
        let dataObject = {
          limbLossMasterDTO: {
            limbLossId: obj.id
          }
        };
        initialAssessmentData.patInitiAssesmentDto.patientLimbLossDTOList.push(dataObject);
      });
    } else {
      // Default limb loss when validation fails or data is empty
      initialAssessmentData.patInitiAssesmentDto.patientLimbLossDTOList = [
        { limbLossMasterDTO: { limbLossId: 16 } }
      ];
    }
    
    // Process mutation data if available and valid
    if (validInitialAssessment && mutationIdIA !== null && mutationIdIA.length !== 0) {
      mutationIdIA.forEach(function (obj) {
        let data = {
          mutationMstDTO: {
            mutationId: obj.id
          }
        };
        initialAssessmentData.patInitiAssesmentDto.patientMutationXrefList.push(data);
      });
    } else {
      // Default mutation when validation fails or data is empty
      initialAssessmentData.patInitiAssesmentDto.patientMutationXrefList = [
        { mutationMstDTO: { mutationId: 415 } }
      ];
    }

    
    // Save initial assessment data
    const response = await fetch(`${C.getUrl()}/userCase/saveUserCase`, {
      method: 'POST',
      mode: 'cors',
      body: JSON.stringify(initialAssessmentData),
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json'
      }
    });

    const responseData = await response.json();
    
    // Check if component is still mounted before continuing
    if (!isMounted.current) return;
    
    const result = classifySaveUserCaseResponse(responseData);
    if (result.outcome === SaveOutcome.BLOCKED) {
      swal({
        title: "Cannot Save",
        text: result.message,
        icon: "error",
        buttons: false,
        timer: 3000,
      });
      return;
    }
    if (result.outcome === SaveOutcome.ERROR) {
      throw new Error(result.message);
    }
    if (result.warnings) {
      swal({
        title: "Saved with Warnings",
        text: result.warnings.join("\n"),
        icon: "warning",
        buttons: false,
        timer: 3000,
      });
    }

    const objmsg = result.saved;
    
    if (objmsg != null && objmsg.patientIniAssessmentId != 0) {
      // Check if component is still mounted before updating state
      if (!isMounted.current) return;
      
      // Set the initial assessment ID in state
      setInitialAssessmentId(objmsg.patientIniAssessmentId);
      
      let diagnosisResult = { success: true };
      // let opVisitResult = { success: true };
      
      // Now save diagnosis if form is visible
      if (isFormVisible) {
        
        // Call the saveDiagnosis method
        diagnosisResult = await saveDiagnosis(objmsg.patientIniAssessmentId);
        
        // Check if component is still mounted before continuing
        if (!isMounted.current) return;
        
      }
      
      // Now save OP Visit data if the ref exists and there's data to save
      // if (opVisitRef.current) {
      //   console.log("Checking if OP Visit has data to save...");
        
      //   // First check if the OP Visit form has any user data
      //   const hasOpVisitData = opVisitRef.current.hasUserEnteredData();
        
      //   if (hasOpVisitData) {
      //     console.log("OP Visit has user-entered data, attempting to save with ID:", objmsg.patientIniAssessmentId);
          
      //     try {
      //       // Call the saveOPVisit method from the child component
      //       opVisitResult = await opVisitRef.current.saveOPVisit(objmsg.patientIniAssessmentId);
            
      //       console.log("OP Visit save result:", opVisitResult);
            
      //       // After saving, explicitly refresh the data and hide the form
      //       if (opVisitResult.success) {
      //         // Add a small delay to ensure the server has processed the save before fetching
      //         setTimeout(async () => {
      //           // First hide the form to ensure the state is reset
      //           opVisitRef.current.hideForm();
                
      //           // Then explicitly refresh the data to show the latest records
      //           await opVisitRef.current.refreshData();
                
      //           console.log("OP Visit data refreshed after saving");
      //         }, 1000); // 1 second delay to ensure server sync
      //       }
      //     } catch (opVisitError) {
      //       console.error("Error saving OP Visit:", opVisitError);
      //       opVisitResult = { 
      //         success: false, 
      //         error: opVisitError.message || "Unknown error saving OP Visit" 
      //       };
      //     }
      //   } else {
      //     console.log("No user-entered data in OP Visit, skipping save operation");
      //     // Don't attempt to save OP Visit if there's no data
      //     opVisitResult = { success: true, message: "No OP Visit data to save" };
      //   }
      // }
      
      // Show appropriate message based on results
      if (!diagnosisResult.success 
        // || !opVisitResult.success
      ) {
        let errorMessage = "";
        if (!diagnosisResult.success) {
          errorMessage += "Error saving diagnosis: " + (diagnosisResult.error || "Unknown error");
        }
        // if (!opVisitResult.success) {
        //   errorMessage += (errorMessage ? "\n" : "") + 
        //                  "Error saving OP Visit: " + (opVisitResult.error || "Unknown error");
        // }
        
        recordDiagDebug('save partially failed', errorMessage);
        swal({
          title: "Warning!",
          text: "Initial assessment was saved but there was an issue with other data: " + errorMessage,
          icon: "warning",
          buttons: false,
          timer: 3000,
        });
      } else {
        swal({
          title: "Success!",
          text: "All data saved successfully!",
          icon: "success",
          buttons: false,
          timer: 2000,
        });
      }
      
      // Check if component is still mounted before fetching updated data
      if (isMounted.current) {
        // Fetch the latest initial assessment details to update the UI
        await getInitialAssessmentDetails(getPatientId());

        // Refresh diagnoses list
        await fetchPatientDiagnoses();

        // Page refresh after a SUCCESSFUL save only. Reloading after a failure
        // threw away both the error on screen and the console output needed to
        // diagnose it, while leaving the user to guess what had been saved.
        if (diagnosisResult.success) {
          window.location.reload();
        }
      }
    }
  } catch (err) {
    
    // Check if component is still mounted before showing error notification
    if (isMounted.current) {
      swal({
        title: "Error!",
        text: "Failed to save data: " + (err.message || "Unknown error"),
        icon: "error",
        buttons: false,
        timer: 3000,
      });
    }
  } finally {
    // Check if component is still mounted before updating state
    if (isMounted.current) {
      setSaveLoader(false);
    }
  }
};

  // New function to fetch initial assessment by ID
  const fetchInitialAssessmentById = async (assessmentId) => {
    try {
      
      // Try the first endpoint
      let response = await fetch(`${C.getUrl()}/patientInitialAssessment/get/${assessmentId}`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      // If first endpoint fails, try the second endpoint
      if (!response.ok) {
        response = await fetch(`${C.getUrl()}/userCase/getInitialAssessmentById?patientIniAssessmentId=${assessmentId}`, {
          method: 'GET',
          headers: getAuthHeaders(),
        });
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch initial assessment. Status: ${response.status}`);
      }

      const data = await response.json();
      
      let initialAssessment = null;
      if (data && data.statusCode === 200 && data.message) {
        try {
          initialAssessment = JSON.parse(data.message);
        } catch (e) {
          initialAssessment = data.message;
        }
      } else {
        initialAssessment = data;
      }
      
      return initialAssessment;
    } catch (err) {
      return null;
    }
  };

  // Function to fetch initial assessment details by patient ID
  const fetchInitialAssessmentDetails = async (patientId) => {
    const getDefaultAssessmentData = () => ({
      patInitiAssesmentDto: {
        creationDate: new Date().toISOString(),
        startTime: new Date().toISOString(),
        endTime: new Date().toISOString(),
        doctorObservation: '',
        pathologyText: 'TBD',
        pathology: 'TBD',
        patientDTO: { patientID: patientId },
        createdByIdUser: { userId: getCurrentUserId?.() || '' },
        stageMasterDTO: { stageId: '6' },
        patientCancerSiteDTOList: [],
        patientPrevCancerSiteDTOList: [],
        patientLimbLossDTOList: [],
        patientMutationXrefList: [],
        boneMetasis: 'N',
        brainMets: 'N',
        histologyText: '',
        histologyGradeText: ''
      }
    });

    try {
      const response = await fetch(`${C.getUrl()}/userCase/getInitialAssessmentDetails?patientId=${patientId}`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        if (response.status === 400) {
          console.warn(`[diagnosis] invalid patient ID: ${patientId}, using defaults`);
        } else {
          console.warn(`[diagnosis] failed to fetch initial assessment (status ${response.status}), using defaults`);
        }
        return getDefaultAssessmentData();
      }

      const apiResponse = await response.json();

      // New response format: data field contains the assessment object
      if (apiResponse && apiResponse.data && apiResponse.data.patInitiAssesmentDto) {
        return apiResponse.data;
      }

      // Fallback for backward compatibility with old response format
      if (apiResponse && apiResponse.message) {
        try {
          const parsed = JSON.parse(apiResponse.message);
          if (parsed && parsed.patInitiAssesmentDto) {
            return parsed;
          }
        } catch (e) {
          // Not JSON, treat as old format
          if (apiResponse.message && apiResponse.message.patInitiAssesmentDto) {
            return apiResponse.message;
          }
        }
      }

      console.warn('[diagnosis] unexpected response format, using defaults:', apiResponse);
      return getDefaultAssessmentData();
    } catch (err) {
      console.warn('[diagnosis] exception fetching initial assessment, using defaults:', err.message);
      return getDefaultAssessmentData();
    }
  };

  // Function to view Base64 encoded PDF
  const viewPdfFile = (base64Content, fileName) => {
    try {
      // Check if the content is already in base64 format
      let pdfContent = base64Content;
      
      // If the content doesn't start with the data URL prefix, add it
      if (!pdfContent.startsWith('data:application/pdf;base64,')) {
        // If it's just a base64 string without the data URL prefix
        if (!pdfContent.startsWith('data:')) {
          pdfContent = 'data:application/pdf;base64,' + pdfContent;
        }
      }
      
      // Create a new window to display the PDF
      const pdfWindow = window.open('');
      pdfWindow.document.write(
        `<html>
          <head>
            <title>${fileName}</title>
            <style>
              body, html {
                margin: 0;
                padding: 0;
                height: 100%;
                overflow: hidden;
              }
              embed {
                width: 100%;
                height: 100%;
              }
            </style>
          </head>
          <body>
            <embed width="100%" height="100%" src="${pdfContent}" type="application/pdf" />
          </body>
        </html>`
      );
    } catch (error) {
      swal({
        title: "Error",
        text: "Failed to open the PDF file. Please try again.",
        icon: "error",
        buttons: false,
        timer: 2000
      });
    }
  };

  // Render the component
  return (
    <>
    {/* <strong>OP Visit</strong> <br/><br/>
    <OPVisitManagement ref={opVisitRef} assessmentId={initialAssessmentId} /> */}
    <div className="diagnosis-container">

      {/* <strong> Diagnosis</strong><br/><br/> */}
      {/* Add button */}
      <div className="mb-3 dx-actions d-flex justify-content-between align-items-center">

        <Button
          className="btn btn-secondary"
          onClick={() => showDiagnosisForm()}
        >
          Add New Diagnosis
        </Button>

        {/* Cancel (only while the form is open) and Save, right aligned */}
        <div className="d-flex align-items-center">
          {isFormVisible && (
            <Button
              variant="danger"
              className="mr-2"
              onClick={hideDiagnosisForm}
            >
              Cancel
            </Button>
          )}

          <Button
            type="button"
            className="btn btn-secondary"
            onClick={saveInitialAssessment}
            disabled={saveLoader} // Only disable during saving, not based on validation
          >
            {saveLoader && (
              <Spinner className='mr-2' animation='border' size='1.5rem' />
            )}
            {!saveLoader && (
              <FaSave className='mr-2' size='1.5rem' style={{ verticalAlign: "sub" }} />
            )}
            Save
          </Button>
        </div>
      </div>

 
      {/* Loading state */}
      {loading && (
        <div className="text-center my-4">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p className="mt-2">Loading diagnosis data...</p>
        </div>
      )}

      {/* Error state. Hidden while the add/edit form is open so the empty-list
          message ("No diagnosis data found...") does not sit above the form the
          user just opened to fix it. Cancelling brings it back, since the error
          state itself is left untouched. */}
      {error && !isFormVisible && (
        <Alert variant="danger" className="my-3">
          {error}
        </Alert>
      )}

      {/* Diagnosis form */}
      {isFormVisible && (
        <div className="mb-4 border p-3 rounded dx-form-panel">
          {/* AI pre-population status. Never blocks the form: the fields below
              stay editable whether or not anything was filled in. */}
          {extractionLoading && (
            <Alert variant="light" className="mb-3 d-flex align-items-center">
              <Spinner animation="border" size="sm" className="mr-2" />
              Reading the latest scanned report&hellip;
            </Alert>
          )}

          {!extractionLoading && extractionNotice && (
            <Alert
              variant={extractionNotice.tone}
              className="mb-3"
              dismissible
              onClose={() => setExtractionNotice(null)}
            >
              <div>{extractionNotice.text}</div>

              {extractionNotice.notes && extractionNotice.notes.map((note, i) => (
                <div key={`note-${i}`} className="mt-2 small">{note}</div>
              ))}

              {extractionNotice.unmatched && extractionNotice.unmatched.length > 0 && (
                <div className="mt-2 small">
                  <div>
                    Read from the report but not in the dropdown lists, so left blank:
                  </div>
                  <ul className="mb-0 pl-4">
                    {extractionNotice.unmatched.map((item, i) => (
                      <li key={`unmatched-${i}`}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {extractionNotice.canRetry && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="mt-2"
                  onClick={applyExtractionToForm}
                >
                  Retry auto-fill
                </Button>
              )}
            </Alert>
          )}

          <form>

            {/* Moved initial assessment fields from PatientProfile */}

            {/* Original Diagnosis Form */}
            <div className="dx-section">
            <h6 className="dx-section-title">Diagnosis Details</h6>
            <div className="row">
            
            
<div className="col-md-4">
  <div className="form-group mb-3">
    <label>Status<span className="text-red"> *</span></label>
    <Select
      className={`w-100 ${errors.status ? "is-invalid" : ""}`}
      dropdownClassName="dx-dropdown"
      placeholder="Select Status"
      value={formData.status || undefined}
      onChange={(value) => {
        setFormData(prev => ({
          ...prev,
          status: value
        }));
        setInitialFormData(prev => ({
          ...prev,
          status: value
        }));
        if (errors.status) {
          setErrors(prev => ({
            ...prev,
            status: ""
          }));
        }
      }}
      loading={loadingDropdowns.statuses}
      showSearch
      filterOption={(input, option) => {
        if (!input) return true;
        return option.children.toLowerCase().includes(input.toLowerCase());
      }}
      onSearch={(value) => {
        setNewItemInput(prev => ({ ...prev, status: value }));
      }}
      notFoundContent={
        newItemInput.status ? (
          <div 
            style={{ padding: '5px', cursor: 'pointer', background: '#f0f0f0', borderRadius: '4px' }}
            onClick={() => {
              if (newItemInput.status.trim()) {
                saveDiagnosisStatus(newItemInput.status.trim())
                  .then(result => {
                    if (result) {
                      setFormData(prev => ({
                        ...prev,
                        status: newItemInput.status.trim()
                      }));
                      swal({
                        title: "Success!",
                        text: "New status added successfully",
                        icon: "success",
                        buttons: false,
                        timer: 2000,
                      });
                      setNewItemInput({...newItemInput, status: ""});
                    }
                  });
              }
            }}
          >
            Create "{newItemInput.status}"
          </div>
        ) : (
          <div style={{ padding: '5px' }}>No data found</div>
        )
      }
    >
      {diagnosisStatuses.map((status) => (
        <Option key={status.statusId} value={status.statusName}>
          {status.statusName}
        </Option>
      ))}
    </Select>
    {errors.status && (
      <div className="invalid-feedback d-block">{errors.status}</div>
    )}
  </div>
</div>
                  {/* <Select
                    className={`w-100 ${errors.status ? "is-invalid" : ""}`}
                    placeholder="Select Status"
                    value={formData.status || undefined}
                    onChange={(value) => {
                      setFormData(prev => ({
                        ...prev,
                        status: value
                      }));
                      setInitialFormData(prev => ({
                        ...prev,
                        status: value
                      }));
                    }}
                    loading={loadingDropdowns.statuses}
                    showSearch
                    filterOption={(input, option) => {
                      // Simple filter function that only checks if the option includes the input text
                      if (!input) return true;
                      return option.children.toLowerCase().includes(input.toLowerCase());
                    }}
                    onSearch={(value) => {
                      // Move the state update from filterOption to onSearch
                      setNewItemInput(prev => ({ ...prev, status: value }));
                    }}
                    notFoundContent={
                      newItemInput.status ? (
                        <div 
                          style={{ padding: '5px', cursor: 'pointer', background: '#f0f0f0', borderRadius: '4px' }}
                          onClick={() => {
                            if (newItemInput.status.trim()) {
                              saveDiagnosisStatus(newItemInput.status.trim())
                                .then(result => {
                                  if (result) {
                                    setFormData(prev => ({
                                      ...prev,
                                      status: newItemInput.status.trim()
                                    }));
                                    swal({
                                      title: "Success!",
                                      text: "New status added successfully",
                                      icon: "success",
                                      buttons: false,
                                      timer: 2000,
                                    });
                                    setNewItemInput({...newItemInput, status: ""});
                                  }
                                });
                            }
                          }}
                        >
                          Create "{newItemInput.status}"
                        </div>
                      ) : (
                        <div style={{ padding: '5px' }}>No data found</div>
                      )
                    }
                  >
                    {diagnosisStatuses.map((status) => (
                      <Option key={status.statusId} value={status.statusName}>
                        {status.statusName}
                      </Option>
                    ))}
                  </Select> */}
                  {/* {errors.status && (
                    <div className="invalid-feedback">{errors.status}</div>
                  )}
                </div>
              </div> */}
              
              <div className="col-md-4">
                <div className="form-group mb-3">
                  <label>Date of Diagnosis</label>
                  <DatePicker
                    className={`form-control ${errors.dateOfDiagnosis ? "is-invalid" : ""}`}
                    value={formData.dateOfDiagnosis}
                    onChange={(date) => {
                      setFormData(prev => ({
                        ...prev,
                        dateOfDiagnosis: date
                      }));
                    }}
                    format="DD-MM-YYYY" // Using standard DD-MM-YYYY format
                    placeholder="Select date"
                  />
                  {errors.dateOfDiagnosis && (
                    <div className="invalid-feedback">{errors.dateOfDiagnosis}</div>
                  )}
                </div>
              </div>
              
              <div className="col-md-4">
                <div className="form-group mb-3">
                  <label>Diagnosis Type<span className="text-red"> *</span></label>
                 
<Select
  className={`w-100 ${errors.diagnosisType ? "is-invalid" : ""}`}
  dropdownClassName="dx-dropdown"
  placeholder="Select Diagnosis Type"
  value={formData.diagnosisType || undefined}
  onChange={(value) => {
    setFormData(prev => ({
      ...prev,
      diagnosisType: value
    }));
    setInitialFormData(prev => ({
      ...prev,
      diagnosisType: value
    }));
  }}
  loading={loadingDropdowns.types}
  showSearch
  filterOption={(input, option) => {
    if (!input) return true;
    return option.children.toLowerCase().includes(input.toLowerCase());
  }}
  onSearch={(value) => {
    setNewItemInput(prev => ({ ...prev, type: value }));
  }}
  notFoundContent={
    newItemInput.type ? (
      <div 
        style={{ padding: '5px', cursor: 'pointer', background: '#f0f0f0', borderRadius: '4px' }}
        onClick={() => {
          if (newItemInput.type.trim()) {
            saveDiagnosisType(newItemInput.type.trim())
              .then(result => {
                if (result) {
                  setFormData(prev => ({
                    ...prev,
                    diagnosisType: newItemInput.type.trim()
                  }));
                  swal({
                    title: "Success!",
                    text: "New diagnosis type added successfully",
                    icon: "success",
                    buttons: false,
                    timer: 2000,
                  });
                  setNewItemInput({...newItemInput, type: ""});
                }
              });
          }
        }}
      >
        Create "{newItemInput.type}"
      </div>
    ) : (
      <div style={{ padding: '5px' }}>No data found</div>
    )
  }
>
  {/* Use typeId as a unique key to prevent duplicates */}
  {diagnosisTypes.map((type) => (
    <Option key={type.typeId} value={type.typeName}>
      {type.typeName}
    </Option>
  ))}
</Select>
                  {/* <Select
                    className={`w-100 ${errors.diagnosisType ? "is-invalid" : ""}`}
                    placeholder="Select Diagnosis Type"
                    value={formData.diagnosisType || undefined}
                    onChange={(value) => {
                      setFormData(prev => ({
                        ...prev,
                        diagnosisType: value
                      }));
                      setInitialFormData(prev => ({
                        ...prev,
                        diagnosisType: value
                      }));
                    }}
                    loading={loadingDropdowns.types}
                    showSearch
                    filterOption={(input, option) => {
                      if (!input) return true;
                      return option.children.toLowerCase().includes(input.toLowerCase());
                    }}
                    onSearch={(value) => {
                      setNewItemInput(prev => ({ ...prev, type: value }));
                    }}
                    notFoundContent={
                      newItemInput.type ? (
                        <div 
                          style={{ padding: '5px', cursor: 'pointer', background: '#f0f0f0', borderRadius: '4px' }}
                          onClick={() => {
                            if (newItemInput.type.trim()) {
                              saveDiagnosisType(newItemInput.type.trim())
                                .then(result => {
                                  if (result) {
                                    setFormData(prev => ({
                                      ...prev,
                                      diagnosisType: newItemInput.type.trim()
                                    }));
                                    swal({
                                      title: "Success!",
                                      text: "New diagnosis type added successfully",
                                      icon: "success",
                                      buttons: false,
                                      timer: 2000,
                                    });
                                    setNewItemInput({...newItemInput, type: ""});
                                  }
                                });
                            }
                          }}
                        >
                          Create "{newItemInput.type}"
                        </div>
                      ) : (
                        <div style={{ padding: '5px' }}>No data found</div>
                      )
                    }
                  >
                    {diagnosisTypes.map((type) => (
                      <Option key={type.typeId} value={type.typeName}>
                        {type.typeName}
                      </Option>
                    ))}
                  </Select> */}
                  {errors.diagnosisType && (
                    <div className="invalid-feedback">{errors.diagnosisType}</div>
                  )}
                </div>
              </div>
            </div>
            <div className="row">
              {/* Not PrimarySitePicker: that wraps this same control with the
                  protocol screens' lead-site radios, and on a diagnosis the
                  backend assigns the lead and ignores whatever we send
                  (spec §2) — the radios would be a no-op UI lie. It also emits
                  site ids, and we need the objects: they carry
                  diagnosisPrimarySiteId and, for a site since retired from the
                  master, the only copy of its name (spec §6). */}
              <div className="col-md-6">
                <MultiSelectField
                  label="Primary Site"
                  required
                  options={primarySites}
                  value={selectedPrimarySites}
                  onChange={handlePrimarySitesChange}
                  error={errors.primarySite}
                  idKey="siteId"
                  labelKey="siteName"
                  /* ~21% of live diagnoses sit on "Not Found", which is
                     unintelligible without its description (spec §6.2). */
                  descriptionKey="siteDescription"
                  placeholder="Select Primary Site"
                  loading={loadingDropdowns.primarySites}
                  groupClassName="form-group mb-3"
                  listHeight={320}
                  notFoundText="No matching site found"
                />
              </div>
              <Col lg='6'>
                <MultiSelectField
                  label="Current Diagnosis"
                  required
                  options={canSitesArr}
                  value={canSiteIdIAD}
                  onChange={handleCanSiteSelect}
                  error={errors.canSiteIdIAD}
                />
              </Col>
            </div>

            {/* Diagnosis Description gets its own full-width row. It used to be
                the third col-md-6 in the row above, which Bootstrap wrapped onto
                a new line and left a blank half beside it. */}
            <div className="row">
              <div className="col-12">
                <div className="form-group mb-3">
                  <label>Diagnosis Description</label>
                  <MicButton
                    label="Diagnosis Description"
                    long
                    silenceMs={5000}
                    showLangToggle={false}
                    onTranscript={appendVoiceText('description')}
                  />
                  <textarea
                    className="form-control"
                    rows="4"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                  ></textarea>
                </div>
              </div>
            </div>
            </div>

            <div className="dx-section">
            <h6 className="dx-section-title">Histology</h6>
            <div className="row">
              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label>Histology</label>
                  <Select
                    className={`w-100 ${errors.histology ? "is-invalid" : ""}`}
                    dropdownClassName="dx-dropdown"
                    placeholder="Select Histology"
                    value={formData.histology || undefined}
                    onChange={(value) => {
                      setFormData(prev => ({
                        ...prev,
                        histology: value
                      }));
                    }}
                    loading={loadingDropdowns.histologies}
                    showSearch
                    filterOption={(input, option) => {
                      if (!input) return true;
                      return option.children.toLowerCase().includes(input.toLowerCase());
                    }}
                    onSearch={(value) => {
                      setNewItemInput(prev => ({ ...prev, histology: value }));
                    }}
                    notFoundContent={
                      newItemInput.histology ? (
                        <div 
                          style={{ padding: '5px', cursor: 'pointer', background: '#f0f0f0', borderRadius: '4px' }}
                          onClick={() => {
                            if (newItemInput.histology.trim()) {
                              saveHistology(newItemInput.histology.trim())
                                .then(result => {
                                  if (result) {
                                    setFormData(prev => ({
                                      ...prev,
                                      histology: newItemInput.histology.trim()
                                    }));
                                    swal({
                                      title: "Success!",
                                      text: "New histology added successfully",
                                      icon: "success",
                                      buttons: false,
                                      timer: 2000,
                                    });
                                    setNewItemInput({...newItemInput, histology: ""});
                                  }
                                });
                            }
                          }}
                        >
                          Create "{newItemInput.histology}"
                        </div>
                      ) : (
                        <div style={{ padding: '5px' }}>No data found</div>
                      )
                    }
                  >
                    {histologies.map((histology) => (
                      <Option key={histology.histologyId} value={histology.histologyName}>
                        {histology.histologyName}
                      </Option>
                    ))}
                  </Select>
                  {errors.histology && (
                    <div className="invalid-feedback">{errors.histology}</div>
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label>Histology Grade</label>
                  <Select
                    className={`w-100 ${errors.histologyGrade ? "is-invalid" : ""}`}
                    dropdownClassName="dx-dropdown"
                    placeholder="Select Histology Grade"
                    value={formData.histologyGrade || undefined}
                    onChange={(value) => {
                      setFormData(prev => ({
                        ...prev,
                        histologyGrade: value
                      }));
                    }}
                    loading={loadingDropdowns.histologyGrades}
                    showSearch
                    filterOption={(input, option) => {
                      if (!input) return true;
                      return option.children.toLowerCase().includes(input.toLowerCase());
                    }}
                    onSearch={(value) => {
                      setNewItemInput(prev => ({ ...prev, histologyGrade: value }));
                    }}
                    notFoundContent={
                      newItemInput.histologyGrade ? (
                        <div 
                          style={{ padding: '5px', cursor: 'pointer', background: '#f0f0f0', borderRadius: '4px' }}
                          onClick={() => {
                            if (newItemInput.histologyGrade.trim()) {
                              saveHistologyGrade(newItemInput.histologyGrade.trim())
                                .then(result => {
                                  if (result) {
                                    setFormData(prev => ({
                                      ...prev,
                                      histologyGrade: newItemInput.histologyGrade.trim()
                                    }));
                                    swal({
                                      title: "Success!",
                                      text: "New histology grade added successfully",
                                      icon: "success",
                                      buttons: false,
                                      timer: 2000,
                                    });
                                    setNewItemInput({...newItemInput, histologyGrade: ""});
                                  }
                                });
                            }
                          }}
                        >
                          Create "{newItemInput.histologyGrade}"
                        </div>
                      ) : (
                        <div style={{ padding: '5px' }}>No data found</div>
                      )
                    }
                  >
                    {histologyGrades.map((grade) => (
                      <Option key={grade.gradeId} value={grade.gradeName}>
                        {grade.gradeName}
                      </Option>
                    ))}
                  </Select>
                  {errors.histologyGrade && (
                    <div className="invalid-feedback">{errors.histologyGrade}</div>
                  )}
                </div>
              </div>
            </div>
            </div>

            <div className="dx-section">
            <h6 className="dx-section-title">Risk &amp; Metastasis</h6>
            <div className="row mb-3">
              <div className="col-12">
                <span className="dx-subtitle">Risk Stratification</span>

                <div>
                {riskStratificationOptions.map((opt) => (
  <div className="form-check form-check-inline" key={opt.value}>
    <input
      className="form-check-input"
      type="radio"
      id={`risk-${opt.value}`}
      name="riskStratification"
      checked={
        formData.riskStratification === opt.value
      }
      onChange={() =>
        setFormData((prev) => ({
          ...prev,
          riskStratification: opt.value,
        }))
      }
    />
    <label className="form-check-label" htmlFor={`risk-${opt.value}`}>
      {opt.label}
    </label>
  </div>
))}
    </div>
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-12">
                <span className="dx-subtitle">Metastasis</span>
                <div>
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      id="metastasis-yes"
                      name="metastasis"
                      checked={formData.metastasis === "Yes"}
                      onChange={() => handleMetastasisChange("Yes")}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="metastasis-yes"
                    >
                      Yes
                    </label>
                  </div>
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      id="metastasis-no"
                      name="metastasis"
                      checked={formData.metastasis === "No"}
                      onChange={() => handleMetastasisChange("No")}
                    />
                    <label className="form-check-label" htmlFor="metastasis-no">
                      No
                    </label>
                  </div>
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      id="metastasis-unknown"
                      name="metastasis"
                      checked={formData.metastasis === "Don't Know"}
                      onChange={() => handleMetastasisChange("Don't Know")}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="metastasis-unknown"
                    >
                      Don't Know
                    </label>
                  </div>
                 {/* If metastasis is Yes, show this section */}
{formData.metastasis === "Yes" && (
  <div className="mt-3 border p-3 rounded">
    <div className="row">
      {/* Left side: Details */}
      <div className="col-md-6 border-right">
        <div className="row">
          <div className="col-12">
            <label>Metastasis Details</label>
            <textarea
              className="form-control"
              rows="6"
              name="metastasisDetails"
              value={formData.metastasisDetails || ""}
              onChange={handleInputChange}
            ></textarea>
          </div>
        </div>
      </div>
      
      {/* Right side: Metastatic Site */}
      <div className="col-md-6">
        <h5 className="mb-3">Metastatic Site</h5>
        
        <div className="d-flex justify-content-between align-items-center mb-2">
          <Button 
            className="btn btn-sm btn-outline-primary"
            onClick={() => {
              // Add your save to list functionality here
              if (chooseInput) {
                handleMetastaticSiteToggle(chooseInput);
              }
            }}
          >
            Save To List
          </Button>
          {/* <Button className="btn btn-sm btn-outline-secondary">Options</Button> */}
        </div>
        
        {/* Input for adding new sites */}
        <div className="form-group mb-2">
          <input
            type="text"
            className="form-control"
            placeholder="Add new metastatic site"
            value={chooseInput}
            onChange={(e) => setChooseInput(e.target.value)}
          />
        </div>
        
        {/* Display the selected sites with delete buttons */}
        <div 
          className="form-control d-flex flex-wrap align-items-center" 
          style={{
            minHeight: '38px', 
            height: 'auto', 
            padding: '0.375rem 0.75rem',
            marginBottom: '10px'
          }}
        >
          {selectedMetastaticSites && selectedMetastaticSites.length > 0 ? (
            selectedMetastaticSites.map((site, index) => (
              <div 
                key={`site-${index}`} 
                className="btn btn-sm btn-secondary d-flex align-items-center mr-2 mb-1"
              >
                {typeof site === 'string' ? site : (site.siteName || 'Unknown')}
                <Button
                  variant="link"
                  className="text-white p-0 ml-2"
                  onClick={() => removeMetastaticSite(site)}
                  style={{ lineHeight: 1 }}
                >
                  &times;
                </Button>
              </div>
            ))
          ) : (
            <span className="text-muted">No sites selected</span>
          )}
        </div>

        {/* Available sites to select */}
        <div className="form-group mt-2">
          <div className="d-flex flex-wrap">
            {availableMetastaticSites.map((site, index) => {
              // Check if this site is already selected using a consistent comparison
              const isSelected = selectedMetastaticSites.some(s => {
                const currentSiteName = typeof s === 'string' ? s : s.siteName;
                const newSiteName = typeof site === 'string' ? site : site.siteName;
                
                return currentSiteName === newSiteName;
              });
              
              return (
                <Button
                  key={`site-${site.siteId || site.id || index}`}
                  className={`btn btn-sm mr-1 mb-1 ${isSelected ? 'btn-secondary' : 'btn-outline-secondary'}`}
                  onClick={() => {
                    if (!isSelected) {
                      handleMetastaticSiteToggle(site);
                    }
                  }}
                  disabled={isSelected}
                >
                  {site.siteName}
                </Button>
              );
            })}
          </div>
          {errors.metastaticSites && (
            <div className="text-danger mt-2">{errors.metastaticSites}</div>
          )}
        </div>
      </div>
    </div>
  </div>
)}
                </div>
              </div>
            </div>
            </div>

            <div className="dx-section">
            <h6 className="dx-section-title">Biomarkers</h6>
            <div className="row mb-3">
              <div className="col-12">
                <div className="table-responsive">
                  <table className="table table-bordered border">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Name</th>
                        <th>Status</th>
                        <th>Value</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                    {formData.biomarkers.map((biomarker, index) => (
  <tr 
    key={`biomarker-${index}`}
    className={biomarker.name ? "row-with-content" : ""}
  >
    <td>{index + 1}</td>
    <td>
    <Select
  className="w-100"
  dropdownClassName="dx-dropdown"
  placeholder="Select Biomarker"
  value={biomarker.name || undefined}
  onChange={(value) => handleBiomarkerChange(index, 'name', value)}
  loading={loadingDropdowns.biomarkers}
  showSearch
  filterOption={(input, option) => {
    if (!input) return true;
    return option.children.toLowerCase().includes(input.toLowerCase());
  }}
  onSearch={(value) => {
    setNewItemInput(prev => ({ ...prev, biomarker: value }));
  }}
  notFoundContent={
    newItemInput.biomarker ? (
      <div 
        style={{ padding: '5px', cursor: 'pointer', background: '#f0f0f0', borderRadius: '4px' }}
        onClick={() => {
          if (newItemInput.biomarker.trim()) {
            saveBiomarker(newItemInput.biomarker.trim())
              .then(result => {
                if (result) {
                  handleBiomarkerChange(index, 'name', newItemInput.biomarker.trim());
                  swal({
                    title: "Success!",
                    text: "New biomarker added successfully",
                    icon: "success",
                    buttons: false,
                    timer: 2000,
                  });
                  setNewItemInput({...newItemInput, biomarker: ""});
                }
              });
            }
          }}
        >
          Create "{newItemInput.biomarker}"
        </div>
      ) : (
        <div style={{ padding: '5px' }}>No data found</div>
      )
    }
  >
  {biomarkers.map((biomarker) => (
    <Option key={biomarker.biomarkerId} value={biomarker.biomarkerName}>
      {biomarker.biomarkerName}
    </Option>
  ))}
</Select>
</td>
    <td>
    <Select
      className="w-100"
      dropdownClassName="dx-dropdown"
      placeholder="Select Status"
      value={biomarker.status || undefined}
      onChange={(value) => handleBiomarkerChange(index, 'status', value)}
      loading={loadingDropdowns.biomarkerStatuses}
      showSearch
      filterOption={(input, option) => {
        if (!input) return true;
        return option.children.toLowerCase().includes(input.toLowerCase());
      }}
      onSearch={(value) => {
        setNewItemInput(prev => ({ ...prev, biomarkerStatus: value }));
      }}
      notFoundContent={
        newItemInput.biomarkerStatus ? (
          <div 
            style={{ padding: '5px', cursor: 'pointer', background: '#f0f0f0', borderRadius: '4px' }}
            onClick={() => {
              if (newItemInput.biomarkerStatus.trim()) {
                saveBiomarkerStatus(newItemInput.biomarkerStatus.trim())
                  .then(result => {
                    if (result) {
                      handleBiomarkerChange(index, 'status', newItemInput.biomarkerStatus.trim());
                      swal({
                        title: "Success!",
                        text: "New biomarker status added successfully",
                        icon: "success",
                        buttons: false,
                        timer: 2000,
                      });
                      setNewItemInput({...newItemInput, biomarkerStatus: ""});
                    }
                  });
              }
            }}
          >
            Create "{newItemInput.biomarkerStatus}"
          </div>
        ) : (
          <div style={{ padding: '5px' }}>No data found</div>
        )
      }
    >
      {biomarkerStatuses.map((status) => (
        <Option key={status.statusId} value={status.statusName}>
          {status.statusName}
        </Option>
      ))}
    </Select>
    </td>
    <td>
      <input
        type="text"
        className="form-control"
        value={biomarker.value}
        onChange={(e) => handleBiomarkerChange(index, 'value', e.target.value)}
      />
    </td>
    <td>
      <span 
        className="delete-icon1" 
        onClick={() => removeBiomarkerRow(index)}
        style={{ cursor: 'pointer', color: '#dc3545', fontSize: '18px', padding: '4px 8px' }}
      >
        ✕
      </span>
    </td>
  </tr>
))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            </div>

            <div className="dx-section">
            <h6 className="dx-section-title">Tumour Size, Lymph Nodes &amp; Staging</h6>
              {/* Primary Tumor Size Section */}
              <div className="row mb-5"> {/* Changed from mb-3 to mb-5 for more spacing */}
                <div className="col-12">
                  <span className="dx-subtitle">Primary Tumor Size</span>
                  <div className="table-responsive" style={{ overflow: "visible" }}> {/* Added overflow: visible */}
                    <table className="table table-bordered border">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>LENGTH</th>
                          <th>WIDTH</th>
                          <th>DEPTH</th>
                          <th>T STAGE</th>
                          <th>DESCRIPTION</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {formData.tumorSizes.map((tumor, index) => (
                          <tr 
                            key={`tumor-${index}`}
                            className={tumor.length ? "row-with-content" : ""}
                          >
                            <td>{index + 1}</td>
                            <td>
                              <div className="dropdown-container position-relative">
                                <input
                                  type="text"
                                  className="form-control"
                                  value={tumor.length || ''}
                                  onClick={() => handleDropdownToggle('tumor', index, 'length')}
                                  onChange={(e) => {
                                    // Extract the numeric value from the input
                                    const value = e.target.value.replace(/[^0-9.]/g, '');
                                    handleTumorSizeChange(index, "length", value);
                                  }}
                                  placeholder="Enter length"
                                />
                                {/* Show unit as a separate element next to the input */}
                                {tumor.length && tumor.lengthUnit && (
                                  <span className="position-absolute" style={{ right: '10px', top: '50%', transform: 'translateY(-50%)' }}>
                                    {tumor.lengthUnit}
                                  </span>
                                )}
                                {/* Always show both units in dropdown when field is clicked */}
                                {activeDropdown.type === 'tumor' && 
                                activeDropdown.index === index && 
                                activeDropdown.field === 'length' && (
                                  <div className="position-absolute bg-white border shadow-sm w-100"
                                      style={{ 
                                        zIndex: 9999, 
                                        top: 'calc(100% + 5px)',
                                        left: 0,
                                        maxHeight: '150px',
                                        overflowY: 'auto'
                                      }}
                                  >
                                    {measurementUnitOptions.map((unit) => (
                                      <div 
                                        key={unit} 
                                        className="p-2 hover-bg-light cursor-pointer"
                                        onClick={() => {
                                          handleTumorUnitSelect(index, 'length', unit);
                                          // Close dropdown after selection
                                          setActiveDropdown({ type: null, index: null, field: null });
                                        }}
                                        style={{ cursor: 'pointer' }}
                                      >
                                        {tumor.length || "0"} {unit}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td>
                              <div className="dropdown-container position-relative">
                                <input
                                  type="text"
                                  className="form-control"
                                  value={tumor.width || ''}
                                  onClick={() => handleDropdownToggle('tumor', index, 'width')}
                                  onChange={(e) => {
                                    // Extract the numeric value from the input
                                    const value = e.target.value.replace(/[^0-9.]/g, '');
                                    handleTumorSizeChange(index, "width", value);
                                  }}
                                  placeholder="Enter width"
                                />
                                {/* Show unit as a separate element next to the input */}
                                {tumor.width && tumor.widthUnit && (
                                  <span className="position-absolute" style={{ right: '10px', top: '50%', transform: 'translateY(-50%)' }}>
                                    {tumor.widthUnit}
                                  </span>
                                )}
                                {/* Always show both units in dropdown when field is clicked */}
                                {activeDropdown.type === 'tumor' && 
                                activeDropdown.index === index && 
                                activeDropdown.field === 'width' && (
                                  <div className="position-absolute bg-white border shadow-sm w-100"
                                      style={{ 
                                        zIndex: 9999, 
                                        top: 'calc(100% + 5px)',
                                        left: 0,
                                        maxHeight: '150px',
                                        overflowY: 'auto'
                                      }}
                                  >
                                    {measurementUnitOptions.map((unit) => (
                                      <div 
                                        key={unit} 
                                        className="p-2 hover-bg-light cursor-pointer"
                                        onClick={() => {
                                          handleTumorUnitSelect(index, 'width', unit);
                                          // Close dropdown after selection
                                          setActiveDropdown({ type: null, index: null, field: null });
                                        }}
                                        style={{ cursor: 'pointer' }}
                                      >
                                        {tumor.width || "0"} {unit}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td>
                              <div className="dropdown-container position-relative">
                                <input
                                  type="text"
                                  className="form-control"
                                  value={tumor.depth || ''}
                                  onClick={() => handleDropdownToggle('tumor', index, 'depth')}
                                  onChange={(e) => {
                                    // Extract the numeric value from the input
                                    const value = e.target.value.replace(/[^0-9.]/g, '');
                                    handleTumorSizeChange(index, "depth", value);
                                  }}
                                  placeholder="Enter depth"
                                />
                                {/* Show unit as a separate element next to the input */}
                                {tumor.depth && tumor.depthUnit && (
                                  <span className="position-absolute" style={{ right: '10px', top: '50%', transform: 'translateY(-50%)' }}>
                                    {tumor.depthUnit}
                                  </span>
                                )}
                                {/* Always show both units in dropdown when field is clicked */}
                                {activeDropdown.type === 'tumor' && 
                                activeDropdown.index === index && 
                                activeDropdown.field === 'depth' && (
                                  <div className="position-absolute bg-white border shadow-sm w-100"
                                      style={{ 
                                        zIndex: 9999, 
                                        top: 'calc(100% + 5px)',
                                        left: 0,
                                        maxHeight: '150px',
                                        overflowY: 'auto'
                                      }}
                                  >
                                    {measurementUnitOptions.map((unit) => (
                                      <div 
                                        key={unit} 
                                        className="p-2 hover-bg-light cursor-pointer"
                                        onClick={() => {
                                          handleTumorUnitSelect(index, 'depth', unit);
                                          // Close dropdown after selection
                                          setActiveDropdown({ type: null, index: null, field: null });
                                        }}
                                        style={{ cursor: 'pointer' }}
                                      >
                                        {tumor.depth || "0"} {unit}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td>
                              <Select
                                className="w-100"
                                dropdownClassName="dx-dropdown"
                                placeholder="Select T Stage"
                                value={tumor.tStage || undefined}
                                onChange={(value) => {
                                  // Update the tumor size with the selected stage
                                  handleTumorSizeChange(index, "tStage", value);
                                }}
                                showSearch
                                filterOption={(input, option) => {
                                  if (!input) return true;
                                  return option.children.toLowerCase().includes(input.toLowerCase());
                                }}
                              >
                                {T_STAGE_VALUES.map((stage) => (
                                  <Option key={stage} value={stage}>
                                    {stage}
                                  </Option>
                                ))}
                              </Select>
                            </td>
                            <td>
                              <input
                                type="text"
                                className="form-control"
                                value={tumor.description || ''}
                                onChange={(e) => handleTumorSizeChange(index, "description", e.target.value)}
                                placeholder="Add description"
                              />
                            </td>
                            <td>
                              <span 
                                className="delete-icon" 
                                onClick={() => removeTumorSizeRow(index)}
                                style={{ cursor: 'pointer', color: '#dc3545', fontSize: '18px', padding: '4px 8px' }}
                              >
                                ✕
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Add spacing div to ensure dropdowns have room to display */}
              <div style={{ marginBottom: "30px" }}></div>

              {/* Lymph Node Section */}
              <div className="row mb-3">
                <div className="col-12">
                  <span className="dx-subtitle">Lymph Node</span>
                  <div className="table-responsive" style={{ overflow: "visible" }}> {/* Added overflow: visible */}
                    <table className="table table-bordered border">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>LN GROUP</th>
                          <th>LENGTH</th>
                          <th>WIDTH</th>
                          <th>DEPTH</th>
                          <th>N STAGE</th>
                          <th>DESCRIPTION</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                      {formData.lymphNodes.map((node, index) => (
  <tr 
    key={`node-${index}`}
    className={node.lnGroup ? "row-with-content" : ""}
  >
    <td>{index + 1}</td>
    <td>
      <input
        type="text"
        className="form-control"
        value={node.lnGroup || ''}
        onChange={(e) => handleLymphNodeChange(index, "lnGroup", e.target.value)}
        placeholder="Enter LN Group"
      />
    </td>
    <td>
      <div className="dropdown-container position-relative">
        <input
          type="text"
          className="form-control"
          value={node.length || ''}
          onClick={() => handleDropdownToggle('lymph', index, 'length')}
          onChange={(e) => {
            // Extract the numeric value from the input
            const value = e.target.value.replace(/[^0-9.]/g, '');
            handleLymphNodeChange(index, "length", value);
          }}
          placeholder="Enter length"
        />
        {/* Show unit as a separate element next to the input */}
        {node.length && node.lengthUnit && (
          <span className="position-absolute" style={{ right: '10px', top: '50%', transform: 'translateY(-50%)' }}>
            {node.lengthUnit}
          </span>
        )}
        {/* Unit selection dropdown */}
        {activeDropdown.type === 'lymph' && 
          activeDropdown.index === index && 
          activeDropdown.field === 'length' && (
          <div className="position-absolute bg-white border shadow-sm w-100"
              style={{ 
                zIndex: 9999, 
                top: 'calc(100% + 5px)',
                left: 0,
                maxHeight: '150px',
                overflowY: 'auto'
              }}
          >
            {measurementUnitOptions.map((unit) => (
              <div 
                key={unit} 
                className="p-2 hover-bg-light cursor-pointer"
                onClick={() => {
                  handleLymphUnitSelect(index, 'length', unit);
                  // Close dropdown after selection  
                  setActiveDropdown({ type: null, index: null, field: null });
                }}
                style={{ cursor: 'pointer' }}
              >
                {node.length || "0"} {unit}
              </div>
            ))}
          </div>
        )}
      </div>
    </td>
                            <td>
                              <div className="dropdown-container position-relative">
                                <input
                                  type="text"
                                  className="form-control"
                                  value={node.width || ''}
                                  onClick={() => handleDropdownToggle('lymph', index, 'width')}
                                  onChange={(e) => {
                                    // Extract the numeric value from the input
                                    const value = e.target.value.replace(/[^0-9.]/g, '');
                                    handleLymphNodeChange(index, "width", value);
                                  }}
                                  placeholder="Enter width"
                                />
                                {/* Show unit as a separate element next to the input */}
                                {node.width && node.widthUnit && (
                                  <span className="position-absolute" style={{ right: '10px', top: '50%', transform: 'translateY(-50%)' }}>
                                    {node.widthUnit}
                                  </span>
                                )}
                                {/* Always show both units in dropdown when field is clicked */}
                                {activeDropdown.type === 'lymph' && 
                                activeDropdown.index === index && 
                                activeDropdown.field === 'width' && (
                                  <div className="position-absolute bg-white border shadow-sm w-100"
                                      style={{ 
                                        zIndex: 9999, 
                                        top: 'calc(100% + 5px)',
                                        left: 0,
                                        maxHeight: '150px',
                                        overflowY: 'auto'
                                      }}
                                  >
                                    {measurementUnitOptions.map((unit) => (
                                      <div 
                                        key={unit} 
                                        className="p-2 hover-bg-light cursor-pointer"
                                        onClick={() => {
                                          handleLymphUnitSelect(index, 'width', unit);
                                          // Close dropdown after selection
                                          setActiveDropdown({ type: null, index: null, field: null });
                                        }}
                                        style={{ cursor: 'pointer' }}
                                      >
                                        {node.width || "0"} {unit}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td>
                              <div className="dropdown-container position-relative">
                                <input
                                  type="text"
                                  className="form-control"
                                  value={node.depth || ''}
                                  onClick={() => handleDropdownToggle('lymph', index, 'depth')}
                                  onChange={(e) => {
                                    // Extract the numeric value from the input
                                    const value = e.target.value.replace(/[^0-9.]/g, '');
                                    handleLymphNodeChange(index, "depth", value);
                                  }}
                                  placeholder="Enter depth"
                                />
                                {/* Show unit as a separate element next to the input */}
                                {node.depth && node.depthUnit && (
                                  <span className="position-absolute" style={{ right: '10px', top: '50%', transform: 'translateY(-50%)' }}>
                                    {node.depthUnit}
                                  </span>
                                )}
                                {/* Always show both units in dropdown when field is clicked */}
                                {activeDropdown.type === 'lymph' && 
                                activeDropdown.index === index && 
                                activeDropdown.field === 'depth' && (
                                  <div className="position-absolute bg-white border shadow-sm w-100"
                                      style={{ 
                                        zIndex: 9999, 
                                        top: 'calc(100% + 5px)',
                                        left: 0,
                                        maxHeight: '150px',
                                        overflowY: 'auto'
                                      }}
                                  >
                                    {measurementUnitOptions.map((unit) => (
                                      <div 
                                        key={unit} 
                                        className="p-2 hover-bg-light cursor-pointer"
                                        onClick={() => {
                                          handleLymphUnitSelect(index, 'depth', unit);
                                          // Close dropdown after selection
                                          setActiveDropdown({ type: null, index: null, field: null });
                                        }}
                                        style={{ cursor: 'pointer' }}
                                      >
                                        {node.depth || "0"} {unit}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td>
                              <Select
                                className="w-100"
                                dropdownClassName="dx-dropdown"
                                placeholder="Select N Stage"
                                value={node.nStage || undefined}
                                onChange={(value) => {
                                  // Update the lymph node with the selected stage
                                  handleLymphNodeChange(index, "nStage", value);
                                }}
                                showSearch
                                filterOption={(input, option) => {
                                  if (!input) return true;
                                  return option.children.toLowerCase().includes(input.toLowerCase());
                                }}
                              >
                                {N_STAGE_VALUES.map((stage) => (
                                  <Option key={stage} value={stage}>
                                    {stage}
                                  </Option>
                                ))}
                              </Select>
                            </td>
                            <td>
                              <input
                                type="text"
                                className="form-control"
                                value={node.description || ''}
                                onChange={(e) => handleLymphNodeChange(index, "description", e.target.value)}
                                placeholder="Add description"
                              />
                            </td>
                            <td>
                              <span 
                                className="delete-icon" 
                                onClick={() => removeLymphNodeRow(index)}
                                style={{ cursor: 'pointer', color: '#dc3545', fontSize: '18px', padding: '4px 8px' }}
                              >
                                ✕
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            {/* Add Staging section */}
            <PatientStagingDetails 
              patientId={getPatientId()}
              doctorId={doctorId}
              formData={formData}
              setFormData={setFormData}
              viewOnly={!isFormVisible}
              onCancerStageChange={(numericStage) => {
                // Find the corresponding stage ID from the cancer stage master array
                const stageId = cancerStageMstArr.find(stage => stage.label === `Stage-${numericStage}`)?.id || '';
                if (stageId) {
                  // Update the cancer stage in the form
                  setCancerStage(stageId);
                }
              }}
            />

            

            </div>

            <div className="dx-section">
            <h6 className="dx-section-title">Investigation Reports</h6>
            {/* Add Investigations Confirmation Reports upload */}
            <div className="row mb-3">
              <div className="col-12">
                <input
                  type="file"
                  className="form-control"
                  multiple
                  onChange={handleFileUpload}
                />
                <div className="mt-2">
                  {formData.investigationReports.map((file, index) => (
                    <div key={index} className="d-flex align-items-center mt-2">
                      <span className="mr-2">{file.name || file.fileName}</span>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() =>
                          setFormData((prev) => ({
                            ...prev,
                            investigationReports:
                              prev.investigationReports.filter(
                                (_, i) => i !== index,
                              ),
                          }))
                        }
                      >
                        Remove
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() =>
                          viewPdfFile(file.fileContent, file.name || file.fileName)
                        }
                      >
                        View
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            </div>

            <div className="dx-section">
            <h6 className="dx-section-title">Classification &amp; Remarks</h6>
<Row>
              {/* <Col lg='3'>
                <Form.Group>
                  <Form.Label>Pathology<span className="text-red"> *</span></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter Pathology"
                    name="pathology"
                    value={pathology}
                    onChange={handleInitialAssessmentChange}
                    id="pathologyId"
                  />
                  <div className="invalid-feedback">{initialAssesmentErrors.pathology}</div>
                </Form.Group>
              </Col> */}
              <Col lg='3'>
  <Form.Group>
    <Form.Label>Pathology<span className="text-red"> *</span></Form.Label>
    <Form.Control
      type="text"
      placeholder="Enter Pathology"
      name="pathology"
      value={pathology}
      onChange={handleInitialAssessmentChange}
      id="pathologyId"
      className={errors.pathology ? "is-invalid" : ""}
    />
    {errors.pathology && (
      <div className="invalid-feedback d-block">{errors.pathology}</div>
    )}
  </Form.Group>
</Col>

<Col lg='3'>
  <Form.Group>
    <Form.Label>Cancer Stage<span className="text-red"> *</span></Form.Label>
    {/* antd Select in place of the native <select>, to match the rest of the
        form's dropdowns. Three things keep this behaviourally identical to the
        <select> it replaces:
        1. handleInitialAssessmentChange reads e.target.name / e.target.value,
           so the change event is reconstructed below.
        2. Option values are String(item.id). A DOM <option> always yields a
           string, but antd hands back the raw value — without this, selecting a
           stage would put a number into cancerStage and into the saved
           stageId, where a string used to go.
        3. No allowClear, and the value is coerced with `?? ''`: validateForm
           tests `cancerStage === ""`, which an undefined would slip past. The
           empty selection is carried by placeholder, which is why the old
           `<option value="">` row is gone.
        The value prop also stringifies because PatientStagingDetails'
        onCancerStageChange sets this state from a numeric stageId. */}
    <Select
      id="cancerStageId"
      className={`w-100 ${errors.cancerStage ? "is-invalid" : ""}`}
      dropdownClassName="dx-dropdown"
      placeholder="- Select Cancer Stage -"
      value={
        cancerStage === '' || cancerStage === null || cancerStage === undefined
          ? undefined
          : String(cancerStage)
      }
      onChange={(value) =>
        handleInitialAssessmentChange({
          target: { name: 'cancerStage', value: value ?? '' }
        })
      }
      showSearch
      filterOption={(input, option) => {
        if (!input) return true;
        return option.children.toLowerCase().includes(input.toLowerCase());
      }}
    >
      {
        cancerStageMstArr.map((item, i) => {
          return (
            <Option key={item.key} value={String(item.id)}>{`${item.label}`}</Option>
          );
        })
      }
    </Select>
    {errors.cancerStage && (
      <div className="invalid-feedback d-block">{errors.cancerStage}</div>
    )}
  </Form.Group>
</Col>
              {/* <Col lg='3'>
                <Form.Group>
                  <Form.Label>Cancer Stage<span className="text-red"> *</span></Form.Label>
                  <Form.Control 
                    as="select" 
                    id="cancerStageId" 
                    name='cancerStage' 
                    value={cancerStage} 
                    onChange={handleInitialAssessmentChange}
                  >
                    <option value="">- Select Cancer Stage -</option>
                    {
                      cancerStageMstArr.map((item, i) => {
                        return (
                          <option key={item.key} value={item.id}>{`${item.label}`}</option>
                        );
                      })
                    }
                  </Form.Control>
                  <div className="invalid-feedback">{initialAssesmentErrors.cancerStage}</div>
                </Form.Group>
              </Col> */}
              {cancerStage === '4' && (
                <Col lg='3'>
                  <Form.Group>
                    <Form.Label>Bone Metastasis</Form.Label>
                    <Form.Control
                      as="select"
                      id=""
                      name="boneMetasis"
                      value={boneMetasis}
                      onChange={handleInitialAssessmentChange}
                    >
                      <option value="N">No</option>
                      <option value="Y">Yes</option>
                    </Form.Control>
                    <div className="invalid-feedback">{initialAssesmentErrors.boneMetasis}</div>
                  </Form.Group>
                </Col>
              )}
              {cancerStage === '4' && (
                <Col lg='3'>
                  <Form.Group>
                    <Form.Label>Brain Metastasis</Form.Label>
                    <Form.Control
                      as="select"
                      id=""
                      name="brainMets"
                      value={brainMets}
                      onChange={handleInitialAssessmentChange}
                    >
                      <option value="N">No</option>
                      <option value="Y">Yes</option>
                    </Form.Control>
                    <div className="invalid-feedback">{initialAssesmentErrors.brainMets}</div>
                  </Form.Group>
                </Col>
              )}
              <Col lg='3'>
                <MultiSelectField
                  label="Limb Loss"
                  required
                  options={limbLossArr}
                  value={limbLossIdIAD}
                  onChange={handleLimbLossSelect}
                  error={errors.limbLossIdIAD}
                />
              </Col>
              <Col lg='3'>
                <MultiSelectField
                  label="Mutation"
                  required
                  options={mutationArr}
                  value={mutationIdIA}
                  onChange={handleMutationSelect}
                  error={errors.mutationIdIA}
                />
              </Col>

              <Col lg='3'>
                <MultiSelectField
                  label="Previous Cancer History"
                  required
                  options={prevCanSitesArr}
                  value={prevCanSiteIdIAD}
                  onChange={handlePrevCanSiteSelect}
                  error={errors.prevCanSiteIdIAD}
                />
              </Col>
              {/* <Col lg='6'>
                <Form.Group>
                  <Form.Label>Doctor Observation<span className="text-red"> *</span></Form.Label>
                  <Form.Control 
                    as="textarea" 
                    value={doctorObservation} 
                    rows={2} 
                    name='doctorObservation' 
                    onChange={handleInitialAssessmentChange} 
                  />
                  <div className="invalid-feedback">{initialAssesmentErrors.doctorObservation}</div>
                </Form.Group>
              </Col> */}
            </Row>

            <div className="row mb-3">
              <div className="col-12">
                <div className="form-group">
                  <label>Remarks</label>
                  <MicButton
                    label="Remarks"
                    long
                    silenceMs={5000}
                    showLangToggle={false}
                    onTranscript={appendVoiceText('remarks')}
                  />
                  <textarea
                    className="form-control"
                    rows="3"
                    name="remarks"
                    value={formData.remarks}
                    onChange={handleInputChange}
                  ></textarea>
                </div>
              </div>
            </div>
            </div>

          </form>
        </div>
      )}

      {/* Save Button */}
      {/* <div className="row mt-4 mb-4">
        <div className="col-12 text-end">
          <Button 
            type="button"
            className="btn btn-secondary"
            onClick={saveInitialAssessment}
            disabled={!isFormValid || saveallLoader} // Disabled if form is invalid or saving
          >
            {saveLoader && (
              <Spinner className='mr-2' animation='border' size='1.5rem' />
            )}
            {!saveLoader && (
              <FaSave className='mr-2' size='1.5rem' style={{ verticalAlign: "sub" }} />
            )}
            Save 
            </Button>
        </div>
      </div> */}
   
      {/* Diagnosis list */}
     
      {showDiagnosisList && (
  <div className="diagnosis-list-container">
    {diagnosisData.length > 0 ? (
      // Sort diagnoses by date (newest first)
      // [...diagnosisData]
      //   .sort((a, b) => {
      //     // Parse dates for comparison
      //     const dateA = a.dateOfDiagnosis ? new Date(a.dateOfDiagnosis) : new Date(0);
      //     const dateB = b.dateOfDiagnosis ? new Date(b.dateOfDiagnosis) : new Date(0);
      //     // Sort in descending order (newest first)
      //     return dateB - dateA;
      //   })
      [...diagnosisData]
  .sort((a, b) => {
    // First try to use full dateTime information if available
    
    // Look for creation date/time fields first (more precise)
    const createdAtA = a.createdDate || a.createdAt || a.updatedDate || a.updatedAt;
    const createdAtB = b.createdDate || b.createdAt || b.updatedDate || b.updatedAt;
    
    if (createdAtA && createdAtB) {
      // Use the creation date/time for comparison (most precise)
      const timeA = new Date(createdAtA);
      const timeB = new Date(createdAtB);
      
      // Sort in descending order (newest first)
      return timeB - timeA;
    }
    
    // If creation times are not available, use diagnosis dates
    const dateA = a.dateOfDiagnosis ? new Date(a.dateOfDiagnosis) : new Date(0);
    const dateB = b.dateOfDiagnosis ? new Date(b.dateOfDiagnosis) : new Date(0);
    
    // Get the date portion only (without time) for same-day comparison
    const dayA = new Date(dateA.getFullYear(), dateA.getMonth(), dateA.getDate());
    const dayB = new Date(dateB.getFullYear(), dateB.getMonth(), dateB.getDate()); 
    
    // Check if the dates are the same day
    if (dayA.getTime() === dayB.getTime()) {
      // Dates are the same, now sort by diagnosisId (if available) 
      // Assuming higher IDs are newer records
      if (a.diagnosisId && b.diagnosisId) {
        return b.diagnosisId - a.diagnosisId;
      }
      // If no ID, try to use timestamps again if available
      return dateB - dateA;
    }
    
    // Different days, sort by date (newest first)
    return dateB - dateA;
  })
        .map((diagnosis, index) => (
        <div key={diagnosis.diagnosisId || `diagnosis-${index}`} className="card mb-3">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              {diagnosis.dateOfDiagnosis ? 
                `Diagnosis (${new Date(diagnosis.dateOfDiagnosis).toLocaleDateString('en-GB', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric'
                }).replace(/\//g, '-')})` : 
                `Diagnosis ${index + 1}`}
            </h5>
            <div className="text-end" style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                className="btn btn-secondary"
                onClick={() => printDiagnosis(diagnosis.diagnosisId)}
              >
                Print
              </Button>
              <Button
                className="btn btn-secondary"
                onClick={() => showDiagnosisForm(index)}
                style={{ marginLeft: '10px' }}
              >
                Edit
              </Button>
            </div>
          </div>
          <div className="card-body">
            {/* Existing fields */}
            <div className="row mb-3">
              <div className="col-md-4">
                <strong>Status</strong>
                <div>{diagnosis.status || "N/A"}</div>
              </div>
              <div className="col-md-4">
                <strong>Diagnosis Type</strong>
                <div>{diagnosis.diagnosisType || "N/A"}</div>
              </div>
              {/* <div className="col-md-4">
                <strong>Date of Diagnosis</strong>
                <div>{diagnosis.dateOfDiagnosis ? formatDate(diagnosis.dateOfDiagnosis) : "N/A"}</div>
              </div> */}
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <strong>Primary Site</strong>
                <div>{formatPrimarySites(diagnosis)}</div>
              </div>
              <div className="col-md-6">
                <strong>Diagnosis Description</strong>
                <div style={{ whiteSpace: "pre-line" }}>
                  {diagnosis.diagnosisDescription || "N/A"}
                </div>
              </div>
            </div>

            {/* Always show these fields */}
            <div className="row mb-3">
              <div className="col-md-6">
                <strong>Pathology</strong>
                <div>{diagnosis.pathology || diagnosis.pathologyText || diagnosis.initialAssessmentData?.patInitiAssesmentDto?.pathologyText || "N/A"}</div>
              </div>
              <div className="col-md-6">
                <strong>Cancer Stage</strong>
                  <div>
                    {
                      diagnosis.initialAssessmentData?.patInitiAssesmentDto?.stageMasterDTO?.stageName ||
                      (
                        diagnosis.initialAssessmentData?.patInitiAssesmentDto?.stageMasterDTO?.stageId
                          ? diagnosis.initialAssessmentData.patInitiAssesmentDto.stageMasterDTO.stageId === 6
                            ? "TBD"
                            : `Stage-${diagnosis.initialAssessmentData.patInitiAssesmentDto.stageMasterDTO.stageId}`
                          : diagnosis.stage || "N/A"
                      )
                    }
                  </div>

              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <strong>Limb Loss</strong>
                <div>
                  {diagnosis.initialAssessmentData?.patInitiAssesmentDto?.patientLimbLossDTOList?.map((item, idx) => (
                    <span key={`limb-loss-${idx}`}>
                      {item.limbLossMasterDTO?.limbLossName || item.limbLossDTO?.selectionValue || item.name || "N/A"}
                      {idx < (diagnosis.initialAssessmentData?.patInitiAssesmentDto?.patientLimbLossDTOList?.length - 1) ? ", " : ""}
                    </span>
                  )) || "N/A"}
                </div>
              </div>
              <div className="col-md-6">
                <strong>Mutation</strong>
                <div>
                  {diagnosis.initialAssessmentData?.patInitiAssesmentDto?.patientMutationXrefList?.map((item, idx) => (
                    <span key={`mutation-${idx}`}>
                      {item.mutationMstDTO?.mutationName || item.mutationDTO?.selectionValue || item.name || "N/A"}
                      {idx < (diagnosis.initialAssessmentData?.patInitiAssesmentDto?.patientMutationXrefList?.length - 1) ? ", " : ""}
                    </span>
                  )) || "N/A"}
                </div>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <strong>Previous Cancer History</strong>
                <div>
                  {diagnosis.initialAssessmentData?.patInitiAssesmentDto?.patientPrevCancerSiteDTOList?.map((item, idx) => (
                    <span key={`prev-cancer-${idx}`}>
                      {item.cancerSiteMstDTO?.cancerSiteName || item.prevCancerSiteDTO?.selectionValue || item.name || "N/A"}
                      {idx < (diagnosis.initialAssessmentData?.patInitiAssesmentDto?.patientPrevCancerSiteDTOList?.length - 1) ? ", " : ""}
                    </span>
                  )) || "N/A"}
                </div>
              </div>
              <div className="col-md-6">
                <strong>Current Diagnosis</strong>
                <div>
                  {diagnosis.initialAssessmentData?.patInitiAssesmentDto?.patientCancerSiteDTOList?.map((site, idx) => (
                    <span key={`cancer-site-${idx}`}>
                      {site.cancerSiteMstDto?.cancerSiteName || site.name || "N/A"}
                      {idx < (diagnosis.initialAssessmentData?.patInitiAssesmentDto?.patientCancerSiteDTOList?.length - 1) ? ", " : ""}
                    </span>
                  )) || diagnosis.patientCancerSiteDTOList?.map((site, idx) => (
                    <span key={`cancer-site-alt-${idx}`}>
                      {site.cancerSiteDTO?.selectionValue || site.cancerSiteMstDto?.cancerSiteName || site.name || "N/A"}
                      {idx < (diagnosis.patientCancerSiteDTOList.length - 1) ? ", " : ""}
                    </span>
                  )) || "N/A"}
                </div>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <strong>Histology</strong>
                <div>{diagnosis.histology || diagnosis.histologyList?.[0]?.histologyDescription || diagnosis.initialAssessmentData?.patInitiAssesmentDto?.histologyText || "N/A"}</div>
              </div>
              <div className="col-md-6">
                <strong>Histology Grade</strong>
                <div>{diagnosis.histologyGrade || diagnosis.histologyGrades?.length > 0 ? diagnosis.histologyGrades[0].gradeCode : diagnosis.initialAssessmentData?.patInitiAssesmentDto?.histologyGradeText || "N/A"}</div>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-4">
                <strong>Metastasis</strong>
                <div>{diagnosis.metastasis || "N/A"}</div>
              </div>
              <div className="col-md-4">
                <strong>Risk Stratification</strong>
                <div>{diagnosis.riskStratification || "N/A"}</div>
              </div>
              {/* <div className="col-md-4">
                <strong>Date of Diagnosis</strong>
                <div>{diagnosis.dateOfDiagnosis ? formatDate(diagnosis.dateOfDiagnosis) : "N/A"}</div>
              </div> */}
            </div>

            {diagnosis.metastasis === "Yes" && diagnosis.metastaticSites && diagnosis.metastaticSites.length > 0 && (
              <div className="row mb-3">
                <div className="col-12">
                  <strong>Metastatic Sites</strong>
                  <div className="table-responsive">
                    <table className="table table-bordered table-sm">
                      <thead>
                        <tr>
                          <th>Site Name</th>
                        </tr>
                      </thead>
                      <tbody>
                        {diagnosis.metastaticSites.map((site, idx) => (
                          <tr key={`display-metastatic-site-${idx}`}>
                            <td>{site.siteName || site}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {diagnosis.metastasisDetails && (
              <div className="row mb-3">
                <div className="col-12">
                  <strong>Metastasis Details</strong>
                  <div style={{ whiteSpace: "pre-line" }}>
                    {diagnosis.metastasisDetails}
                  </div>
                </div>
              </div>
            )}

            {diagnosis.biomarkers && diagnosis.biomarkers.length > 0 && (
              <div className="row mb-3">
                <div className="col-12">
                  <strong>BioMarkers</strong>
                  <div className="table-responsive">
                    <table className="table table-bordered table-sm">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Status</th>
                          <th>Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {diagnosis.biomarkers.map((biomarker, idx) => (
                          <tr key={`display-biomarker-${idx}`}>
                            <td>{biomarker.name || ""}</td>
                            <td>{biomarker.status || ""}</td>
                            <td>{biomarker.value || ""}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {diagnosis.tumorSizes && diagnosis.tumorSizes.length > 0 && (
              <div className="row mb-3">
                <div className="col-12">
                  <strong>Primary Tumor Size</strong>
                  <div className="table-responsive">
                    <table className="table table-bordered table-sm">
                      <thead>
                        <tr>
                          <th>Length</th>
                          <th>Width</th>
                          <th>Depth</th>
                          <th>T Stage</th>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {diagnosis.tumorSizes.map((tumor, idx) => (
                          <tr key={`display-tumor-${idx}`}>
                            <td>{tumor.length || ""}</td>
                            <td>{tumor.width || ""}</td>
                            <td>{tumor.depth || ""}</td>
                            <td>{tumor.tStage || ""}</td>
                            <td>{tumor.description || ""}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}


{diagnosis.lymphNodes && diagnosis.lymphNodes.length > 0 && (
  <div className="row mb-3">
    <div className="col-12">
      <strong>Lymph Nodes</strong>
      <div className="table-responsive">
        <table className="table table-bordered table-sm">
          <thead>
            <tr>
              <th>LN Group</th>
              <th>Length</th>
              <th>Width</th>
              <th>Depth</th>
              <th>N Stage</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {diagnosis.lymphNodes.map((node, idx) => {
              // Parse measurements to avoid duplicate units
              const length = parseMeasurement(node.length);
              const width = parseMeasurement(node.width);
              const depth = parseMeasurement(node.depth);
              
              return (
                <tr key={`display-node-${idx}`}>
                  <td>{node.lnGroup || ""}</td>
                  <td>{length.value ? `${length.value} ${length.unit}` : ""}</td>
                  <td>{width.value ? `${width.value} ${width.unit}` : ""}</td>
                  <td>{depth.value ? `${depth.value} ${depth.unit}` : ""}</td>
                  <td>{node.nStage || ""}</td>
                  <td>{node.description || ""}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  </div>
)}

            {/* Staging Section - Now correctly shown for each diagnosis */}
            {/* <div className="row mb-3">
  <div className="col-12">
    <strong>Staging</strong>
    <div className="table-responsive">
      {diagnosis.stagingDetails && diagnosis.stagingDetails.length > 0 ? (
        <table className="table table-bordered table-sm">
          <thead>
            <tr>
              <th>TYPE</th>
              <th>T</th>
              <th>N</th>
              <th>M</th>
              <th>STAGE</th>
              <th>DATE</th>
            </tr>
          </thead>
          <tbody>
            {diagnosis.stagingDetails.map((staging, idx) => (
              <tr key={`staging-view-${idx}`}>
                <td>{staging.type || staging.stagingType || "N/A"}</td>
                <td>{staging.t || staging.tStage || "N/A"}</td>
                <td>{staging.n || staging.nStage || "N/A"}</td>
                <td>{staging.m || staging.mStage || "N/A"}</td>
                <td>{staging.stage || "N/A"}</td>
                <td>{staging.date || (staging.stagingDate ? moment(staging.stagingDate).format("DD-MM-YYYY") : "N/A")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="alert alert-info mt-2">No staging information available for this diagnosis.</div>
      )}
    </div>
  </div>
</div> */}
 {/* Staging Section - Only shown when there's staging data */}
 {diagnosis.stagingDetails && diagnosis.stagingDetails.length > 0 && (
               <div className="row mb-3">
                 <div className="col-12">
                   <strong>Staging</strong>
                   <div className="table-responsive">
                     <table className="table table-bordered table-sm">
                       <thead>
                         <tr>
                           <th>TYPE</th>
                           <th>T</th>
                           <th>N</th>
                           <th>M</th>
                           <th>STAGE</th>
                           <th>DATE</th>
                         </tr>
                       </thead>
                       <tbody>
                         {diagnosis.stagingDetails.map((staging, idx) => (
                           <tr key={`staging-view-${idx}`}>
                             <td>{staging.type || staging.stagingType || "N/A"}</td>
                             <td>{staging.t || staging.tStage || "N/A"}</td>
                             <td>{staging.n || staging.nStage || "N/A"}</td>
                             <td>{staging.m || staging.mStage || "N/A"}</td>
                             <td>{staging.stage || "N/A"}</td>
                             <td>{staging.date || (staging.stagingDate ? moment(staging.stagingDate).format("DD-MM-YYYY") : "N/A")}</td>
                           </tr>
                         ))}
                       </tbody>
                     </table>
                   </div>
                 </div>
               </div>
             )}
             
            {diagnosis.icd10Diagnosis && (
              <div className="row mb-3">
                <div className="col-12">
                  <strong>ICD-10 Diagnosis</strong>
                  <div>{diagnosis.icd10Diagnosis}</div>
                </div>
              </div>
            )}

            {diagnosis.icdDiagnoses && diagnosis.icdDiagnoses.length > 0 && (
              <div className="row mb-3">
                <div className="col-12">
                  <strong>ICD Diagnoses</strong>
                  <div className="table-responsive">
                    <table className="table table-bordered table-sm">
                      <thead>
                        <tr>
                          <th>Code</th>
                        </tr>
                      </thead>
                      <tbody>
                        {diagnosis.icdDiagnoses.map((icd, idx) => (
                          <tr key={`display-icd-${idx}`}>
                            <td>{icd.icdCode || ""}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {diagnosis.investigationReports && diagnosis.investigationReports.length > 0 && (
              <div className="row mb-3">
                <div className="col-12">
                  <strong>Reports</strong>
                  <div className="table-responsive">
                    <table className="table table-bordered table-sm">
                      <thead>
                        <tr>
                          <th>File Name</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {diagnosis.investigationReports.map((report, idx) => (
                          <tr key={`display-report-${idx}`}>
                            <td>{report.fileName || ""}</td>
                            <td>
                              <Button 
                                variant="primary" 
                                size="sm"
                                onClick={() =>
                                  viewPdfFile(report.fileContent, report.fileName)
                                }
                              >
                                View
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {diagnosis.remarks && (
              <div className="row mb-3">
                <div className="col-12">
                  <strong>Remarks</strong>
                  <div style={{ whiteSpace: "pre-line" }}>
                    {diagnosis.remarks}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      ))
    ) : (
      !loading && !error && !isFormVisible && (
        <div className="alert alert-info">
          No diagnosis records found. Click the "Add New Diagnosis" button to create one.
        </div>
      )
    )}
  </div>
)}
    </div>

    </>
  );
});

export default DiagnosisManagement;